// Russian strings, written for a Russian-speaking machinist rather than
// translated word-for-word from Hebrew: shop-floor terminology (наряд, чертёж,
// брак, смена, артикул), direct imperative, no anglicisms where a normal
// Russian technical word exists.
// ── ⚠ קיצורים ברוסית אינם נוטים ────────────────────────────────────────────
//
// נמדד 23/09/2026 בסריקה אחרי מחרוזות שצריכות צורות ריבוי: **14 מועמדות,
// ושלוש בלבד שבורות.** ההבדל הוא בין שם עצם מלא לבין קיצור:
//
//   ✗  „Осталось 1 деталей"      — שם עצם מלא, נוטה. צריך „1 деталь".
//   ✓  „1 мм" · „2 мм" · „5 мм"  — קיצור, אינו נוטה. תקין כמו שהוא.
//
// וכך גם `шт` · `дн` · `ч` · `мин` — כולם קיצורים, וכולם תקינים בכל מספר.
//
// זה כתוב כאן כדי שלא אחזור לבדוק את אותן אחת-עשרה שורות: הן **אינן**
// שבורות, והמרתן לשלוש צורות הייתה מוסיפה שלושים שורות מילון בלי לתקן
// דבר.

export const ru: Record<string, string> = {
  // ── shared ──
  'common.back': 'Назад',
  // ⚠ Добавлено 28/09/2026 — см. i18n-child-components.test.ts.
  'common.gotIt': 'Понятно',
  'sigpad.title': 'Подпись представителя заказчика',
  'sigpad.signerName': 'Имя подписавшего *',
  'sigpad.signerPlaceholder': 'Кто со стороны заказчика принял работу',
  'sigpad.signature': 'Подпись',
  'sigpad.clear': 'Очистить',
  'sigpad.signHere': 'Подпишите здесь пальцем',
  'sigpad.submitDefault': 'Закрыть выезд',
  'sigpad.hintDefault': 'После подписи отчёт заблокирован. Изменить можно только через руководителя сервиса.',
  'dhold.failed': 'Подтверждение не удалось',
  'dhold.title': 'Нет чертежа',
  'dhold.heading': 'Нет чертежа — нужно подтверждение мастера',
  'dhold.step1': '1 · Подтверждение мастера',
  'dhold.managerCode': 'Код мастера',
  'dhold.codePlaceholder': 'Код',
  'dhold.orScan': 'или скан карты мастера',
  'dhold.scanHere': 'Сканируйте здесь',
  'dhold.step2': '2 · Что делать с деталью',
  'dhold.waive': 'Не требует прослеживаемости',
  'dhold.waiveHint': 'Можно работать сейчас, даже без чертежа.',
  'dhold.wait': 'Ждать чертёж',
  'dhold.waitHint': 'Деталь останавливается. Как только чертёж загрузят — киоск сообщит, что можно отчитываться.',
  'dhold.needId': 'Введите код или отсканируйте карту, чтобы выбрать.',
  'dhold.back': 'Назад к списку',
  'dhold.arrived': 'Чертёж загружен',
  'dhold.arrivedCanReport': '— можно отчитываться',
  'dhold.waitingKiosk': 'Мастер решил не начинать, пока не загрузят чертёж. Как только он появится — экран сообщит здесь.',
  'dhold.waitingStation': 'Мастер решил не начинать, пока не загрузят чертёж. Как только он появится — экран сообщит.',
  'common.cancel': 'Отмена',
  'common.confirm': 'Подтвердить',
  'common.save': 'Сохранить',
  'common.close': 'Закрыть',
  'common.loading': 'Загрузка…',
  'common.error': 'Ошибка',
  'common.search': 'Поиск',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.quantity': 'Количество',
  'common.language': 'Язык',
  'common.units': 'шт.',
  'common.minutes': 'мин.',
  'common.hours': 'ч.',
  'common.project': 'Проект',
  'common.part': 'Деталь',
  'common.department': 'Участок',
  'common.worker': 'Рабочий',
  'common.none': 'Нет',

  'print.print': 'Печать',
  'print.dialog': '{title} — предпросмотр печати',
  'print.hint': 'Так будет выглядеть напечатанная страница. «Печать» открывает окно печати — там же можно сохранить PDF.',
  'print.printedAt': 'Напечатано',
  'print.printedBy': 'Кем',
  'print.customer': 'Заказчик',
  'print.page': 'Стр.',
  'print.of': 'из',
  'print.signName': 'Имя',
  'print.signature': 'Подпись',
  'print.date': 'Дата',

  // ── «Всё, что требуется проекту» — закупки проекта ──
  'reqReport.title': 'Всё, что требуется проекту',
  'reqReport.rows': 'Строк',
  'reqReport.here': 'На месте',
  'reqReport.state.ISSUED': 'Выдано в производство',
  'reqReport.state.RECEIVED': 'Прибыло — ожидает',
  'reqReport.state.ORDERED': 'Заказано',
  'reqReport.state.REQUESTED': 'Ещё не заказано',
  'reqReport.basis.RECEIVED': 'получено',
  'reqReport.basis.PROMISED': 'обещано',
  'reqReport.basis.LEAD_TIME': 'по сроку поставки',
  'reqReport.viewProduction': 'Производство',
  'reqReport.viewWarehouse': 'Склад',
  'reqReport.print': 'Печать',
  'reqReport.statusTitle': 'Статус заявок на закупку',
  'reqReport.printView': 'Вид',
  'reqReport.sigPicked': 'Собрал (склад)',
  'reqReport.sigReceived': 'Принял (производство)',
  'reqReport.printWarehouse': 'Комплектация склада',
  'reqReport.empty': 'Для этого проекта ещё нет заявок на закупку.',
  'reqReport.loadError': 'Не удалось загрузить заявки на закупку — это ошибка загрузки, а не «заявок нет». Обновите страницу.',
  'reqReport.unknown': 'Неизвестно',
  'reqReport.col.sku': 'Артикул',
  'reqReport.col.name': 'Описание',
  'reqReport.col.qty': 'Кол-во',
  'reqReport.col.status': 'Статус',
  'reqReport.col.eta': 'Когда прибудет',
  'reqReport.col.requester': 'Кто заказал',
  'reqReport.col.orderedAt': 'Заказано',
  'reqReport.col.promised': 'Обещано',
  'reqReport.col.neededBy': 'Нужно производству',
  'reqReport.col.late': 'Опоздание',
  'reqReport.late': 'с опозданием',
  'reqReport.supplierNotYet': 'Поставщик не выбран',
  'reqReport.supplierNotRecorded': 'Поставщик не записан',
  'unplanned.PENDING': 'Без заказа',
  'unplanned.PENDING.hint': 'Принято без заказа — закупка ещё не внесла цену и заказ',
  'unplanned.NEEDS_SUPPLIER': 'Открыть поставщика',
  'unplanned.NEEDS_SUPPLIER.hint': 'Принято без заказа — поставщик записан текстом и ещё не открыт в системе',
  'reqReport.noNeededBy': 'Не задано',
  'reqReport.col.picked': 'Собрано',
  'reqReport.col.action': 'Действие',

  // ── Отчёт по деталям проекта — печать и экспорт (U-0924-12) ──
  'partsReport.open': 'Печать / экспорт',
  'partsReport.title': 'Детали проекта и их статус',
  'partsReport.dialogLabel': 'Отчёт по деталям проекта {project}',
  'partsReport.print': 'Печать',
  'partsReport.excel': 'Экспорт в Excel',
  'partsReport.printedAt': 'Напечатано:',
  'partsReport.printedBy': 'Кем:',
  'partsReport.summary': 'Итог',
  'partsReport.sum.parts': 'Артикулов',
  'partsReport.sum.finished': 'Готово',
  'partsReport.sum.noDrawing': 'Без чертежа',
  'partsReport.sum.withNc': 'С открытым несоответствием',
  'partsReport.sum.blocked': 'Остановлено / заблокировано',
  'partsReport.partsSection': 'Детали — из дерева изделия',
  'partsReport.purchaseSection': 'Закупки — всё, что требуется проекту',
  'partsReport.noParts': 'В дереве изделия проекта ещё нет деталей.',
  'partsReport.treeError': 'Не удалось загрузить дерево изделия — это ошибка загрузки, а не «деталей нет».',
  'partsReport.extrasError': 'Несоответствия и блокировки не загрузились — эти столбцы в отчёте пусты, а не «нет».',
  'partsReport.retry': 'Повторить',
  'partsReport.col.sku': 'Артикул',
  'partsReport.col.name': 'Название',
  'partsReport.col.qty': 'Кол-во',
  'partsReport.col.status': 'Состояние',
  'partsReport.col.stage': 'Текущий этап',
  'partsReport.col.progress': 'Штук по этапам',
  'partsReport.col.drawing': 'Чертёж',
  'partsReport.col.nc': 'Открытые несоответствия',
  'partsReport.col.blocked': 'Остановлено / заблокировано',
  'partsReport.col.unit': 'Ед.',
  'partsReport.col.received': 'Получено',
  'partsReport.col.supplier': 'Поставщик',
  'partsReport.noDrawing': 'Нет чертежа',
  'partsReport.hasDrawing': 'Чертёж есть',
  'partsReport.revision': 'Ревизия {rev}',
  'partsReport.version': 'Версия {v}',
  'partsReport.allStagesDone': 'Все этапы выполнены',
  'partsReport.status.done': 'Готово',
  'partsReport.status.in_work': 'В работе',
  'partsReport.status.not_started': 'Не начато',
  'partsReport.status.stuck': 'Застряло',
  'partsReport.blocked.HOLD': 'Остановка производства',
  'partsReport.blocked.ECO': 'Инженерное изменение',
  'partsReport.blocked.NC': 'Несоответствие',

  // ── Все артикулы с чертежом — карточка проекта на инженерной доске ──
  'partsGallery.tab': 'Артикулы и чертежи',
  'partsGallery.tabHint': 'Все артикулы проекта с изображением чертежа — загрузка, отмена и решение на одном месте',
  // ── Инженерная карточка: пять вкладок + подвиды (E2, 30/10/2026) ──
  'engCard.tabs': 'Вкладки карточки проекта',
  'engCard.views': 'Виды во вкладке',
  'engCard.tab.overview': 'Обзор',
  'engCard.tab.drawings': 'Чертежи',
  'engCard.tab.purchase': 'Закупки',
  'engCard.tab.quality': 'Качество',
  'engCard.tab.thread': 'Переписка',
  'engCard.view.state': 'Состояние и правка',
  'engCard.view.brief': 'Досье',
  'engCard.view.times': 'Время и история',
  'engCard.view.family': 'Подпроекты',
  'engCard.view.parts': 'Артикулы и чертежи',
  'engCard.view.eco': 'ECO / изменения',
  'engCard.view.media': 'Фото, 3D, раскрой',
  'engCard.view.purchase': 'Закупки',
  'engCard.view.qa': 'FAT, требования и несоответствия',
  'engCard.view.reviews': 'Ревью и утверждения',
  'engCard.view.thread': 'Переписка',
  'engCard.view.meetings': 'Встречи',
  'engCard.count.missing': 'нет: {n}',
  'engCard.count.eco': '{n} ECO',
  'engCard.count.fat': 'замечаний: {n}',
  'engCard.count.mySign': 'ваша подпись',
  'engCard.count.ask': 'ждёт вас',
  'engCard.reportHours': 'Отчёт часов',
  'engCard.more': 'Ещё',
  'engCard.moreHint': 'Ещё действия: связать такой же проект, объединить в один, предложить в каталог, 3D, развернуть',
  'engCard.section.hours': 'Отчёт часов',
  'engCard.section.timeline': 'Хронология',
  'engCard.section.history': 'Что происходило в проекте',
  'partsGallery.title': 'Артикулы и чертежи',
  'partsGallery.search': 'Поиск артикула или названия…',
  'partsGallery.emptyCanUpload': 'В дереве изделия ещё нет деталей. Перетащите ниже первый чертёж — именно он его построит.',
  'partsGallery.empty': 'В дереве изделия ещё нет деталей — они создаются из первого загруженного чертежа.',
  'partsGallery.noPreview': 'Нет предпросмотра',
  'partsGallery.openDrawing': 'Открыть чертёж',
  'partsGallery.openDrawingOf': 'Открыть чертёж {sku}',
  'partsGallery.thumbAlt': 'Чертёж {sku}',

  // ── Миниатюра чертежа у каждого артикула, в любом дереве ──
  'drawingThumb.none': 'Нет чертежа',
  'drawingThumb.noneTip': 'У артикула {sku} нет активного чертежа. Его загружает конструкторский отдел в карточке проекта.',
  'drawingThumb.noPreview': 'Нет превью',
  'drawingThumb.open': 'Увеличить чертёж {sku}',
  'drawingThumb.dialogTitle': 'Чертёж {sku}',
  'drawingThumb.newTab': 'Открыть в новой вкладке',
  'drawingThumb.openFull': 'Открыть полный лист',
  'drawingThumb.failed': 'Изображение не загрузилось',
  'drawingThumb.failedTip': 'Изображение чертежа {sku} не загрузилось — это сбой загрузки, а не «нет чертежа». Нажмите, чтобы повторить.',
  'drawingThumb.retry': 'Повторить',
  'drawingThumb.rev': 'Ревизия {rev}',
  'drawingThumb.ver': 'Версия {v}',
  'drawingThumb.previewOnly': 'Показано превью — сам файл PDF не найден. Обратитесь в конструкторский отдел, чтобы загрузить его заново.',
  'drawingThumb.noPdf': 'У артикула есть чертёж, но его файл не найден на диске. Это сбой файла, а не «нет чертежа» — обратитесь в конструкторский отдел, чтобы загрузить лист заново.',

  // ── badge / identification ──
  'badge.title': 'Приложите пропуск или введите табельный номер',
  'badge.placeholder': 'Табельный номер',
  'badge.enter': 'Вход',
  'badge.notFound': 'Пропуск не распознан — обратитесь к мастеру смены',
  'badge.hello': 'Здравствуйте, {name}',
  'badge.logout': 'Выход',

  // ── worker station ──

  // ── fault reporting ──
  'fault.title': 'Сообщение о неисправности',
  'fault.type': 'Тип неисправности',
  'fault.describe': 'Опишите проблему одним предложением',
  'fault.markOnDrawing': 'Отметить на чертеже',
  'fault.send': 'Отправить',
  'fault.sent': 'Сообщение отправлено. Мастер уже видит его.',
  'fault.timerStopped': 'Отсчёт времени по детали остановлен — вы не теряете время из-за неисправности',


  // ── warehouse kiosk ──
  'warehouse.subtitle': 'Склад — выдача / возврат',
  'warehouse.stationSignedOut': 'Рабочее место не подключено к системе. Войдите на этом рабочем месте, чтобы выдавать или возвращать позиции.',
  'warehouse.issue': 'Выдача',
  'warehouse.returnToStock': 'Возврат на склад',
  'warehouse.worker': 'Рабочий (приложите пропуск)',
  'warehouse.workerPlaceholder': 'Табельный номер',
  'badge.scanPlaceholder': 'Отсканируйте пропуск',
  'badge.change': 'Изменить',
  'badge.typingRefused': 'Пропуск нужно отсканировать — ручной ввод табельного номера здесь недоступен. Если пропуск не сканируется, выберите из списка.',
  'warehouse.project': 'Проект (отсканируйте или введите номер)',
  'warehouse.projectPlaceholder': 'Номер проекта',
  'warehouse.projectNotFound': 'Проект с таким номером не найден',
  'warehouse.item': 'Позиция (отсканируйте артикул или найдите по названию)',
  'warehouse.itemPlaceholder': 'Артикул или название — например SCRE-000100 / двигатель',
  'warehouse.inStockN': '(на складе {n})',
  'warehouse.stockNow': 'на складе {n}',
  'saw.fullBar': 'Целый пруток',
  'saw.pieces': 'деталей',
  'saw.notOnRack': 'Нет на стеллаже',
  'saw.tryAgain': 'Повторить',
  'saw.barClosedWhileShown': 'Этот пруток закрыт — за 30 минут с него ничего не отрезали, поэтому он не засчитан как работа. Нажмите «Следующий пруток», чтобы начать.',
  'saw.outOf': 'из',
  'saw.item': 'Позиция',
  'saw.drawing': 'Чертёж',
  'saw.cutDone': 'Отрезал',
  'saw.release': 'Не режу сейчас — вернуть пруток',
  'saw.releaseConfirm': 'Вернуть пруток? Время не засчитывается, детали возвращаются в очередь.',
  'saw.releaseYes': 'Да, вернуть пруток',
  'saw.releaseNo': 'Отмена',
  'saw.released': 'Пруток вернулся в очередь ✓',
  'saw.releasedBody': 'Часы остановлены — 0 минут. Детали вернулись в очередь резки.',
  'saw.addRemnant': 'Добавить остаток вручную',
  'saw.remnantProfile': 'Профиль',
  'saw.remnantSearch': 'Поиск профиля…',
  'saw.remnantPick': 'Выберите профиль',
  'saw.remnantLength': 'Измеренная длина (мм)',
  'saw.remnantSave': 'Сохранить и пересчитать',
  'saw.remnantAdded': 'Добавлен остаток {sku} · {mm} мм — план обновлён',
  'saw.remnantScrapped': '{mm} мм короче минимального остатка — записано в лом',
  'saw.finishedBar': 'Закончил пруток',
  'saw.closeBar': 'Закрыть пруток',
  'saw.uncutAsk': 'Осталось {n} деталей, которые вы не отметили. Что с ними?',
  'saw.uncutAsk_one': "Осталась {n} деталь, которую вы не отметили. Что с ней?",
  'saw.uncutAsk_few': "Осталось {n} детали, которые вы не отметили. Что с ними?",
  'saw.uncutAsk_many': "Осталось {n} деталей, которые вы не отметили. Что с ними?",
  'saw.uncutAllCut': 'Я отрезал их все',
  'saw.uncutAllCutHint': 'Будут закрыты как отрезанные и не попадут в план снова',
  'saw.uncutNone': 'Я их не резал',
  'saw.uncutNoneHint': 'Вернутся в план и будут отрезаны из другого прутка',
  'saw.answerFirst': 'Выберите ответ, чтобы закрыть пруток',
  'saw.history': 'История резки',
  'saw.historyEmpty': 'Пока ничего не отрезано.',
  'saw.searchCuts': 'Поиск: проект, профиль, артикул или длина',
  'saw.missingConfirm': 'Отметить, что остаток {sku} не найден на стеллаже? План пересчитается без него, и вы получите целый пруток.',
  'saw.skipConfirm': 'Пропустить эту деталь? Она вернётся в план и будет назначена на другой пруток.',
  'saw.callManager': 'Обратитесь к начальнику производства.',
  'saw.subtitle': 'Пила — резка профилей',
  'saw.emptyBody': 'Очередь пуста. Когда инженер загрузит новый список резки, он появится здесь автоматически, уже оптимизированный по всем проектам.',
  'saw.replanLead': 'План пересчитан. ',
  'saw.replanTail': ' — изменение касается того, что после него.',
  'saw.replanSame': 'Пруток, который вы режете сейчас, не изменился',
  'saw.replanTitle': 'Поступил новый список резки',
  'saw.scrapNote': 'Остаток слишком короткий — записан как лом.',
  'saw.fullyUsed': 'Пруток использован полностью. Остатка нет.',
  'saw.stockOffNote': 'Учёт остатков пилы выключен — остаток не записывается на стеллаж.',
  'saw.closeHint': 'Осталось закрыть пруток и записать остаток.',
  'saw.allCut': 'Все детали этого прутка отрезаны',
  'saw.returnToRack': 'и верните его на стеллаж — план уже учитывает его.',
  'saw.markRemnant': 'Промаркируйте остаток:',
  'saw.scanBadge': 'Отсканируйте бейдж',
  'saw.nothingToCut': 'Нечего резать',
  // בחירת פרויקט במסור (אלי 01/10/2026, U-1001-02)
  'saw.project': 'Проект',
  'saw.allProjects': 'Все проекты',
  'saw.pickProject': 'Выбрать проект',
  'saw.clearProject': 'Все проекты',
  'saw.projectSearch': 'Номер или название проекта',
  'saw.projectNone': 'Нет проекта, переданного в производство, где осталось что резать. Попробуйте другой номер или вернитесь ко всем проектам.',
  'saw.projectPieces': 'резов: {n} · профилей: {p}',
  'saw.projectCut': 'Проект {number} нарезан',
  'saw.projectCutBody': 'Резать больше нечего. Вернитесь ко всем проектам, чтобы продолжить очередь.',
  'saw.otherProject': 'другой проект — режется вместе с прутком',
  'saw.replanProject': 'Пересчитать только для этого проекта',
  'saw.replanProjectHint': 'Общих прутков с другим проектом: {n}. По умолчанию — резать весь пруток.',
  'saw.replanProjectConfirm': 'Пересчитать, чтобы у проекта {number} были свои прутки? Детали остальных проектов перейдут на другие прутки.',
  'saw.replanRunning': 'Прутки, уже на пиле, не изменены ({n}) — режьте их как обычно.',
  'saw.projectsError': 'Список проектов не загрузился. Проверьте связь и попробуйте снова.',
  'saw.switchWorker': 'Сменить работника',
  'saw.whichSaw': 'На какой пиле вы работаете?',
  'saw.whichSawHint': 'Спрашиваем один раз в день. Сменить можно в любой момент — «Сменить пилу».',
  'saw.changeSaw': 'Сменить пилу',
  'saw.sawsError': 'Список пил не загрузился. Проверьте связь и попробуйте снова.',
  'saw.workNow': 'Текущая работа',
  'saw.notFound': 'Работник не найден',
  'saw.takeFromRack': 'Возьмите со стеллажа',
  'saw.updateNow': 'Обновить',
  'saw.wholeQueue': 'Вся очередь',
  'saw.barClosed': 'Пруток закрыт',
  'saw.doNotCut': 'Не режьте.',
  'saw.wholeBar': 'Весь пруток',
  'saw.notUploaded': '· не загружено',
  'saw.piece': 'Деталь',
  'saw.scrapShort': '· лом',
  'saw.continue': 'Далее',
  'saw.error': 'Ошибка',
  'saw.skip': 'Пропустить',
  'saw.miscut': 'Ошибся в размере',
  'saw.miscutTitle': 'Рез вышел другого размера',
  'saw.miscutRequired': 'Требуется',
  'saw.miscutActual': 'Сколько отрезано фактически? (мм)',
  'saw.miscutHint': 'Деталь вернётся в план и будет распланирована заново, а отрезанный кусок запишется на стеллаж с измеренной длиной.',
  'saw.miscutSave': 'Записать и перепланировать',
  'saw.miscutSaved': 'Записано — деталь вернулась в план',
  'saw.miscutRemnant': 'Записано на стеллаж',
  'saw.miscutScrapped': 'Короче минимума — записано как брак',
  'saw.metres': 'м',
  'saw.mm': 'мм',
  'saw.running': 'Время на пиле',
  'saw.bar': 'Пруток',
  'warehouse.discipline': 'К какой статье затрат',
  'warehouse.disciplineAuto': 'По каталогу',
  'warehouse.notInCatalog': 'Нет в каталоге — {verb} как свободный текст (без изменения остатка)',
  'warehouse.willIssue': 'будет выдано',
  'warehouse.willReturn': 'будет возвращено',
  'warehouse.overStock': 'Количество ({qty}) больше остатка на складе ({stock}). Выдать всё равно можно.',
  'warehouse.issueButton': 'Выдать на проект',
  'warehouse.returnButton': 'Вернуть на склад',
  'warehouse.currentSession': 'В текущей сессии',
  'warehouse.wasIssued': 'Выдано',
  'warehouse.wasReturned': 'Возвращено',
  'warehouse.stock': 'Склад',

  // ── scan kiosk ──
  'scan.title': 'Пост сканирования',
  'scan.scanPart': 'Отсканируйте штрих-код детали',
  'scan.startStage': 'Начать операцию',
  'scan.endStage': 'Завершить операцию',
  'scan.coWorkers': 'Другие рабочие',
  'scan.addCoWorker': 'Добавить рабочего',
  'scan.success': 'Успешно зарегистрировано',

  // ── NC kiosk (несоответствия) ──
  'nc.subtitle': 'Сообщение о несоответствии',
  'nc.switchWorker': 'Сменить рабочего',
  'nc.badgeTitle': 'Отсканируйте или введите номер пропуска',
  'nc.badgePlaceholder': 'Номер пропуска',
  'nc.continue': 'Продолжить',
  'nc.badgeNotFound': 'Пропуск не распознан',
  'nc.photoFailed': 'Не удалось загрузить изображение',
  'nc.photoNotSaved': 'Отчёт сохранён, но фото не сохранилось. Отправьте ещё раз с фото.',
  'nc.sendFailed': 'Ошибка при отправке',
  'nc.sent': 'Несоответствие отправлено',
  'nc.sentNote': 'Конструкторский отдел и мастера получили уведомление.',
  'nc.reportAnother': 'Сообщить ещё об одном несоответствии',
  'nc.searchPlaceholder': 'Поиск по артикулу или номеру проекта',
  'nc.scan': 'Сканировать',
  'nc.noParts': 'Детали не найдены',
  'nc.type': 'Тип несоответствия',
  'nc.photo': 'Фото',
  'nc.takePhoto': 'Сделать фото (необязательно)',
  'nc.notePlaceholder': 'Описание (необязательно)',
  'nc.send': 'Отправить несоответствие',

  // ── badge steps (scan kiosk) ──
  'badge.scanBadge': 'Отсканируйте пропуск',
  'badge.scanOrType': 'Отсканируйте или введите и нажмите Enter',
  'badge.badgeNumber': 'Номер пропуска',
  'badge.ok': 'Подтвердить',
  'badge.scanWithCamera': 'Сканировать пропуск камерой',
  'badge.tapCard': 'Приложите NFC-карту',
  'badge.greet.MORNING': 'Доброе утро, {name}',
  'badge.greet.NOON': 'Добрый день, {name}',
  'badge.greet.EVENING': 'Добрый вечер, {name}',
  'badge.helloName': 'Здравствуйте, {name}',
  'badge.departmentOf': 'Участок: {name}',
  'badge.scanDrawingHint': 'Отсканируйте чертёж для начала или завершения, либо ещё один пропуск, чтобы добавить напарника',
  'badge.barcode': 'Штрих-код',
  'badge.notMe': 'Это не я, назад',
  'badge.scanDrawingCamera': 'Сканировать чертёж камерой',
  'units.howMany': '{stage} — сколько единиц? (1–{max})',
  'units.sending': 'Отправка…',
  'keypad.clear': 'Очистить',

  // ── worker station ──
  'st.subtitle': 'Рабочий терминал',
  'st.activeDept': 'Текущий участок:',
  'st.logout': 'Выйти',
  'st.switchWorker': 'Сменить работника',
  'st.badgeNotFound': 'Пропуск не распознан',
  'st.scanBadge': 'Отсканируйте пропуск',
  'st.badgePlaceholder': 'Номер пропуска',
  'st.enter': 'Вход',
  'st.myParts': 'Мои детали ({n})',
  'st.crewToday': 'Сегодня: {h} ч на проект',
  'st.crewTodayHint': 'Часть твоего дня — на этот проект; его детали первыми в списке ниже.',
  'st.modelLoading': 'Загрузка 3D-модели…',
  'st.modelLoadFailed': 'Не удалось загрузить модель',
  'st.modelCenter': 'По центру',
  'st.modelSection': 'Сечение',
  'st.modelParts': 'Детали',
  'st.modelPartsCount': 'Детали ({n})',
  'st.modelShowAll': 'Показать все',
  'st.modelIsolate': 'Изолировать',
  'st.modelTools': 'Инструменты',
  'st.modelViews': 'Вид',
  'st.viewIso': 'Изометрия',
  'st.viewFront': 'Спереди',
  'st.viewTop': 'Сверху',
  'st.viewSide': 'Сбоку',
  'st.modelMeasure': 'Измерить между 2 точками',
  'st.modelMeasureHint': 'Нажмите на две точки модели — привязка к углам',
  'st.modelMeasureSecond': 'Нажмите на вторую точку',
  'st.modelMeasureAim': 'Поверните модель, чтобы крестик был на углу — точка привяжется',
  'st.modelMarkPoint': 'Отметить точку {n} из 2',
  'st.modelDistance': 'Прямое расстояние',
  'st.modelAxisLen': 'Длина',
  'st.modelAxisH': 'Высота',
  'st.modelAxisW': 'Ширина',
  'st.modelFlip': 'Перевернуть',
  'st.modelClear': 'Очистить',
  'st.modelExplode': 'Разнесённый вид',
  'st.modelExplodeClosed': 'Собрано',
  'st.modelExplodeSubs': 'Узлы',
  'st.modelExplodeAll': 'Все детали',
  'st.modelDisplay': 'Отображение',
  'st.modelColorStatus': 'Цвет по статусу',
  'st.modelXray': 'Остальные прозрачные',
  'st.modelGhostDone': 'Готовое прозрачным',
  'st.mDone': 'Готово',
  'st.mWork': 'В работе',
  'st.mIssue': 'Нехватка / несоответствие',
  'st.mTodo': 'Не начато',
  'st.mBuy': 'Покупное',
  'st.mNone': 'Нет карточки в системе',
  'st.modelSkus': '{n} артикулов · {u} шт. в модели',
  'st.modelMainAsm': 'Главная сборка',
  'st.modelOutliers': '{n} деталей далеко от машины не учтены в кадре: {list}',
  'st.modelDims': 'Размеры (по модели)',
  'st.modelQty': 'Кол-во в модели',
  'st.modelSubAsm': 'Узел',
  'st.modelStatusFrom': 'Статус · по отметкам станций',
  'st.modelReportNc': 'Сообщить о несоответствии с замером',
  'st.modelMeasureNote': 'Замер в 3D: {d} мм (длина {x} · высота {y} · ширина {z})',
  'st.modelMm': 'мм',
  'st.modelUnnamed': 'Деталь без имени',
  'st.modelOpen': '3D',
  'st.modelLookReal': 'Реалистично',
  'st.modelLookStatus': 'Статус',
  'st.modelHideShow': 'Скрыть / показать',
  'st.modelSearch': 'Поиск артикула',
  'st.modelNoMatch': 'Такого артикула нет в модели',
  'st.modelScopePart': 'Только деталь',
  'st.modelScopeAsm': 'Её узел',
  'st.modelScopeAll': 'Вся модель',
  'st.modelXrayRest': 'Остальное прозрачным',
  'st.modelDetails': 'Подробнее',
  'st.modelNotInModel': 'Артикул {p} не найден в модели — показана вся модель. Найдите его в списке деталей.',
  'st.drawingAlt': 'Чертёж',
  'st.dimChecked': 'Проверено',
  'st.dimTapToVerify': 'Нажмите, чтобы подтвердить проверку размера',
  'st.zoomIn': 'Увеличить',
  'st.zoomInDrawing': 'Увеличить чертёж',
  'st.zoomOut': 'Уменьшить',
  'st.zoomOutDrawing': 'Уменьшить чертёж',
  'st.fitToScreen': 'По размеру экрана',
  'st.criticalDims': 'Критические размеры: проверено {done} из {total}',
  'st.tapCircleToVerify': 'Нажмите на кружок для подтверждения',
  'st.markupClear': 'Очистить',
  'st.markupSave': 'Сохранить пометку',
  'st.markupTitle': 'Отметьте на чертеже',
  'st.treeProject': 'Проект {n}',
  'st.treeSearch': 'Поиск по артикулу или названию',
  'st.treeSearchLabel': 'Поиск по дереву изделия',
  'st.treeNoMatch': 'Подходящих деталей не найдено',
  'st.treeEmpty': 'Других деталей в дереве нет',
  'st.treeDrawingOf': 'Чертёж {part}',
  'st.treeViewOnly': 'Только просмотр',
  'st.treeBack': 'Назад к дереву',
  'st.treeYourPart': 'Ваша деталь',
  'st.inWorkNow': 'Сейчас в работе',
  'st.notStarted': 'Ещё не начато',
  'st.thisIsNc': 'Это несоответствие',
  'st.pinned': 'Срочно — закреплено мастером',
  'st.completed': 'Готово',
  'st.readyStage': 'Готово к работе · {stage}',
  'st.notReadyYet': 'Ещё не готово · {dept}',
  'st.myDept': 'Мой участок:',
  'st.searchPlaceholder': 'Поиск по номеру проекта (например 250039) или артикулу — по всем чертежам и участкам',
  'st.scanDrawingPlaceholder': 'Отсканируйте штрих-код чертежа — работа начнётся сразу',
  'st.scanNotADrawing': 'Это не штрих-код чертежа. Отсканируйте код в углу чертежа.',
  'st.scanPartNotFound': 'Артикул {part} не найден. Возможно, чертёж не загружен в систему.',
  'st.searchResults': 'Результаты поиска',
  'st.waitingInDept': 'Ожидает на участке {dept}',
  'st.noPartsFound': 'Детали не найдены',
  'st.noPartsWaiting': 'На участке нет деталей в очереди — найдите номер проекта выше, чтобы открыть чертёж и взять деталь',
  'st.withYou': 'У вас · {stage}',
  'st.inDept': 'Участок {dept}',
  'st.canPull': ' · можно взять',
  'st.tasks': 'Задачи к выполнению ({n})',
  'st.taskDue': 'Срок {date}',
  'st.overdue': ' · просрочено',
  'st.taskDone': 'Выполнено',
  'st.confirmDone': 'Подтвердить выполнение?',
  'st.tryAgain': 'Ошибка — попробуйте ещё раз',
  'st.engUpdated': 'Конструкторский отдел обновил несоответствие',
  'st.ncOpen': 'Открытое несоответствие: {cat} · найдено на участке {stage}',
  'st.ncOpenBlocking': 'Открытое несоответствие · блокирует: {cat} · найдено на участке {stage}',
  'st.reworkBack': 'Вернулась на исправление — {stage}',
  'st.viewEngMarkup': 'Посмотреть пометку на чертеже',
  'st.drawingReplaced': 'Чертёж заменён',
  'st.drawingReplacedBody': 'Конструкторы загрузили новый чертёж. Не продолжайте по старому.',
  'st.drawingReplacedRev': 'Конструкторы загрузили ревизию {rev}. Не продолжайте по старому.',
  'st.showNewDrawing': 'Показать новый чертёж',
  'st.workHalted': 'Работа остановлена — действуйте по указанию.',
  'st.ownBadge': 'Это ваш собственный пропуск',
  'st.coAlready': 'Этот напарник уже добавлен',
  'st.coMax': 'Можно добавить не более {n} напарников',
  'st.coMax_one': "Можно добавить не более {n} напарника",
  'st.coMax_few': "Можно добавить не более {n} напарников",
  'st.coMax_many': "Можно добавить не более {n} напарников",
  'st.coNotFound': 'Пропуск не найден',
  'st.badManagerCode': 'Неверный код мастера',
  'st.drawingMissingTitle': 'Деталь ожидает чертёж',
  'st.sessionClosedZero': 'Часы закрыты — 0 единиц, деталь не продвинулась',
  'st.reported': '✓ Записано {done}/{total} · {stage}',
  'st.finishedWithMissing': ' · Внимание — ещё не хватает деталей: {list}',
  'st.stageCompleted': ' · операция завершена',
  'st.reportFailed': 'Ошибка при записи',
  'st.priorUpdated': '✓ Обновлено {stage}: {units} · работа начата',
  'st.sessionClosedStarted': '✓ Предыдущая работа закрыта · работа начата',
  'st.closeSessionFailed': 'Ошибка при закрытии работы',
  'st.productTree': 'Состав изделия',
  'st.fullScreen': 'Во весь экран',
  'st.threeD': '3D-модель',
  'st.no3d': 'Для проекта {project} не загружена 3D-модель. Инженер загружает её на экране «Загрузка чертежей» → «3D-модель» (файл .glb).',
  'st.howTo': 'Как это делается?',
  'st.howToTitle': 'Обучение — материалы этого проекта',
  'st.howToNone': 'Нет подходящих материалов для этого проекта.',
  'st.howToDocs': 'Документы',
  'st.howToNoDocs': 'Для этой модели ещё нет документов.',
  'st.howToPage': 'стр.',
  'st.howToFaqs': 'Частые ошибки',
  'st.howToOpen': 'Открыть',
  'st.print': 'Печать',
  'st.comesFrom': 'Поступает с:',
  'st.routeStart': '— начало маршрута',
  'st.goesTo': 'Далее на:',
  'st.routeEnd': 'Конец — последняя операция',
  'st.process': 'Маршрут',
  'st.currentStage': 'Текущая операция',
  'st.myStage': 'Здесь вы работаете',
  'st.partners': 'Напарники',
  'st.partnerPlaceholder': 'Отсканируйте или введите пропуск напарника',
  'st.partnersMax': 'Достигнут предел напарников ({n})',
  'st.working': 'В работе',
  'st.partnersNote': 'Часы записываются напарникам вместе с вами.',
  'st.finishAndReport': 'Завершить и указать количество',
  'st.notInYourDept': 'Эта деталь не проходит через ваш участок',
  'st.joinAnyway': 'Присоединиться к работе (код мастера)',
  'st.joinCurrentStage': 'Вашего участка нет в маршруте детали. С кодом мастера можно присоединиться к текущей операции — минуты запишутся на неё.',
  'chk.remaining': 'Перед началом работы — осталось {n}',
  'chk.allDone': 'Всё подтверждено · можно начинать',
  'chk.willNotStart': 'Работа не начнётся, пока все пункты не подтверждены.',
  'chk.markDone': 'Отметить выполненным',
  'chk.unmark': 'Снять отметку',
  'chk.requestEng': 'Запросить проверку инженера',
  'chk.valuePlaceholder': 'Измеренное значение',
  'chk.engHint': 'Этот пункт требует проверки инженера. Отметка здесь вызывает инженера и не останавливает вашу работу.',
  'chk.engSent': 'Запрос на проверку инженера отправлен. Можно продолжать работу — деталь не перейдёт на следующий пост, пока инженер не подтвердит.',
  'gate.beforeStart': 'Перед началом',
  // „מתוכנן להיום" / „לא בוצע אתמול" — נבחר למחלקה בלוח הקיבולת (אלי 29/09/2026).
  'st.dayPlanToday': 'Запланировано на сегодня',
  'st.dayPlanCarried': 'Не выполнено вчера',
  'st.pinnedFirst': 'Закреплено мастером — в первую очередь',
  'st.projectGroupOne': 'Проект {n} · одна деталь',
  'st.projectGroup': 'Проект {n} · деталей: {parts}',
  'st.tree.byProject': 'По проектам',
  'st.tree.openCount': 'Открыто: {n}',
  'st.tree.allClosed': 'Всё свёрнуто',
  'st.tree.foldAll': 'Свернуть всё',
  'st.tree.project': 'Проект',
  'st.tree.assembly': 'Узел',
  'st.tree.queueOne': 'У вас: 1 деталь',
  'st.tree.queueMany': 'У вас: {n} дет.',
  'st.tree.blocked': 'Остановлено: {n}',
  'st.tree.progress': 'Готово {done}/{total}',
  'st.tree.progressTitle': 'Готово {done} из {total} деталей ниже — во всех цехах',
  'st.tree.otherAt': 'В: {dept}',
  'st.tree.otherDone': 'Готово',
  'st.tree.otherNoRoute': 'Без маршрута',
  'st.tree.otherOpen': 'Открыть {n} — деталь другого участка; работа — через «забрать» с кодом мастера',
  'st.tree.notHere': 'Не на вашем участке — объединяет детали ниже',
  'st.tree.openWork': 'Открыть работу',
  'st.tree.fold': 'Свернуть {n}',
  'st.tree.unfold': 'Развернуть {n}',
  'st.deliveryIn': 'Поставка проекта через {d} дн. · {date}',
  'st.deliveryToday': 'Поставка проекта сегодня · {date}',
  'st.deliveryLate': 'Поставка проекта просрочена на {d} дн. · {date}',
  'st.deliveryLateOne': 'Поставка проекта просрочена на 1 день · {date}',
  'st.deliveryNone': 'У проекта нет даты поставки',
  'gate.notYoursTitle': 'Деталь не на вашем участке',
  'gate.notYoursHint': 'Деталь сейчас на участке {dept}. Чтобы открыть чертёж, возьмите её на свой участок с разрешения руководителя — и отсчёт времени начнётся.',
  'gate.backToList': 'Вернуться к списку чертежей',
  'gate.drawingLocked': 'Чертёж откроется после проверок',
  'gate.drawingLockedHint': 'Необходимо выполнить проверки перед началом работы. Они требуются один раз для каждой детали.',
  'gate.qaAfter': 'Проверки качества перед передачей дальше',
  'gate.doneRecording': 'Я закончил запись',
  'meas.title': 'Размеры для измерения',
  'meas.titleRef': 'Размеры, которые нужно измерить',
  'meas.countOpen': "{n} из {total} ещё не измерены",
  'meas.countOpen_one': "{n} из {total} ещё не измерен",
  'meas.countOpen_few': "{n} из {total} ещё не измерены",
  'meas.countOpen_many': "{n} из {total} ещё не измерены",
  'meas.countDone': 'измерено: {total}',
  'meas.countRef': 'размеров: {total} · записываются в конце',
  'meas.unit': 'Единица',
  'meas.ofTotal': 'из {n}',
  'meas.critical': 'Критично',
  'meas.scopeMine': 'Ваш пост',
  'meas.scopeGeneral': 'Пост не указан',
  'meas.scopeOther': 'Другой пост',
  'meas.calibratedOnly': 'Только поверенный прибор',
  'meas.method': 'Средство измерения: {m}',
  'meas.ok': 'В норме',
  'meas.fail': 'Отклонение',
  'meas.record': 'Записать',
  'meas.laterAtFinish': 'Будет записано в конце работы',
  'meas.valueFor': 'Измеренное значение — {label}',
  'meas.saveFailed': 'Не удалось сохранить измерение',
  'hold.title': 'Деталь ожидает проверки инженера',
  'hold.requestedBy': 'Запросил: {name}',
  'hold.note': 'Можно работать на посту, где запрошена проверка. Следующий пост не откроется, пока инженер не подтвердит.',
  'notes.title': 'Заметки по артикулу',
  'notes.empty': 'Заметок нет.',
  'notes.ackedBy': 'Подтвердили прочтение:',
  'notes.toNc': 'Что-то не так? Открыть несоответствие',
  'notes.converted': 'Преобразовано в несоответствие',
  'notes.mention': 'Упомянуть людей в заметке',
  'notes.send': 'Отправить заметку',
  'notes.placeholder': 'Заметка для того, кто будет работать после вас',
  'notes.error': 'Ошибка',
  'meas.withTool': 'Прибор',
  'meas.pickTool': 'Выберите прибор',
  'meas.noTool': 'Нет доступного поверенного прибора — обратитесь в ОТК.',
  'meas.notConfirmed': 'Ожидает подтверждения инженера — измерение пока записать нельзя',
  'meas.onDrawing': 'Размер на чертеже',
  'meas.seeOnDrawing': 'Показать на чертеже',
  'meas.required': 'Требуемое значение',
  'meas.measuredUnit': 'Полученное значение · единица {n}',
  'meas.zoomIn': 'Увеличить',
  'meas.zoomOut': 'Уменьшить',
  'meas.noPosition': 'Положение выноски на чертеже для этой детали неизвестно',
  'st.startWork': 'Начать работу',
  'st.blocked': 'Заблокировано — начать нельзя',
  'st.ncOpenMark': 'Открыто несоответствие — на следующий участок не пройдёт',
  // ── „חלקים חסרים" / Не хватает деталей (27/09/2026) ─────────────────────
  'st.missingChildren': 'Не хватает деталей ({n})',
  'st.missingChildrenMore': 'и ещё {n}',
  'st.missingChildrenHelp': 'Работать можно — подготовка, оснастка, частичная сварка. Завершить этап можно только когда поступят все детали.',
  'st.pullToMyDept': 'Взять на мой участок',
  'st.reportNc': 'Сообщить о несоответствии',
  'st.workingNow': 'Сейчас работают над деталью',
  'st.you': ' (вы)',
  'st.close': 'Закрыть',
  'st.model3d': '3D-модель',
  'st.qty': 'Количество:',
  'st.rev': 'Ред.:',
  'st.finishWork': 'Завершить работу',
  'st.minimize': 'Свернуть',
  'st.pullTitle': 'Передача {part} на участок {dept}',
  'st.ncReported': '✓ Несоответствие отправлено — появится на табло',
  'st.ncStoppedTitle': 'Несоответствие записано — деталь не перейдёт на следующий участок',
  'st.ncStoppedHint': 'Следующий участок не сможет начать её, пока инженеры не решат. Если здесь делать нечего — перейдите к другой детали.',
  'st.ncStateHeld': 'Блокирующее несоответствие',
  'st.ncLoggedTitle': 'Несоответствие записано — можно продолжать работу над деталью',
  'st.ncOtherPart': 'Остановить часы и перейти к другой детали',
  'st.ncBackToList': 'Назад к списку',
  'st.deptOther': 'Другой участок',
  'st.deptOtherHide': 'Скрыть участки',
  'st.ncWaitEng': 'Ждёт инженеров',
  'st.ncWaitQa': 'Ждёт проверки',
  'st.ncOpenedBy': 'Открыл: {name} · {time}',
  'sl.missing': 'Не хватает',
  'sl.owner': 'У кого',
  'sl.next': 'Следующий шаг',
  'st.closeSessionOf': 'Закрытие работы',
  'st.managerCode': 'Код мастера',
  'st.closeMinutes': 'Сколько минут записать работнику',
  'st.closeMinutesHint': 'Прошло {n} минут с начала работы. Можно изменить — 0 не запишет ничего.',
  'st.closeMinutesHint_one': "Прошла {n} минута с начала работы. Можно изменить — 0 не запишет ничего.",
  'st.closeMinutesHint_few': "Прошло {n} минуты с начала работы. Можно изменить — 0 не запишет ничего.",
  'st.closeMinutesHint_many': "Прошло {n} минут с начала работы. Можно изменить — 0 не запишет ничего.",
  'st.closeSession': 'Закрыть работу',
  'st.managerApproval': 'Подтверждение мастера',
  'st.managerUser': 'Имя пользователя мастера',
  'st.password': 'Пароль',
  'st.approveAndPull': 'Подтвердить и взять',
  'dp.pinProject': 'Приоритет на участке',
  'dp.unpinProject': 'Снять приоритет',
  'dp.pinnedIn': 'Приоритет на участке {dept}',
  'dp.pinnedInNone': 'Приоритет не задан ни на одном участке',
  'dp.pinProjectHint': 'Любая деталь этого проекта, попавшая на этот участок — в том числе созданная завтра — поднимется в начало его очереди.',
  'dp.chooseDept': 'На каком участке?',
  'st.pullChoose': 'Что сделать с деталью?',
  'st.pullTake': 'Взять на мой участок',
  'st.pullTakeHint': 'Деталь переходит к вам. Укажите, сколько времени над ней работали на предыдущем участке — иначе их часы пропадут.',
  'st.pullParallel': 'Работать параллельно',
  'st.pullParallelHint': 'Оба участка работают над деталью одновременно. Ни один отсчёт не закрывается.',
  // «Я сам выполню предыдущий этап» (Эли, 29/09/2026)
  'st.pullSelf': 'Я сам выполню предыдущий этап',
  'st.pullSelfHint': 'Предыдущий этап не выполнен — вы делаете его сами, без кода мастера. Время записывается на предыдущий этап, а по окончании нажмите «Перехожу к своему этапу».',
  'st.pullSelfWhich': 'Какой этап вы выполните?',
  'st.pullSelfNone': 'Нет предыдущего этапа, который можно выполнить самому. Лазер, гибка и мехобработка остаются на своих участках.',
  'st.pullSelfBusy': '{name} сейчас работает на этом этапе ({min} мин.). Его отсчёт будет закрыт — нужен код мастера и сколько минут он фактически работал.',
  'st.pullSelfStart': 'Начать: {stage}',
  'st.selfPriorBanner': 'Вы выполняете {stage} (предыдущий этап). Когда закончите — «Перехожу к {own}».',
  'st.continueOwn': 'Закончил {stage} — перехожу к {own}',
  'st.finishWithoutContinue': 'Завершить, не переходя к своему этапу',
  'st.continuedOwn': '✓ {stage}: сдано {done}. Отсчёт перешёл на {own}.',
  'st.continuedOwnBlocked': '✓ {stage}: сдано {done}. {between} ещё не завершён — чтобы перейти к {own}, нужен обычный порядок (код мастера).',
  'st.forgotSwitch': 'Забыл переключиться — закончил и {own}',
  'st.ownUnits': 'Сколько единиц готово на {own}?',
  'st.priorMinutes': 'Сколько минут из {total} заняло {stage}?',
  'st.suggestFromStd': 'По нормативу: {min} мин.',
  'st.finishBoth': 'Сдать оба этапа',
  'st.finishedBoth': '✓ {stage}: {prior} · {own}: {ownDone}',
  'st.continuedOwnDone': '✓ {stage}: сдано {done}. {own} уже завершён — продолжать нечего.',
  'st.pullWorkedSoFar': 'Записано на участке {dept}: {min} мин.',
  'st.pullWorkedNone': 'На участке {dept} минуты не записаны — видимо, не отмечали в системе',
  'st.pullMinutesLabel': 'Сколько минут фактически работали на участке {dept}',
  'st.pullMinutesRequired': 'Укажите минуты перед передачей',
  'st.pullMinutesTitle': 'Сколько минут фактически работали на каждом участке',
  'st.pullStageOpen': 'Таймер открыт · записано {min} мин. — укажите фактическое время',
  'st.pullStageRecorded': 'Записано {min} мин. · открытого таймера нет',
  'st.pullNoEarlierStages': 'Предыдущих участков нет — это первая операция.',
  'st.afterFinish': 'После завершения → {dept}',
  'st.lastStage': 'Это последняя операция маршрута',
  'st.workTime': 'Время работы:',
  'st.howManyDone': 'Сколько деталей вы закончили?',
  'st.remainingHint': '(осталось {n} · не закончили ни одной? укажите 0)',
  'st.unitsNotAccepted': '«{v}» — это не количество, осталось {n}. Введите, сколько сделано (от 0 до {n}). Если отсканировали чертёж — закройте окно крестиком X.',
  'st.closeNoReport': 'Закрыть без отчёта',
  'st.zeroConfirm': '0 единиц — деталь остаётся на этом этапе и дальше не идёт. Если вы сделали детали — введите, сколько.',
  'st.zeroConfirmBtn': 'Да, сделано 0 — закрыть',
  'st.enterQty': 'Введите, сколько сделали',
  'st.arrivedFromPrev': 'Пришло с «{stage}»: {n}',
  'st.priorNotReported': '«{stage}» ещё не отчитался — ваш отчёт сохранится',
  'st.savedPriorToManager': ' · Отчёт сохранён. «{stage}» не отчитался — мастер производства подтвердит этап',
  'st.doneHereCount': 'Записано здесь: {n}',
  'st.overArrivedAll': 'Всё, что пришло с «{stage}», здесь уже отчитано. Чтобы записать больше, нужен код мастера.',
  'st.overArrived':'Это больше, чем пришло с «{stage}» — можно записать до {n}. Чтобы записать больше, нужен код мастера.',
  'st.stageAlreadyFull': 'Эта операция уже записана полностью — можно только закрыть без отчёта.',
  'st.scrapUnits': 'Брак',
  'st.goodUnits': 'Годные',
  'st.closeFillAll': 'Введите годные и брак по каждому таймеру — 0, если нет.',
  'st.gapTitle': 'Не хватает {hm} ч — чем вы занимались?',
  'st.gapBody': 'Сегодня на станции: {present} · записано на таймерах и вспомогательной работе: {recorded}',
  'st.gapLastPart': '{part} (проект {project})',
  'st.gapCleaning': 'Уборка и порядок',
  'st.gapWaiting': 'Ожидание работы/материала',
  'st.gapOther': 'Другое',
  'st.gapOtherNote': 'Чем именно?',
  'st.gapSave': 'Сохранить и выйти',
  'st.gapSkip': 'Пропустить',
  'st.gapSaved': 'Передано начальнику. Хорошего дня!',
  'st.gapSaveFailed': 'Не сохранилось — можно пропустить и сказать начальнику.',
  'st.targetIncludesScrap': 'Включая забракованные на следующем этапе — их нужно заменить',
  'st.stoppedTitle': 'Работа остановлена',
  'st.stoppedECO': 'Открыто инженерное изменение (ECO) — по прежнему чертежу продолжать нельзя',
  'st.stoppedDRAWING': 'Чертёж заменён — работайте по новой версии',
  'st.stoppedROUTE': 'Руководитель изменил маршрут изготовления детали',
  'st.stoppedCANCELLED': 'Проект отменён — работать по нему нельзя',
  'st.stoppedNC': 'Инженерия ответила на несоответствие по детали',
  'st.stoppedAsk': 'Сколько единиц вы успели сделать до остановки?',
  'st.stoppedHint': 'Отработанное время уже записано. Единицы запишутся на этот этап.',
  'st.stoppedSave': 'Записать',
  'st.stoppedNone': 'Ничего не успел',
  'st.stoppedSaved': 'Записано единиц: {n}',
  'st.scrapHint': '{n} шт. брака не идут дальше — на них откроется несоответствие.',
  'st.scrapHint_one': "{n} шт. брака не идёт дальше — на неё откроется несоответствие.",
  'st.scrapHint_few': "{n} шт. брака не идут дальше — на них откроется несоответствие.",
  'st.scrapHint_many': "{n} шт. брака не идут дальше — на них откроется несоответствие.",
  'err.projectCancelled': 'Проект {projectNumber} отменён — работу по нему продолжать нельзя',
  'err.ecoOpen': 'Чертёж детали меняется (ECO) — начинать нельзя: {text}',
  'err.faiPending': 'FAI — первая единица ждёт полной проверки ОТК, следующую начинать нельзя. Позовите начальника производства или главного инженера.',
  'err.ncBlocking': 'Открытое несоответствие останавливает деталь: {text}',
  'err.checklistIncomplete': 'Перед началом работы нужно выполнить: {items}',
  'err.engInspectionPending': 'Деталь ждёт проверки инженера (пост «{stageName}») — продолжать нельзя, пока инженер не подтвердит.',
  'err.stageComplete': 'Операция «{stageName}» уже полностью отмечена ({done}/{target}) — последний отчёт: {by}, {when}. Если это ошибка — начальник производства исправляет отчёт в «Истории» (0 шт.), и тогда можно начать. Ничего не записано.',
  'err.oldVersion': 'Отсканирован не актуальный чертёж — распечатайте обновлённый перед работой',
  // экран остановки при сканировании (21/10/2026)
  'stop.title': 'СТОП — не работать с этой деталью',
  'stop.ok': 'Понятно — не начинаю',
  'stop.who.nc': 'Обратитесь к начальнику производства или в инженерный отдел. Деталь освободится, когда инженеры ответят на несоответствие.',
  'stop.who.hold': 'Обратитесь к начальнику производства — только он снимает остановку.',
  'stop.who.eco': 'Чертёж будет изменён — работа сейчас пойдёт в брак. Обратитесь в инженерный отдел.',
  'stop.who.fai': 'Первая единица ждёт проверки QA. Обратитесь в QA или к главному инженеру.',
  'stop.who.inspection': 'Деталь ждёт утверждения инженера. Обратитесь к инженеру проекта.',
  'stop.who.drawingWait': 'Начальник решил ждать чертёж. Обратитесь к начальнику производства.',
  // работа без чертежа (21/10/2026)
  'nodraw.title': 'Нет чертежа для этой детали',
  'nodraw.body': 'Можно продолжить без чертежа — часы идут как обычно, а работа будет отмечена «без чертежа». Инженеры получат уведомление.',
  'nodraw.continue': 'Продолжить без чертежа',
  'nodraw.cancel': 'Не сейчас',
  'nodraw.managerWait': 'Начальник: остановить до чертежа',
  'nodraw.flag': 'без чертежа',
  'err.productionHold': 'Производство по этой детали остановлено — {text}',
  'err.revisionNotChanged': 'Работа уже идёт по актуальной ревизии',
  'rev.title': 'Новая ревизия {rev}',
  'rev.body': 'Чертёж обновлён с {from} на {to}. Продолжать по {from} нельзя.',
  'rev.clockRuns': 'Таймер продолжает идти — время до подтверждения записывается на {from}.',
  'rev.ask': 'Сколько вы сделали по {from}?',
  'rev.zeroOk': '0 — тоже правильный ответ.',
  'rev.scrap': 'Брак (необязательно)',
  'rev.confirm': 'Обновить — перейти на {to}',
  'rev.unitsBad': 'Введите целое число, 0 или больше',
  'rev.switched': 'Вы перешли на ревизию {to} — новый таймер запущен',
  'rev.notStartedComplete': 'Операция завершена — новый таймер не запущен',
  'rev.notStartedHold': 'Производство по детали остановлено — {text}. Новый таймер не запущен.',
  'rev.refreshTo': 'Обновить на {to}',
  'rev.titleFirst': 'Загружен чертёж — ревизия {rev}',
  'rev.bodyFirst': 'Работа началась без чертежа, теперь загружен чертёж ревизии {to}. Продолжайте по нему.',
  'rev.clockRunsFirst': 'Таймер продолжает идти — время до подтверждения записывается на работу без чертежа.',
  'rev.askFirst': 'Сколько вы сделали без чертежа?',
  'rev.mineBanner': 'Новая ревизия {to} — откройте деталь и обновите',
  'err.approvalPending': 'Запрос отправлен — ждёт подтверждения мастера. Отсканируйте снова после подтверждения.',
  'err.approvalAssembly': 'Детали этой сборки ещё не готовы — нужно подтверждение мастера',
  'err.approvalSkip': 'Предыдущая операция не завершена — нужно подтверждение мастера',
  'err.assemblyChildrenMissing': 'Этап нельзя завершить — не хватает деталей: {missingLabel}. Можно сообщить меньшее количество или закрыть смену без отчёта; завершить этап можно только когда детали поступят.',
  'err.notSessionOwner': 'Нельзя менять работу другого работника',
  'err.partNotFound': 'Деталь не найдена',
  'err.partCutNoDrawingParent': 'Деталь {partNumber} вырезана на лазере {cutAt}, но её чертёж ещё не загружен — инженерный отдел уже получил запрос. Пока можно работать над сборкой {parent} или обратиться к начальнику производства.',
  'err.partCutNoDrawing': 'Деталь {partNumber} вырезана на лазере {cutAt}, но её чертёж ещё не загружен — инженерный отдел уже получил запрос. Пока обратитесь к начальнику производства.',
  'err.deptRequired': 'У работника не указан участок',
  'err.deptMismatch': 'Выбранный участок не входит в маршрут этой детали',
  'err.employeeInactive': 'Работник неактивен',
  'err.sessionClosedByManager': 'Работу только что закрыл мастер',
  'err.machineRequired': 'Выберите, на каком станке идёт работа',
  'err.machineNotActive': 'Станок {machineName} выведен из работы — выберите другой станок.',
  'err.machineWrongDept': 'Станок {machineName} не относится к участку «{departmentName}» — выберите станок из списка.',
  'err.maintenanceDue': 'Начать работу нельзя — {items}. Выполните обслуживание и подтвердите его на экране станков.',
  'err.coworkersDisabled': 'Совместная работа не разрешена',
  'err.coworkerInvalid': 'Нельзя добавить самого себя как напарника',
  'err.tooManyCoworkers': 'Можно добавить не более {max} напарников',
  'err.noDepartment': 'За вами не закреплён участок — обратитесь в офис',
  'err.drawingNotYours': 'Деталь сейчас на участке «{departmentName}» — чтобы открыть чертёж, её нужно перевести на ваш участок с разрешения мастера',
  'err.drawingForbidden': 'Перед открытием чертежа отсканируйте бейдж',
  'err.badgeTokenInvalid': 'Срок опознания истёк — отсканируйте бейдж или выберите себя из списка снова',
  'err.cardNotEnrolled': 'Карта не привязана к работнику. Её нужно выпустить на экране карт работников, либо пока опознайтесь из списка.',
  'err.employeeNotFound': 'Работник не найден',
  'err.sessionNotYours': 'Эта работа не ваша',
  'err.taskNotYours': 'Это задание не ваше',
  'err.alreadyReported': 'Вы уже отчитались за эту остановку',
  'err.sessionAlreadyOpen': 'Работа уже открыта',
  'err.invalidPayload': 'Недопустимый штрихкод',
  'err.laserExcelOnly': 'Лазерный участок отчитывается загрузкой файла Excel на посту лазера, а не сканированием',
  'err.laserNotConfigured': 'Лазерный участок не задан в настройках системы',
  'err.invalidFile': 'Файл пуст или повреждён',
  'err.noPhoto': 'Снимок не сделан',
  'err.noParts': 'В файле не найдено деталей — проверьте, что это отчёт раскроя лазерного станка',
  'err.noReportableParts': 'В файле нет деталей для отчёта — все детали пропущены',
  'err.itemNotDeletable': 'Пункт формы контроля качества удалить нельзя. Пункт, не относящийся к проекту, помечается «не применимо» с пояснением — так он остаётся в отчёте, и видно, кто и почему так решил.',
  'err.badVehicleCode': 'Бейдж не распознан, или наклейка в машине заменена. Отсканируйте бейдж ещё раз, а если повторится — заново отсканируйте наклейку в машине.',
  'err.tripIncomplete': 'Поездку нельзя закрыть. Заполните цель и пробег на выезде и при возврате и нажмите ещё раз.',
  'err.odometerBackwards': 'Пробег при возврате ({back}) меньше пробега при выезде ({out}). Проверьте одометр и введите ещё раз.',
  'err.odometerLow': 'Вы ввели {typed}, а последний пробег машины {last}. Проверьте одометр. Если на нём именно так, нажмите «Так на одометре».',
  'err.vehicleFormInvalid': 'Одно из полей заполнено неверно. Проверьте, что пробег — целое число без точки, и попробуйте ещё раз.',
  'err.tripNotFound': 'Поездка не найдена в этой машине. Отсканируйте наклейку ещё раз и выберите из списка.',
  'err.tripOtherVehicle': 'Это поездка другой машины. Отсканируйте наклейку той машины, на которой вы ехали.',
  'err.tripOtherDriver': 'Это поездка другого водителя. Чтобы отчитаться за неё, выберите «Отчитаться о возврате вместо него».',
  'err.tripProjectRequired': 'Для поездки типа «{purpose}» нужно указать проект. Выберите проект и сохраните ещё раз.',
  'err.refuelIncomplete': 'Вы отметили заправку — укажите, сколько литров и сколько заплачено, и сохраните ещё раз.',
  'err.checkFutureDay': 'Нельзя отчитаться о проверке за день, который ещё не наступил. Обновите экран и попробуйте ещё раз.',
  'err.checkNoteRequired': 'Непройденный пункт проверки требует пояснения — {item}. Напишите, что обнаружено, и сохраните ещё раз.',
  'err.monthAlreadyReported': 'Пробег за этот месяц уже записан. Исправление делается в офисе — обратитесь к офис-менеджеру.',
  'err.monthReadingLow': 'Пробег меньше, чем в начале месяца ({start}). Проверьте одометр и введите ещё раз.',
  'err.notMonthlyVehicle': 'Эта машина отчитывается за каждую поездку, а не раз в месяц. Отсканируйте наклейку ещё раз.',
  'err.tripClosed': 'Поездка уже закрыта. Исправление делается из офиса, с причиной, которая сохраняется в поездке.',
  'err.monthClosed': 'Месяц закрыт — стоимость использования уже отчитана. Чтобы изменить, откройте месяц заново.',
  'err.monthIncomplete': 'Нельзя закрыть месяц без пробега на его начало и конец',
  'err.mustSignIn': 'Нужно войти в систему',
  'saw.continueThisBar': 'Продолжить этот пруток',
  'saw.overRangeAngle': 'Угол {deg}° от перпендикуляра — больше хода головки пилы ({max}°).',
  'saw.overRangeComplement': 'Дополнительный угол — {comp}°, но если набрать его, деталь выйдет другой: поворот профиля в тисках не меняет угол реза относительно оси.',
  'saw.leftoverAsk': 'Сколько осталось от прутка? ({unit})',
  'saw.expectedHint': 'Расчёт ожидал {n} {unit}. Измерьте фактически — ваше измерение главнее.',
  'saw.willScrap': 'Короче минимума, заданного для профиля ({len}) — будет записано в лом и в планирование не войдёт.',
  'saw.barsCount': 'прутков: {bars} · отрезков: {pieces}',
  'rem.belowMinimum': '{len} — меньше минимальной длины, заданной для этого профиля. Записано, но в планирование не войдёт.',
  'rem.belowMinProfile': 'Короче минимальной длины ({len}) — будет записано в лом и в планирование не войдёт.',
  'rem.saveAndGetSku': 'Сохранить и получить артикул',
  'rem.endAngleShort': 'Торец {deg}°',
  'rem.fromProject': 'из проекта {n}',
  'wh.waitingPick': 'Ожидает комплектации',
  'wh.waitingPickWarehouse': 'Ожидает комплектации на складе',
  'wh.staleStock': 'Складские количества — из файла, загруженного {n} дн. назад, а не в реальном времени.',
  'wh.bindQuestion': 'К какому артикулу он относится? Привяжем один раз — и дальше сканирование этой коробки будет опознавать позицию само.',
  'wh.noSkuMatch': 'Подходящий артикул не найден. Можно закрыть и продолжить со свободным названием — привязать можно позже.',
  'veh.vehicleSubtitle': 'Машина {plate}',
  'veh.checkSavedFailures': 'Проверка сохранена. Замечаний передано мастеру: {n}.',
  'veh.failList': '{items} — выезжать на этой машине нельзя. Обратитесь к мастеру.',
  'st.openWorks': 'У вас открытых работ: {n}',
  'st.openWorkOne': 'У вас есть открытая работа',
  'err.parallelLimit': 'У вас уже открыто 8 чертежей ({parts}). Завершите или закройте один из них — и тогда начните этот.',
  'st.drawingOf': 'Чертёж {i} из {n}',
  'st.weldIntoParent': 'Сварка в',
  'st.childOpenStage': 'Открытый этап: {stages}',
  'st.prevDrawing': 'Предыдущий чертёж',
  'st.nextDrawing': 'Следующий чертёж',
  'st.pageOf': 'Страница {i} из {n}',
  'st.workDept': 'Записывается на отдел:',
  'st.whatDidYouDo': 'Что вы делали с деталью?',
  'st.skippedPending': 'Пропущен — ждёт отчёта в 07:00',
  'st.resumeWork': 'Вернуться к работе ›',
  'st.myWorkOne': 'Ваша открытая работа',
  'st.myWorkMany': 'Ваша открытая работа — чертежей: {n}',
  'st.clickToEnter': 'Нажмите, чтобы войти ›',
  'st.pickCard':'Отметить, чтобы открыть вместе',
  'st.openPicked': 'Открыть отмеченные ({n})',
  'st.clearPicked': 'Снять отметки',
  'st.prevPage': 'Предыдущая страница',
  'st.nextPage': 'Следующая страница',
  'st.needQualification': 'Обратитесь в офис для оформления аттестации.',
  'st.priorEmpty': 'На этапе «{stage}» по этой детали ещё не отчитано ни одной единицы. Работайте и отчитывайтесь как обычно — ваш отчёт сохранится, а мастер производства подтвердит этап «{stage}».',
  'st.idleNudge': 'Часы продолжают идти — {n} мин без касания экрана. Всё ещё работаете над этим?',
  'st.priorClockTitle': 'Завершите предыдущее',
  'st.priorClockFinish': 'Завершить предыдущее ({n} шт.)',
  'st.priorClockSkip': 'Пропустить',
  'st.finishAll': 'Завершить всё',
  'st.finishAllTitle': 'Завершить все открытые часы',
  'st.finishAllBody': 'Сколько вы сделали по каждой? 0 допустимо. После закрытия вы останетесь в системе и сможете выбрать новую работу.',
  'st.finishAllConfirm': 'Завершить всё',
  'st.finishAllDone': 'Все часы закрыты',
  'st.finishAllNone': 'У вас нет открытых часов',
  'st.idleTitle': 'Тук-тук — вы ещё здесь?',
  'st.idleBody': 'Время идёт уже {n} мин., а экран никто не касался. Открытый чертёж считается рабочим временем, но это не ответ на этот вопрос.',
  'st.idleWarning': 'Без подтверждения работа закроется с отчётом 0 единиц. Часы до этой минуты сохранятся, а начальник производства должен будет подтвердить количество и часы.',
  'st.idleYes': 'Да, я здесь',
  'saw.cutLabel': 'Отрезано:',
  'saw.cutPieceLength': '{n} мм',
  'rem.realLengthPrompt': 'Фактическая длина {sku} в мм:',
  'st.switchOpenTitle': 'У вас открыта работа',
  'st.switchOpenBody': 'Перед сменой работника её нужно закрыть — иначе время продолжит идти на ваше имя и попадёт в работу следующего. 0 = закрыть без отчёта.',
  'st.switchClosing': 'Закрываю…',
  'st.switchCloseAndSwitch': 'Завершить работу и сменить работника',
  'st.switchOpenSince': 'Открыто с {time} · выполнено {done} из {qty}',
  'st.switchHowManyNow': 'Сколько сделали сейчас?',
  'mg.loadFailed': 'Не удалось загрузить состояние станков',
  'mg.blockedTitle': 'Начать нельзя — на участке не выполнено обслуживание',
  'mg.whichMachine': 'На каком станке?',
  'mg.requiredBeforeStart': 'Нужно выбрать перед началом работы.',
  'mg.maintenanceMissing': 'Нет обслуживания',
  'mg.dialogLabel': 'Проверки перед запуском станка',
  'mg.close': 'Закрыть',
  'mg.maintenance': 'Обслуживание: {freq}',
  'mg.ok': 'Исправно',
  'mg.notOk': 'Неисправно',
  'mg.whatIsWrong': 'Что неисправно?',
  'mg.saving': 'Сохраняю…',
  'mg.remaining': 'Осталось пунктов: {n}',
  'mg.writeWhatWrong': 'Напишите, что неисправно',
  'mg.confirmDone': 'Подтвердить выполнение',
  'st.reportAndFinish': 'Записать и завершить',
  'st.saved': 'Записано ✓',
  'st.todayReport': 'Отчёт за сегодня',
  'st.production': 'Производство',
  'st.support': 'Вспомогательные',
  'st.missing': 'не хватает',
  'st.filledGap': 'Подставлено {time} — выберите категорию и нажмите «Записать», чтобы закрыть день.',
  'st.time': 'Время:',
  'st.minutesShort': 'мин.',
  'st.projectOptional': 'Номер проекта (необязательно)',
  'st.incoming': 'Поступает ко мне ({n})',
  'st.incomingHint': '— нажмите на деталь, чтобы посмотреть или взять',
  'st.incomingMore': 'Показать ещё {n}',
  'st.incomingLess': 'Показать меньше',
  'st.nowInDeptOne': 'Сейчас на участке {dept} · одна деталь',
  'st.nowInDept': 'Сейчас на участке {dept} · деталей: {n}',
  'st.noDrawing': 'Нет чертежа',
  'st.nextStage': 'Следующая операция!',
  'st.stagesToGo': 'ещё операций: {n}',
  'st.now': 'Сейчас:',
  'st.arrivesAfter': 'Поступит к вам после «{stage}»',
  'st.arrivesNext': 'Поступит к вам на следующей операции',
  'st.hoursShort': 'ч.',
  'st.minsShort': 'мин.',
  'st.openSessionExists': 'Работа уже открыта',
  'st.part': 'Деталь:',
  'st.stage': 'Операция:',
  'st.heldBy': 'У кого:',
  'st.openFor': 'Открыта уже {time}',
  'st.continueSession': 'Продолжить работу',
  'st.closeSessionApproval': 'Закрыть работу (подтверждение мастера)',
  'st.howManyArrived': 'Сколько деталей поступило на ваш пост?',
  'st.arrivedHint': '(завершено {stage} · до {max})',
  'st.managerCodeLabel': 'Код мастера для подтверждения',
  'st.approveClose': 'Подтвердить закрытие',
  'st.ncTitle': 'Сообщение о несоответствии',
  'st.ncPart': 'Деталь',
  'st.noCategories': 'Категории не заданы',
  'st.whichStation': 'Какой пост? (необязательно)',
  'st.descOptional': 'Описание (необязательно)',
  'st.markupAttached': 'Отметка приложена — нажмите, чтобы изменить',
  'st.markOnDrawing': 'Отметить на чертеже (необязательно)',
  'st.sendReport': 'Отправить',

  // ── language switcher ──
  'lang.switch': 'Язык',
  'lang.he': 'עברית',
  'lang.ru': 'Русский',
  'lang.saved': 'Язык сохранён',
  'st.add': "Добавить",
  'st.minShort': "мин",
  'st.hourShort': "ч",
  'st.minShort2': "м",
  'st.closeSessionTitle': "Закрытие сессии — подтверждение руководителя",
  'st.closeSessionTail': "будет записано в историю (0 единиц).",
  'st.leaveDrawing': "Выход из чертежа",
  'st.afterFinishTo': "После завершения → передать в отдел",
  'st.supportWork': "вспомог.",
  'st.covered': "закрыто ✓",
  'st.completeDay': "Закрыть день",
  'st.reportSupport': "+ Отчёт о вспомогательной работе",
  'st.filledHint': "Заполнено {v} — выберите категорию и нажмите «Записать», чтобы закрыть день.",
  'st.record': "Записать",
  'st.sessionOpenTitle': "Уже есть открытая сессия",
  'st.partLabel': "Деталь:",
  'st.currentPart': "(текущая деталь)",
  'st.stageLabel': "Этап:",
  'st.atLabel': "У:",
  'st.closeSessionMgr': "Закрыть сессию (подтверждение руководителя)",
  'st.cancel2': "Отмена",
  'st.finishedUpTo': "(завершил {stage} · до {max})",
  'st.back': "Назад",
  'st.confirmClose': "Подтвердить закрытие",
  'st.ncReport': "Отчёт о несоответствии",
  'st.handled': "Обработано",
  // ── identify without a badge (shared by every kiosk) ──
  'id.cantScan': "Не удаётся отсканировать? Выберите из списка",
  'id.pickYourself': "Выберите себя из списка",
  'id.noMatch': "Подходящий работник не найден",
  // ── Сообщение о проблеме в системе, общее для всех киосков ──
  'rep.button': "Сообщить о проблеме",
  'rep.title': "Что-то не работает?",
  'rep.badgeFirst': "Отсканируйте бейдж, чтобы сообщить",
  'rep.what': "Что случилось?",
  'rep.placeholder': "Нажал «Готово», и ничего не произошло…",
  'rep.context': "Отправляется вместе с названием экрана и версией системы, чтобы можно было воспроизвести, не спрашивая вас.",
  'rep.send': "Отправить",
  'rep.sent': "Сообщение отправлено. Спасибо — это помогает.",
  'rep.failed': "Сообщение не отправлено. Попробуйте ещё раз.",
  'ks.scanBadgeFirst': "Сначала отсканируйте бейдж работника",
  'ks.deptMissing': "У работника не указан отдел",
  'ks.deptMismatch': "Ваш отдел не участвует в маршруте этой детали",
  'ks.badManagerPass': "Неверный пароль руководителя",
  'ks.stIdle': "Отсканируйте бейдж работника",
  'ks.stBadgeOk': "Готово к сканированию чертежа",
  'ks.stAwaitingUnits': "Сессия открыта — сколько единиц?",
  'ks.stAwaitingApproval': "Ожидает подтверждения руководителя",
  'ks.stResolvingPrior': "Сначала закройте предыдущую сессию",
  'ks.stResult': "✓ Сохранено",
  'ks.stStarted': "✓ Работа начата",
  'ks.subtitle': "Система отчётности работника",
  'ks.configWarn': "⚠ Настройки киоска не загружены — работа со значениями по умолчанию ({s} с)",
  // ── Киоск финишной обработки (Эли, 17.09.2026) ──
  // ── Отчёт контроля — восьмой и последний из восьми экранов ──
  'ir.intro': "Все размеры, измеренные по артикулу — требуемое, фактически измеренное и отклонения — в одном отчёте, который можно распечатать и приложить к отправке. Для начала найдите номер проекта или артикул.",
  'ir.searchLabel': "Найдите номер проекта или артикул",
  'ir.typeTwoChars': "Введите не менее двух символов. Артикулы проекта начинаются с его номера, поэтому поиск по номеру проекта покажет их все.",
  'ir.noParts': "Артикулы не найдены.",
  'ir.emptyTitle': "Отчёт контроля по артикулу",
  'ir.emptyLead': "Введите выше номер проекта или артикул — не менее двух символов — и выберите из списка.",
  'ir.emptyB1': "· Размеры, считанные с чертежа, с утверждённым диапазоном для каждого.",
  'ir.emptyB2': "· Что измерено фактически, кем и когда, и заключение относительно диапазона.",
  'ir.emptyB3': "· Что ещё не измерено — строка, ради которой этот экран и открывают.",
  'ir.emptyNote': "Артикул без размеров, отмеченных на чертеже, строк не покажет. Порядок отметки описан в «Как работает качество».",
  'ir.projectPrefix': "· проект {n}",
  'ir.revision': " · ревизия {n}",
  'ir.quantity': " · количество {n}",
  'ir.manualChar': "Характеристика вручную",
  'ir.printTitle': "Протокол контроля размеров",
  'ir.printPart': "Артикул",
  'ir.printVerdict': "Результат",
  'ir.printReadings': "Измерения",
  'ir.printFailed': "Отклонения",
  'ir.print': "Печать",
  'ir.changePart': "Сменить артикул",
  'ir.noCharacteristics': "По этому артикулу нет характеристик контроля. Их можно считать с размеров, отмеченных на чертеже жёлтым, либо добавить вручную.",
  'ir.conforms': "Соответствует",
  'ir.notConforms': "Не соответствует",
  'ir.inProgress': "В процессе",
  'ir.summaryCounts': "характеристик: {chars} · измерений: {readings}",
  'ir.summaryFailed': " · отклонений: {n}",
  'ir.summaryUnconfirmed': " · ожидают утверждения инженера: {n}",
  'ir.noReadingsYet': "— измерений пока не внесено",
  'ir.balloon': "Выноска",
  'ir.colCharacteristic': "Характеристика",
  'ir.colRequirement': "Требование",
  'ir.colMethod': "Средство",
  'ir.colUnitN': "ед. {n}",
  'ir.awaitingApproval': "Ожидает утверждения",
  'ir.toolTitle': "Инструмент {code}",
  'ir.sigInspected': "Проверил",
  'ir.sigApproved': "Утвердил",
  'ir.sigDate': "Дата",
  'ir.typeDimensional': "Размер",
  'ir.typeSurface': "Шероховатость",
  'ir.typePressure': "Давление",
  'ir.typeVisual': "Визуальный",
  'ir.typeMaterial': "Материал",
  'ir.noTraceability': "Для этого артикула прослеживаемость не задана — идентификация только по артикулу и ревизии. Чтобы добавить серийный номер или номер партии: дерево изделия → выбор артикула → «Идентификация и прослеживаемость».",
  'ir.resultAccepted': "Принят",
  'ir.resultAcceptedDev': "Принят с отклонением",
  'ir.resultRejected': "Забракован",
  'ir.identityMaterial': "Идентификация и материал",
  'ir.modeSerial': "Серийный номер на каждую единицу",
  'ir.modeBatch': "Номер партии на запуск",
  'ir.modeNone': "Без прослеживаемости",
  'ir.missingIdentity': "Нет идентификации у {n} единиц",
  'ir.missingIdentity_one': "Нет идентификации у {n} единицы",
  'ir.missingIdentity_few': "Нет идентификации у {n} единиц",
  'ir.missingIdentity_many': "Нет идентификации у {n} единиц",
  'ir.hSerial': "Серийный номер",
  'ir.hBatch': "Номер партии",
  'ir.hMaterial': "Материал",
  'ir.hSupplier': "Поставщик",
  'ir.hDeliveryNote': "Накладная",
  'ir.hIncomingCheck': "Входной контроль",
  'ir.materialFootnote': "Материал берётся из приёмки, привязанной к единице на экране «Идентификация и прослеживаемость» артикула. Пустая строка = материал не привязан, а не «материала нет».",
  'ir.instrumentsUsed': "Средства измерения в этом отчёте",
  'ir.dialogLabel': "Запись измерения",
  'ir.balloonN': "Выноска {n}",
  'ir.unitN': "Единица {n}",
  'ir.requirement': "Требование",
  'ir.pass': "Годен",
  'ir.fail': "Негоден",
  'ir.phMeasured': "Измеренное значение",
  'ir.measuringTool': "Средство измерения",
  'ir.notSpecified': "— не указано —",
  'ir.noUsableTools': "В реестре нет действующих и откалиброванных средств измерения. Инструмент с просроченной калибровкой не будет принят.",
  'ir.phNote': "Примечание (необязательно)",
  'ir.saveMeasurement': "Сохранить измерение",
  'ir.measureFootnote': "Результат определяется относительно требования и сохраняется. Последующее исправление требования не изменит уже внесённое измерение.",
  'ir.saveError': "Ошибка сохранения измерения",
  'ir.close': "Скрыть",
  'ir.unconfirmedTitle': "Считано с чертежа и ожидает утверждения ({n})",
  'ir.cannotMeasureUnconfirmed': "По неутверждённой характеристике измерять нельзя",
  'ir.identifiedAs': "Распознано как:",
  'ir.noToleranceFound': " — допуск не распознан, заполните вручную",
  'ir.checkAndConfirm': "Проверить и утвердить",
  'ir.delete': "Удалить",
  'ir.specType': "Тип спецификации",
  'ir.specNominalTol': "Номинал + допуск",
  'ir.specMinMax': "Минимум / максимум",
  'ir.specPassFail': "Годен / негоден",
  'ir.nominal': "Номинал",
  'ir.tolMinus': "Нижний допуск",
  'ir.tolPlus': "Верхний допуск",
  'ir.min': "Минимум",
  'ir.max': "Максимум",
  'ir.unit': "Единица",
  'ir.method': "Средство измерения",
  'ir.phMethod': "Штангенциркуль, КИМ, профилометр…",
  'ir.phMethodShort': "Штангенциркуль…",
  'ir.confirmChar': "Утвердить характеристику",
  'ir.description': "Описание",
  'ir.type': "Тип",
  'ir.spec': "Спецификация",
  'ir.error': "Ошибка",
  'ir.save': "Сохранить",
  'ir.cancel': "Отмена",
  // ── Реестры качества (средства измерения / аттестации) — седьмой из восьми ──
  'qr.calibrationOverdue': "средств измерения просрочили калибровку — использовать нельзя до калибровки.",
  'qr.qualificationsExpired': "аттестаций истекло.",
  'qr.tabEquipment': "Средства измерения",
  'qr.tabPeople': "Аттестации работников",
  'qr.tabChecklist': "Проверки перед работой",
  'qr.statusActive': "Действует",
  'qr.statusOutOfService': "Выведен",
  'qr.statusRetired': "Снят с учёта",
  'qr.error': "Ошибка",
  'qr.phCode': "Идентификатор (обязательно)",
  'qr.phName': "Название инструмента (обязательно)",
  'qr.phKind': "Тип (штангенциркуль, манометр…)",
  'qr.phSerial': "Серийный номер",
  'qr.phLocation': "Расположение",
  'qr.save': "Сохранить",
  'qr.cancel': "Отмена",
  'qr.colCode': "Идентификатор",
  'qr.colName': "Название",
  'qr.colKind': "Тип",
  'qr.colLocation': "Расположение",
  'qr.colLastCal': "Последняя калибровка",
  'qr.colNextCal': "Следующая калибровка",
  'qr.colStatus': "Состояние",
  'qr.notManaged': "(не отслеживается)",
  'qr.close': "Скрыть",
  'qr.calibrations': "Калибровки",
  'qr.recordCalibration': "Запись калибровки",
  'qr.resultPass': "Годен",
  'qr.resultAdjusted': "Откалиброван",
  'qr.resultFail': "Не годен",
  'qr.failWarning': "Отказ сразу переведёт инструмент в состояние «Выведен», и дата калибровки не сдвинется.",
  'qr.noCalibrations': "Записей о калибровке нет.",
  'qr.nextDue': "→ следующая {date}",
  'qr.validityReviewOpened': "Открыто корректирующее действие",
  'qr.noValidCal': " — действующей калибровки в записи нет, поэтому включены все выполненные измерения",
  'qr.balloonUnit': "Выноска {balloon}, ед. {unit}",
  'qr.measOk': "Годно",
  'qr.measFailed': "Брак",
  'qr.ownerPlaceholder': "Ответственный…",
  'qr.openAction': "Открыть",
  'qr.returnToService': "Вернуть в работу",
  'qr.retire': "Снять с учёта",
  'qr.qualTypes': "Виды аттестации",
  'qr.noExpiry': " (без срока)",
  'qr.phNewQual': "Новая аттестация (сварка TIG…)",
  'qr.add': "Добавить",
  'qr.grantRenew': "Выдача / продление",
  'qr.employeePlaceholder': "Работник…",
  'qr.qualPlaceholder': "Аттестация…",
  'qr.expiry': "Срок",
  'qr.expiryRequired': " (обязательно)",
  'qr.colEmployee': "Работник",
  'qr.colQual': "Аттестация",
  'qr.colGranted': "Выдана",
  'qr.colExpiry': "Срок",
  'qr.noExpiryValue': "Без срока",
  'qr.expired': " · истекла",
  'qr.expiringInDays': " · через {n} дн",
  'qr.revoke': "Отозвать",
  'qr.newTool': "Новый инструмент",
  'qr.showRetired': "Показывать и снятые с учёта",
  'qr.emptyEquipment': "В реестре нет средств измерения. Каждый инструмент, которым измеряют для контроля качества, должен быть здесь.",
  'qr.overdueDays': "(просрочено на {n} дн)",
  'qr.dueInDays': "(через {n} дн)",
  'qr.performed': "Выполнена",
  'qr.result': "Результат",
  'qr.lab': "Лаборатория",
  'qr.checkPastMeasurements': "Проверить действительность прошлых измерений",
  'qr.toolUnfitPrompt': "Инструмент признан негодным. Измерения, выполненные им после предыдущей калибровки, могут быть ошибочны — кто проверяет и до какого срока?",
  'qr.searchingMeasurements': "Поиск измерений, выполненных этим инструментом…",
  'qr.noSuspect': "Измерений этим инструментом в данном интервале не найдено — сомнительных измерений нет.",
  'qr.suspectCount': "измерений выполнено этим инструментом: {n}",
  'qr.sinceLastGood': " после последней годной калибровки ({date})",
  'qr.disable': "Вывести",
  'qr.peopleNotice': "Реестр фиксирует и предупреждает — работу он не блокирует. Блокировка работника без аттестации будет включена только после заполнения реестра, и это ваше решение.",
  'qr.expires': "Со сроком",
  'qr.grantedAt': "Дата аттестации",
  'qr.emptyQualifications': "Аттестаций не зарегистрировано.",
  'qr.remove': "Удалить",
  // ── Отчёт по несоответствиям — шестой из восьми экранов ──
  'ncr.groupNone': "Без группировки",
  'ncr.groupProject': "Проект",
  'ncr.groupEngineer': "Инженер",
  'ncr.groupReporter': "Заявитель",
  'ncr.groupCategory': "Тип",
  'ncr.noEngineer': "— без инженера",
  'ncr.print': "Печать",
  'ncr.printTitle': "Отчёт о несоответствиях",
  'ncr.printAll': "Все несоответствия, без фильтра",
  'ncr.printRows': "Несоответствий: {n}",
  'ncr.filter': "Фильтр:",
  'ncr.filterProject': "Фильтр по проекту",
  'ncr.allProjects': "Все проекты",
  'ncr.filterEngineer': "Фильтр по инженеру",
  'ncr.allEngineers': "Все инженеры",
  'ncr.filterReporter': "Фильтр по заявителю",
  'ncr.allReporters': "Все заявители",
  'ncr.filterCategory': "Фильтр по типу",
  'ncr.allCategories': "Все типы",
  'ncr.filterStatus': "Фильтр по статусу",
  'ncr.statusAll': "Все",
  'ncr.statusOpen': "Открытые",
  'ncr.statusResolved': "Обработанные",
  'ncr.groupBy': "Группировка:",
  'ncr.groupByAria': "По какому полю группировать",
  'ncr.statTotal': "Всего",
  'ncr.statOpen': "Открытых",
  'ncr.statResolved': "Обработано",
  'ncr.statAvgClose': "Среднее время закрытия",
  'ncr.noneMatch': "Подходящих несоответствий нет",
  'ncr.colType': "Тип",
  'ncr.colProjectPart': "Проект · деталь",
  'ncr.colEngineer': "Инженер / руководитель",
  'ncr.colOpenedBy': "Кто открыл",
  'ncr.colDone': "Что сделано",
  'ncr.colTime': "Время",
  'ncr.colStatus': "Статус",
  'ncr.markup': "Пометка",
  'ncr.resolved': "Обработано",
  'ncr.open': "Открыто",
  // ── Очередь резки (пила) — пятый из восьми экранов ──
  'cq.metres': "{n} м",
  'cq.mm': "{n} мм",
  'cq.missingRemnants': "Остатки, не найденные на полке",
  'cq.foundLater': " · {n} найдено позже",
  'cq.error': "Ошибка",
  'cq.statBars': "Целые прутки",
  'cq.statClosedUncut': "Закрыты без резки",
  'cq.statClosedUncutHint': "Открыты по пропуску, но ничего не отрезано — не засчитываются как работа",
  'cq.closedUncutCount': "Закрыто без резки: {n}",
  'cq.statRemnants': "Остатки в работе",
  'cq.statRemnantsHint': "Оплаченный материал, возвращённый в работу",
  'cq.statPieces': "Заготовок в очереди",
  'cq.statKeep': "Возврат на полку",
  'cq.statWaste': "Отходы",
  'cq.unplannable': "{n} заготовок не вошло в план.",
  'cq.unplannable_one': "{n} заготовка не вошла в план.",
  'cq.unplannable_few': "{n} заготовки не вошли в план.",
  'cq.unplannable_many': "{n} заготовок не вошло в план.",
  'cq.unplannableWhy': " Чаще всего это угол вне диапазона пилы (более 45°)",
  'cq.nothingToCut': "Сейчас резать нечего",
  'cq.barsCount': "{n} прутков",
  'cq.barsCount_one': "{n} пруток",
  'cq.barsCount_few': "{n} прутка",
  'cq.barsCount_many': "{n} прутков",
  'cq.piecesCount': "{n} заготовок",
  'cq.piecesCount_one': "{n} заготовка",
  'cq.piecesCount_few': "{n} заготовки",
  'cq.piecesCount_many': "{n} заготовок",
  'cq.wastePct': "Отходы {n}%",
  'cq.completeIn': "Заполните в ",
  'cq.profilesLink': "Настройки ← профили для резки",
  'cq.profileUnplannable': "заготовок этого профиля невозможно запланировать (угол свыше 45° или заготовка длиннее прутка).",
  'cq.onSaw': "На пиле",
  'cq.barDone': "Завершён",
  'cq.barSeq': "Пруток {n}",
  'cq.wholeBar': "Целый пруток · {size}",
  'cq.hidePieces': "Скрыть заготовки",
  'cq.pieceCut': "Отрезано",
  'cq.pieceSkipped': "Пропущено",
  'cq.pieceWaiting': "Ожидает",
  // ── Комплектация на складе — четвёртый из восьми экранов ──
  //
  // Три формы множественного числа, а не один ключ с {n}: в русском форма
  // зависит от последней цифры (1 день / 2 дня / 5 дней).
  'wp.lessThanHour': "меньше часа",
  'wp.hoursShort': "{n} ч",
  'wp.oneDay': "1 день",
  'wp.twoDays': "2 дня",
  'wp.manyDays': "{n} дней",
  'wp.manyDays_one': "{n} день",
  'wp.manyDays_few': "{n} дня",
  'wp.manyDays_many': "{n} дней",
  'wp.laneCheck': "Проверить склад",
  'wp.lanePurchase': "Ожидает закупки",
  'wp.lanePick': "Комплектовать",
  'wp.laneArrival': "Заказано — в пути",
  'wp.promised': "Обещано {d}",
  'wp.noPromise': "Заказано без даты поставки",
  'wp.neededBy': "Нужно к {d}",
  'kit.actionFailed': "Действие не выполнено",
  'kit.issuedSome': "Выдано по проекту: {n}",
  'kit.noneYet': "По этому проекту комплектов ещё не выдавали",
  'kit.open': "Открыть комплект",
  'kit.title': "Комплект {n}",
  'kit.openedAt': "Открыт {d}",
  'kit.inCart': "В тележке: {n}",
  'kit.noLocation': 'Нет места',
  'kit.empty': "Тележка пуста. Добавьте то, что готово к комплектации.",
  'kit.outOf': "из {n}",
  'kit.removeAria': "Убрать {name} из тележки",
  'kit.issue': "Выдать: {n}",
  'kit.cancel': "Отменить комплект",
  'kit.emptyIsNotIssue': "Пустая тележка — это не выдача",
  'kit.justIssued': "Выдано: комплект {seq} · позиций: {n}",
  // Три формы: 1 этикетка · 2–4 этикетки · 5+ этикеток.
  'kit.labelPerItem': 'Этикеток на позиции: {n}',
  'kit.labelPerItem_one': 'Этикетка на позицию',
  'kit.labelPerItem_few': 'Этикетки на позиции ({n})',
  'kit.labelPerItem_many': 'Этикеток на позиции: {n}',
  'kit.closedLine': "Комплект {seq} · позиций: {n}",
  'kit.issuedList': "Выданные комплекты: {n}",
  'kit.clockChain': "Цепочка",
  'kit.clockWarehouse': "Склад",
  'kit.clockWaiting': "Ожидание поставщика",
  'kit.clockPending': "Пришло не всё",
  'kit.clockRunning': "Идёт сейчас",
  'kit.oneHour': "1 час",
  'kit.manyHours': "{n} ч",
  'kit.oneDay': "1 дн.",
  'kit.twoDays': "2 дн.",
  'kit.manyDays': "{n} дн.",
  'ui.loading': "Загрузка...",
  'ui.errorLoadingLabel': "Ошибка загрузки: {label}",
  'ui.errorLoading': "Ошибка загрузки данных",
  'ui.errorHintNetwork': "Проверьте сеть и повторите",
  'ui.errorHintForbidden': "Обратитесь к администратору, если вы должны видеть этот экран.",
  'ui.retry': "Повторить",
  'wp.laneAll': "Все",
  'wp.stockRecorded': "Записан склад: {n}",
  'wp.markedPurchase': "Отмечено: требуется закупка",
  'wp.notSaved': "Не сохранено",
  'wp.searchPlaceholder': "Артикул, название, проект, заказчик или заявитель",
  'wp.searchAria': "Поиск по заявкам",
  'wp.noneMatch': "Нет заявок, подходящих под фильтр.",
  'wp.noneOpen': "Открытых заявок на складе нет.",
  'wp.itemsCountOne': "1 позиция",
  'wp.itemsCount': "{n} позиций",
  'wp.itemsCount_one': "{n} позиция",
  'wp.itemsCount_few': "{n} позиции",
  'wp.itemsCount_many': "{n} позиций",
  'wp.requestedBy': "Заявитель: {name}",
  'wp.subAssembly': "Подсборка: {name}",
  'wp.belowMin': " — ниже минимума",
  'wp.noMinimum': "Минимум не задан",
  'wp.requestedToStock': "Заявка на склад:",
  'wp.awaitingPurchaseSuffix': " — ожидает закупки",
  'wp.inStock': "Есть на складе",
  'wp.plusToStock': "+ на склад",
  'wp.coversClose': "Есть на складе — закрыть",
  'wp.needsPurchase': "Требуется закупка",
  // ── Операции в реальном времени — экран начальника производства ──
  //
  // Третий из восьми экранов, утверждённых к переводу (17.09.2026).
  'ops.stuckBadge': "{n} застряло",
  'ops.stuckBadge_one': "{n} застрял",
  'ops.stuckBadge_few': "{n} застряли",
  'ops.stuckBadge_many': "{n} застряло",
  'ops.statusAll': "Все статусы",
  'ops.statusStuck': "Застряли",
  'ops.statusPending': "Частично выполнены",
  'ops.statusPaused': "Приостановлены",
  'ops.statusReady': "Готовы к запуску",
  'ops.justNow': "сейчас",
  'ops.agoMinutes': "{n} мин назад",
  'ops.agoHours': "{n} ч назад",
  'ops.agoDays': "{n} дн назад",
  'ops.updatedAt': "Обновлено {when}",
  'ops.soundOn': "Звуковые оповещения включены — нажмите, чтобы выключить",
  'ops.soundOff': "Звуковые оповещения выключены — нажмите, чтобы включить",
  'ops.soundAria': "Звуковые оповещения",
  'ops.sound': "Звук",
  'ops.muted': "Без звука",
  'ops.fullscreenTitle': "Полный экран — для телевизора в цехе",
  'ops.fullscreenAria': "Полный экран",
  'ops.kpiActive': "В работе сейчас",
  'ops.kpiStandby': "В ожидании",
  'ops.kpiDone': "Завершены",
  'ops.filterStatus': "Фильтр по статусу",
  'ops.allDepts': "Все отделы",
  'ops.filterDept': "Фильтр по отделу",
  'ops.allProjects': "Все проекты",
  'ops.filterProject': "Фильтр по проекту",
  'ops.searchPlaceholder': "Поиск: артикул, деталь, проект или работник…",
  'ops.searchAria': "Поиск в операциях",
  'ops.errorLabel': "Операции",
  'ops.deptLabel': "Отдел:",
  'ops.deptAll': "Все",
  // ── Настенное табло „кто над чем работает" / „несоответствия" ──
  //
  // Читается издалека и всю смену. Второй из восьми экранов, утверждённых
  // к переводу (17.09.2026). Технический регистр: несоответствие, ред.
  'tv.matShortage': "Нехватка материала",
  'tv.prodNc': "Производственное несоответствие",
  'tv.prevStationNc': "Несоответствие на предыдущем участке",
  'tv.whoWorks': "Кто над чем работает",
  'tv.ncGaps': "Несоответствия / пробелы",
  'tv.showWhoWorks': "Показать, кто над чем работает",
  'tv.showNcGaps': "Показать несоответствия / пробелы",
  // תו סיני („活") נכנס לכאן בטעות בכתיבה הראשונה. שומר ה-i18n לא היה
  // תופס אותו — אין בו עברית ומצייני המקום תואמים — והעובד היה רואה
  // ג׳יבריש. בדיקה אוטומטית אינה תחליף לקריאת מה שנכתב.
  'tv.noActiveWork': "Сейчас активных работ нет",
  'tv.presentNotWorking': "На смене, но сейчас не в работе:",
  'tv.projectLine': "Проект {project}",
  'tv.revisionSuffix': " · ред. {rev}",
  // Две подписи, а не одно предложение: первый счётчик — время работника
  // над деталью, второй — время детали на участке и отчётные минуты.
  'tv.workerTimer': "Работник работает",
  'tv.partTimer': "На детали · работа",
  'tv.noOpenNc': "Открытых несоответствий нет ✓",
  'tv.ncOpenedChart': "Открытые несоответствия (8 недель)",
  'tv.ncCloseHoursChart': "Среднее время закрытия — часы (8 недель)",
  // ── Табло „что поступает ко мне" (F3) ──
  //
  // Первый из восьми экранов, которые Эли утвердил к переводу (17.09.2026).
  // Технический регистр: деталь = деталь, ред. = редакция чертежа.
  'inc.title': "Что поступает в {dept}",
  'inc.deptFallback': "отдел",
  'inc.subtitle': "Детали в пути в этот отдел, сгруппированы по отделу-источнику · {n} деталей",
  'inc.subtitle_one': "Детали в пути в этот отдел, сгруппированы по отделу-источнику · {n} деталь",
  'inc.subtitle_few': "Детали в пути в этот отдел, сгруппированы по отделу-источнику · {n} детали",
  'inc.subtitle_many': "Детали в пути в этот отдел, сгруппированы по отделу-источнику · {n} деталей",
  'inc.pickDept': "Выберите отдел",
  'inc.pickDeptOption': "Выберите отдел…",
  'inc.refresh': "Обновить",
  'inc.pickToSee': "Выберите отдел, чтобы увидеть, что к нему в пути.",
  'inc.loading': "Загрузка…",
  'inc.none': "Сейчас в этот отдел ничего не в пути. ✓",
  'inc.fromDept': "Из отдела {dept}",
  'inc.partsCount': "{n} деталей",
  'inc.partsCount_one': "{n} деталь",
  'inc.partsCount_few': "{n} детали",
  'inc.partsCount_many': "{n} деталей",
  'inc.noThumb': "Нет изображения",
  'inc.revision': "ред. {rev}",
  'inc.nowAt': "Сейчас:",
  'fin.subtitle': "Киоск финишной обработки",
  'fin.whichStation': "Какой это участок?",
  'fin.whichStationHint': "Выбирается один раз и запоминается на этом компьютере",
  'fin.identify': "Кто работает?",
  'fin.identifyHint': "Отсканируйте бейдж или выберите себя из списка",
  'fin.identifyFirst': "Сначала отсканируйте бейдж, потом деталь",
  'fin.scanPart': "Отсканируйте штрихкод на детали — время пойдёт сразу",
  'fin.notYou': "Это не я",
  'fin.cardPart': "Деталь",
  'fin.cardOpenDrawing': "Открыть чертёж",
  'fin.cardNoDrawing': "Нет активного чертежа",
  'fin.cardNc': "Несоответствие",
  'fin.cardOf': "{done} из {total}",
  'fin.boardEmptyNow': "Сейчас здесь никто не работает",
  'fin.finishedByBadge': "Отсканируйте свою карту, чтобы завершить и указать количество",
  'fin.ncWhat': "Что обнаружено?",
  'fin.ncNote': "Подробности (необязательно)",
  'fin.ncNotePlaceholder': "Что именно и где на детали",
  'fin.ncSend': "Открыть несоответствие",
  'fin.ncScrapHint': "Решение по материалу — доработка, использование как есть или брак — принимается в процессе несоответствия уполномоченным лицом.",
  'fin.ncOpened': "Открыто несоответствие по {part}",
  'fin.cardStateWorking': "В работе",
  'fin.cardStateNc': "Открытое несоответствие",
  'fin.scanHere': "Сканируйте здесь",
  'fin.clockRunning': "Время идёт. Отсканируйте ту же деталь ещё раз, чтобы завершить",
  'fin.viewDrawing': "Чертёж",
  'fin.closeDrawing': "Закрыть",
  'fin.finishFirst': "У вас открыт таймер по {part} — отсканируйте её, чтобы завершить",
  'fin.switchWorker': "Другой работник",
  'fin.working': "Секунду…",
  'fin.loading': "Загрузка…",
  'fin.boardTitle': "Кто сейчас работает: {dept}",
  'fin.nobody': "Сейчас здесь никто не работает",
  'fin.you': "это вы",
  'fin.forMinutes': "{n} мин",
  'fin.forMinutes_one': "{n} минута",
  'fin.forMinutes_few': "{n} минуты",
  'fin.forMinutes_many': "{n} минут",
  'fin.alsoHere': "Над этой деталью также работают: {names}",
  'fin.howMany': "Сколько деталей вы сделали?",
  'fin.alreadyDone': "здесь выполнено {done} из {total}",
  'fin.outOf': "осталось {n}",
  'fin.unitsInvalid': "Введите количество деталей",
  'fin.save': "Сохранить",
  'fin.v2DrawingOf': 'Чертёж {part}',
  'fin.v2ApprovalTitle': 'Требуется одобрение руководителя',
  'fin.v2ManagerCode': 'Код руководителя',
  'fin.v2BadCode': 'Неверный код — попробуйте ещё раз',
  'fin.cancel': "Отмена",
  'fin.stageOpen': "Сохранено · {part}: {done} из {total}. Деталь остаётся на финишной обработке, остальные идут дальше",
  'fin.stageClosed': "Сохранено · {part}: все {done} выполнены. Этап закрыт",
  // «Можно сообщить 0, если не закончил» — Эли, 21.09.2026
  'fin.zeroReported': "Сохранено · {part}: время записано, детали ещё не готовы. Деталь остаётся здесь",
  // «Куда идёт деталь» — Эли, 20.09.2026
  'fin.handoffTitle': "Куда передать деталь",
  'fin.handoffStage': "Следующий этап маршрута: {stage}",
  'fin.handoffParent': "Присоединяется к сборке {part} · {name}",
  'fin.handoffNone': "В маршруте нет продолжения — спросите мастера, прежде чем класть",
  'fin.handoffNoDept': "Этапу не назначен участок — спросите мастера",
  'fin.handoffGot': "Понятно",
  // Напарники на одних часах — Эли, 20.09.2026, «оба режима»
  'fin.mates': "Напарники",
  'fin.matesNone': "Нет — работа засчитывается только вам",
  'fin.mateAdd': "Добавить напарника",
  'fin.mateScanHint': "Напарник прикладывает свой бейдж или выбирается из списка",
  'fin.mateScanHere': "Бейдж напарника",
  'fin.mateRemove': "Убрать {name} с часов",
  'fin.mateCancel': "Отмена",
  'fin.v2RevChanged': 'Ревизия изменена — вы начали с {from}, актуальная {to}',
  'fin.v2FinishWork': 'Завершить и указать количество',
  'fin.v2DrawingArrived': 'Для детали загружен чертёж — ревизия {to}',
  'fin.v2PickPart': 'Выбрать деталь из списка',
  'fin.v2WhichPart': 'Над какой деталью вы работаете?',
  'fin.v2NoQueue': 'Сейчас нет деталей, ожидающих на этом посту',
  'fin.v2ManyOnPart': 'Над этой деталью работают несколько человек — нажмите «Завершить и указать количество» на своей карточке',
  'fin.v2NoteAcked': 'Подтверждено',
  'fin.v2NoteNew': 'К подтверждению',
  'fin.v2NoteTitle': 'Замечание по качеству',
  'fin.v2NoteAck': 'Прочитал и подтверждаю',
  'fin.v2NcTitle': 'Блокирующее несоответствие — не работать до закрытия',
  'fin.v2NcOpenTitle': 'Открытое несоответствие — можно продолжать работу',
  'fin.v2NcSince': 'Открыто {when}',
  'fin.v2Remaining': 'Осталось',
  'fin.v2OutOf': 'из {n}',
  'fin.v2Elapsed': 'Время',
  'fin.v2Switch': 'Сменить пост',
  'fin.v2InWork': '{n} в работе',
  'fin.v2ScanBadge': 'Отсканируйте свой бейдж, чтобы начать',
  'fin.v2NowScanPart': 'Теперь отсканируйте штрихкод детали',
  'fin.v2Started': '{name} — работа начата',
  'fin.v2Closed': 'Закрыто и записано',
  'fin.v2BadgeFailed': 'Бейдж не распознан — попробуйте снова или выберите себя из списка',
  'fin.v2PickFailed': 'Не удалось распознать — попробуйте снова',
  'fin.v2Network': 'Ошибка сети',
  'fin.v2ClosingOn': 'Закрываем работу над',
  'fin.v2ZeroOk': '0 — тоже допустимый ответ',
  'fin.v2ThenOpens': 'После этого откроется работа над {part}',
  'fin.v2UnitsLabel': 'Выполненное количество',
  'fin.v2UnitsBad': 'Введите целое число. 0 допустим.',
  'fin.v2WhoAreYou': 'Кто вы?',
  'fin.v2Close': 'Закрыть',
  'ps.title': "Нельзя начать новую деталь, не закрыв предыдущую",
  'ps.youHaveOpen': "У вас открыта сессия по детали",
  'ps.openedAgo': "открыта",
  'ps.enterUnits': "Введите количество единиц, выполненных в предыдущей сессии — мы закроем её перед началом",
  'ps.partWord': "детали",
  'ps.howMany': "Сколько единиц выполнено по {part}?",
  'ps.closingOpening': "Закрываем и открываем…",
  'ps.cancelPriorStart': "Отменить предыдущую и начать новую деталь",
  'ps.saveStart': "Сохранить и начать новую деталь",
  'ps.cancel': "Отмена",
  'cam.scanWithCamera': "Сканировать камерой",
  'cam.insecure': "Браузер блокирует камеру при незащищённом соединении. Откройте систему по адресу https://mes.edal.co.il — либо, при локальном подключении на iPhone, установите сертификат безопасности.",
  'cam.policyBlocked': "Камера заблокирована настройкой сервера, а не вашим устройством. На телефоне менять нечего — нужно обновить разрешение камеры в nginx.",
  'cam.blocked': "Доступ к камере заблокирован. Разрешите доступ к камере — на iPhone также убедитесь, что сертификат безопасности установлен.",
  'cam.notFound': "Доступная камера на устройстве не найдена.",
  'cam.openError': "Ошибка при открытии камеры.",
  'cam.close': "Закрыть",
  'cam.aim': "Наведите камеру на штрихкод",
  'cam.auto': "Распознавание автоматическое",
  'cam.cancel': "Отмена",
  'ma.title': "Подтверждение руководителя на пропуск этапа",
  'ma.line': "Этап {n} ({stage}) — требуется подтверждение руководителя",
  'ma.code': "Личный код подтверждения",
  'ma.cancel': "Отмена",
  'ma.ok': "Подтвердить",
  'rs.started': "Работа начата",
  'rs.startedAt': "Начало {time}",
  'rs.startedApproved': "Утверждено вне очереди",
  'rs.stage': "Этап {stage}",
  'rs.saved': "Успешно сохранено!",
  'rs.workTime': "Время работы",
  'rs.units': "{n} ед.",
  'rs.part': "Деталь {part}",
  'rs.partners': "Напарники: {names}",
  'rs.returning': "Возврат на главный экран через {n} с…",
  'bo.scanDrawing': "Отсканируйте чертёж для начала/завершения или ещё один бейдж, чтобы добавить напарника",
  'bo.notMe': "Это не я, назад",
  'bo.ok': "Подтвердить",
  'kq.deptQueue': "Очередь отдела {dept} — что следующее по срочности",
  'kq.queue': "Что следующее по срочности",
  'kq.due': "Поставка",
  'nk.reportAnother': "Ещё один отчёт",
  'ls.errBadge': "Ошибка при считывании бейджа",
  'ls.errFile': "Ошибка при чтении файла",
  'ls.errSave': "Ошибка при сохранении отчёта",
  'ls.subtitle': "Система отчётности лазера",
  'ls.employeeNo': "Таб. № {n}",
  'ls.scanToStart': "Отсканируйте бейдж, чтобы начать",
  'ls.identify': "Идентифицируйтесь перед загрузкой отчёта о резке",
  'ls.badgePlaceholder': "Табельный номер / сканирование бейджа",
  'ls.continue': "Далее",
  'ls.uploadTitle': "Загрузите отчёт о резке (Excel)",
  'ls.uploadHint': "Выберите файл раскроя, полученный с лазерного станка",
  'ls.reading': "Читаем файл…",
  'ls.pickFile': "Выбрать файл Excel",
  'ls.cancelBack': "Отмена и возврат",
  'ls.saved': "Отчёт сохранён",
  'ls.reported': "Отчитано",
  'ls.willReport': "Будет отчитано",
  'ls.partsWord': "деталей",
  'ls.unitsWord': "единиц",
  'ls.minutesWord': "мин",
  'ls.skippedDetail': "{n} деталей пропущено (см. подробности выше)",
  'ls.skippedDetail_one': "{n} деталь пропущена (см. подробности выше)",
  'ls.skippedDetail_few': "{n} детали пропущены (см. подробности выше)",
  'ls.skippedDetail_many': "{n} деталей пропущено (см. подробности выше)",
  'ls.newReport': "Новый отчёт",
  'ls.cutReport': "Отчёт о резке",
  'ls.material': "Материал",
  'ls.thickness': "Толщина",
  'ls.mm': "мм",
  'ls.sheetWeight': "Вес листа",
  'ls.kg': "кг",
  'ls.efficiency': "КПД раскроя",
  'ls.setupNote': "Время SETUP: {n} мин — распределяется между деталями по времени резки",
  'ls.colPart': "Деталь",
  'ls.colQty': "Кол-во",
  'ls.colCutTime': "Время резки",
  'ls.colMinutes': "Отчётные минуты",
  'ls.colStatus': "Статус",
  'ls.extraCut': 'Доп. резка — сверх количества',
  'ls.willBeReported': "✓ Будет отчитано",
  'ls.skippedReason': "Пропущено — {reason}",
  'ls.skippedShort': "{n} пропущено",
  'ls.commitAnyway': "Можно подтвердить всё равно — лист, материал и время будут записаны, а на каждую недостающую деталь проекта, который есть в системе, откроется запрос чертежа. Проект, которого нет в системе, пропускается.",
  'ls.otherFile': "Другой файл",
  'ls.confirmReport': "Подтвердить и отчитаться",
  'warehouse.receive': "Приёмка",
  'warehouse.receiveButton': "Принять товар",
  'warehouse.wasReceived': "Принято",
  'warehouse.partial': "Получено {got} из {need}",
  'warehouse.openOrder': "Открытый заказ",
  'warehouse.orderPlaceholder': "Позиция / артикул / поставщик / проект",
  'warehouse.remaining': "Осталось {n}",
  'warehouse.late': "Просрочено",
  'warehouse.noOpenOrders': "Нет открытых заказов",
  'warehouse.supplierDeliveryNote': "Накладная поставщика",
  'warehouse.inspection': 'Входной контроль',
  'warehouse.inspectOk': 'Годно',
  'warehouse.inspectBad': 'Брак',
  'warehouse.rejectQty': 'Сколько забраковано',
  'warehouse.rejectReason': 'Причина брака',
  'warehouse.inspectNotePlaceholder': 'Уточнение (необязательно)',
  'warehouse.rejectExplains': 'Забракованное количество не попадёт на склад, а позиция будет заблокирована для выдачи до разбора.',
  'warehouse.rejectedN': 'забраковано {n}',
  'warehouse.serials': 'Серийные номера',
  'warehouse.serialsWhy': '{label} — серийный номер обязателен для каждой единицы. Без него единицу не найти по сервисному вызову.',
  'warehouse.serialUnit': 'Единица {n}',

  // ── Инвентаризация (киоск) ──
  // ── Карта раскроя у чертежа (02/11/2026) ──
  'stcut.title': 'Карта раскроя чертежа',
  'stcut.mm': 'мм',
  'stcut.square': 'прямой',
  'stcut.cutOf': 'Нарезано {n} из {total}',
  'stcut.cutAll': 'Все заготовки нарезаны',
  'stcut.lineCut': 'Нарезаны все',
  'stcut.notCut': 'Ещё не нарезаны на пиле',
  'stcut.multiplied': 'Количество уже умножено на {n} — количество чертежа в проекте',
  'stqa.title': "Требования качества к этой детали",
  'stqa.openCount': "{n} ожидают",
  'stqa.openCount_one': "{n} ожидает",
  'stqa.openCount_few': "{n} ожидают",
  'stqa.openCount_many': "{n} ожидают",
  'stqa.engineerOnly': "Эту проверку должен подтвердить инженер — позовите инженера.",
  'stqa.faiOnly': "FAI — первую единицу проверяет и подписывает начальник производства или главный инженер. Позовите одного из них.",
  'stqa.whatIsWrong': "Какая проблема обнаружена? (обязательно)",
  'stqa.confirm': "Проверил — исправно",
  'stqa.problem': "Есть проблема",
  'stqa.reportProblem': "Сообщить о проблеме",
  // ── Работа на объекте (монтаж) ──
  'field.title': 'Работа на объекте',
  'field.loginHint': 'Введите код работы, полученный от руководителя сервиса, и свой табельный номер.',
  'field.codeFromLink': 'Код работы получен из ссылки. Осталось отсканировать пропуск или ввести табельный номер.',
  'field.changeCode': 'Другой код',
  'field.myInstallations': 'Мои монтажи',
  'field.codeOnce': 'Новый телефон — один раз введите 6-значный код работы (из письма / WhatsApp). В следующий раз достаточно отсканировать пропуск.',
  'field.scanCode': 'Сканировать код работы',
  'field.scanCodeOrBadge': 'Сканировать код работы или пропуск',
  'field.jobCode': 'Код работы',
  'field.badge': 'Табельный номер',
  'field.enter': 'Вход',
  'field.loginFailed': 'Не удалось войти',
  'field.loadFailed': 'Не удалось загрузить данные работы',
  'field.saveFailed': 'Не сохранилось — повторите',
  'field.photoFailed': 'Фото не загрузилось',
  // ── Подпись представителя заказчика (21/09/2026) ─────────────────────
  'field.customerSignTitle': 'Кто со стороны заказчика подтверждает окончание работ?',
  'field.customerSignAction': 'Получить подпись представителя',
  'field.customerAbsent': 'Не подписано: не было или отказался',
  'field.customerAbsentWhy': 'Почему нет подписи? (записывается на ваше имя)',
  'field.customerAbsentExample': 'Начальник объекта ушёл до окончания работ или отказался подписать',
  'field.customerSignedBy': 'Представитель заказчика подписал',
  'field.customerSignedByWho': 'подпись получил',
  'field.customerNoSignature': 'Без подписи заказчика',
  'field.customerNoSignatureBy': 'сообщил',
  'field.signatureSave': 'Сохранить подпись',
  'field.signatureHint': 'Подпись сохраняется на работе. Закрытие — кнопка ниже.',
  'field.signatureFailed': 'Не удалось сохранить подпись — попробуйте ещё раз',
  'field.queueSent': 'Всё, что ожидало, отправлено',
  'field.photoKept': 'Фото не загрузилось и не сохранилось на устройстве — оно осталось в кнопке, повторите, когда появится связь',
  'field.closePending': 'Часть отчёта ещё не дошла до сервера — закройте день снова, когда появится связь',
  'field.removePhoto': 'Удалить фото',
  'field.removePhotoConfirm': 'Удалить фото?',
  'field.photoRemoveFailed': 'Не удалось удалить фото',

  // ── מה שהשרת שולח למסך בשטח ───────────────────────────────────────────────
  //
  // זה החצי שבאמת נקרא. „מה עוד חסר כדי לסגור" היא הרשימה היחידה במסך השטח
  // שמנחה פעולה — טכנאי שאינו קורא אותה ממציא מה נדרש, וזה יום שטח שני.

  // חסמים
  'fb.DAY_NOTHING': 'Ничего не заполнено',
  'fb.DAY_TIMES': '{parts}',
  'fb.DAY_TIME_LEFT_PLANT': 'время выезда',
  'fb.DAY_TIME_ARRIVED': 'время прибытия',
  'fb.DAY_TIME_LEFT_SITE': 'время убытия',
  'fb.DAY_TIME_RETURNED': 'время возвращения',
  'fb.DAY_ODOMETER': 'километраж',
  'fb.CLOSE_SIGNATURE': 'Подпись представителя заказчика — или почему нет подписи: не было или отказался',
  'fb.CLOSE_NO_TASKS': 'Для работы не заданы задачи',
  'fb.CLOSE_OPEN_TASKS': 'Задач не выполнено: {n}',
  'fb.CLOSE_NO_CHECKS': 'Не заданы финальные проверки',
  'field.noChecksHint': 'их задаёт офис (руководитель монтажа). Позвоните ему; здесь ничего делать не нужно.',
  'fb.CLOSE_VISIT_OPEN': 'Чек-лист обслуживания: {n} строк без ответа',
  'fb.CLOSE_VISIT_UNSIGNED': 'Чек-лист обслуживания ещё не подписан',
  'fb.CLOSE_PENDING_CHECKS': 'Финальных проверок не выполнено: {n}',
  'fb.CLOSE_FAILED_CHECKS': 'Проверок не пройдено и не устранено: {n}',
  'fb.CLOSE_EQUIPMENT': 'Единиц оборудования не отмечено как взятые: {n}',
  'fb.CLOSE_NO_CREW': 'Бригада не назначена',
  'fb.CLOSE_NO_DAYS': '{who} — нет отчёта ни за один день: день, закрытый в телефоне одного из бригады, закрывается для всех; кто не выезжал — руководитель сервиса убирает его из бригады',
  'fb.CLOSE_CREW_NOT_WITH': '{who} — не был на работе ни в один отмеченный день (снят «не был со мной» или работал на другом проекте). Не мешает закрытию; если не выезжал — уберите его из бригады',
  'fb.CLOSE_DAY_GAPS': '{who} · {date} — {parts}',
  'fb.CLOSE_DAY_OPEN': '{who} · {date} — день не закрыт: {who} нажимает «Закрыть день» в своём телефоне, или день закрывает руководитель сервиса',

  // סירובים
  'fe.badFieldCode': 'Неверный код, истёк срок действия, или вы не назначены на эту работу',
  'fe.fieldJobClosed': 'Эта работа уже закрыта — отчитываться не о чем. Вопросы — к менеджеру сервиса.',
  'fe.noFinalChecks': 'Для монтажа ещё не заданы финальные проверки — начать нельзя. Позвоните менеджеру монтажа.',
  'fe.loginIncomplete': 'Введите код работы и табельный номер',
  'fe.notOnCrewStaff': 'Вы не входите в бригаду этой работы',
  'fe.jobNotFound': 'Работа не найдена',
  'fe.mustSignIn': 'Необходимо войти',
  'fe.noCode': 'Отсканируйте пропуск или введите код работы',
  'fe.sessionEnded': 'Сеанс завершён. Войдите заново.',
  'fe.jobDone': 'Работа закрыта.',
  'fe.notOnCrew': 'Вы не назначены на эту работу.',
  'fe.codeExpired': 'Код больше не действует. Запросите новый код у менеджера сервиса.',
  'fe.dayClosed': 'День {date} закрыл(а) {by} — только для просмотра. Исправить может только руководитель монтажа: обратитесь к нему.',
  'fe.dayFuture': 'День {date} ещё не наступил — отчитываются только за наступивший день. Выберите сегодня или прошлый незакрытый день.',
  'fe.jobLocked': 'Работа закрыта — все её дни только для просмотра. Нужно исправить? Обратитесь к руководителю монтажа: он откроет её заново в карточке установки.',
  'fe.departureOtherDay': 'Время выезда не в день {date} — выберите время из списка в этом дне или перейдите к нужному дню в выборе дней.',
  'fe.hoursOverlap': 'Часы пересекаются с отчётом по {projectNumber} ({range}). В одно время — только одна работа: исправьте время.',
  'fe.previousStillOpen': '{projectNumber} у вас ещё открыт. Перейдите к отчёту {projectNumber}, отметьте там уход и возвращение, затем начинайте здесь.',
  'fe.switchNeedsCode': 'Телефон ещё не знаком системе — выйдите и войдите с кодом работы второго проекта.',
  'fe.crewReportedItself': 'Работник уже сам отчитался за день — время окончания он отмечает сам.',
  'fe.crewNoDeparture': 'Сначала отметьте время выезда — потом можно указать, кто ушёл раньше.',
  'fe.crewNotWithYou': 'Работник сегодня не отмечен с вами — сначала отметьте его.',
  'fe.dayReportedByOther': 'Ваш день записал коллега. Хотите отчитаться сами? Введите время в своём дне.',
  'fe.dayRemoved': 'Этот день снят с установки. Работали сегодня? Попросите менеджера вернуть его.',
  'fe.dayInvalid': 'Недопустимые данные',
  'fe.dayIncomplete': 'Нельзя закрыть день — {parts}',
  'fe.odometerBackwards': 'Километраж при возвращении меньше, чем при выезде',
  'fe.returnBeforeDeparture': 'Время возвращения на предприятие раньше времени выезда',
  'fe.notOnDayCrew': 'Работника нет в бригаде этого дня. Менеджер монтажа добавляет его в «Редактировать день».',
  'fe.notInCrewToday': 'Вас нет в бригаде этого проекта сегодня — обновите экран или попросите менеджера монтажа назначить вас',
  'fe.badFieldRequest': 'Неверный запрос — обновите экран',
  'fe.badEndTime': 'Неверное время — нажмите «Закончить сейчас» или выберите время',
  'fe.endBeforeDeparture': 'Время окончания раньше времени выезда — выберите время после выезда',
  'fe.endBeforeArrival': 'Время окончания ({end}) раньше прибытия на объект ({arrived}). Кто не доехал с вами до объекта — снимите с него отметку («не был со мной»).',
  'fe.dayTooLong': 'Время возвращения слишком далеко от времени выезда — один выездной день не может превышать 24 часа. Исправьте время или дату.',
  'fe.naReasonRequired': 'Коротко напишите, почему это не актуально — например: «клиент уже заменил сам».',
  'fe.naAlreadyDone': 'Это уже выполнено — отмечать «не актуально» нечего.',
  'fe.naCheckFailed': 'Проверка, которая не прошла, не может быть «не актуальной» — исправьте и проверьте снова или откройте замечание.',
  'fe.futureTime': 'Это время ещё не наступило — отмечаем только то, что уже было. Выберите время не позже текущего или нажмите «Сейчас», когда это произойдёт.',
  'fe.taskNotFound': 'Задача не найдена',
  'fe.checkNotFound': 'Проверка не найдена',
  'fe.equipmentNotFound': 'Позиция не найдена',
  'fe.documentNotFound': 'Документ не найден',
  'fe.photoNotFound': 'Фото не найдено',
  'fe.photoRemoved': 'Фото удалено или не относится к этой работе.',
  'fe.checkNoteRequired': 'Непройденная проверка требует пояснения — что обнаружено',
  'fe.noFile': 'Файл не выбран',
  'fe.alreadySigned': 'Представитель заказчика уже подписал этот монтаж',
  'fe.signatureRequired': 'Нужна подпись с именем подписавшего, либо причина, по которой заказчик не подписал',
  'fe.signatureMissing': 'Подписи нет',
  'fe.jobIncomplete': 'Нельзя закрыть монтаж — {parts}',
  'fe.jobNotStarted': 'Работа начинается {date} — уже можно отметить, что взять, но выезд, километры, задачи и закрытие — только в день работы',
  'fe.regulationNotAcked': 'Перед отчётом необходимо принять регламент выездных работ',
  'fe.regulationNone': 'Регламент не опубликован',
  'fe.badRequest': 'Недопустимый запрос',
  'fe.badFileType': 'Файл не является изображением JPEG / PNG / WebP',
  'fe.serverError': 'Системная ошибка',
  'fe.rateLimited': 'Слишком много попыток — повторите через несколько минут',
  'fe.network': 'Не отправлено — нет связи с сервером. Проверьте связь и попробуйте снова.',
  'fe.staleClient': 'Приложение работает на старой версии. Закройте его, откройте заново и повторите.',
  'fe.fileTooLarge': 'Фото слишком большое для отправки. Сфотографируйте заново и попробуйте снова.',
  'fe.gapInvalid': 'Выберите тип недоделки и опишите её',
  'fe.gapTypeInvalid': 'Выберите тип недоделки из списка.',
  'fe.gapStageInvalid': 'Недоделку можно открыть на FAT, при отгрузке или на монтаже.',
  'fe.gapSideInvalid': 'Выберите ответственную сторону: производство, конструкторский отдел, закупки или руководство проекта.',
  'fe.gapDescriptionShort': 'Опишите недоделку — чего не хватает или что не так.',
  'fe.gapProjectNotFound': 'Проект не найден. Обновите карточку.',
  'fe.gapPartNotInProject': 'Выбранная деталь не относится к этому проекту. Выберите деталь из списка или оставьте поле пустым.',
  'fe.gapJobMismatch': 'Этот монтаж относится к другому проекту. Обновите карточку.',
  'fe.gapNoRecipient': 'Недоделку некому передать: в системе нет активного ответственного ({side}) и нет активного руководителя монтажа. Обратитесь к генеральному директору, чтобы он назначил пользователей.',
  'fe.gapCauseRequired': 'Выберите причину неисправности — производство, конструкция, монтаж, деталь поставщика, износ или неправильная эксплуатация',
  'fe.gapCauseInvalid': 'Выберите причину из списка: производство, конструкция, монтаж, деталь поставщика, износ или неправильная эксплуатация.',
  'fe.repairInvalid': 'Проверьте запрос — что обнаружено, и часы числом (или пусто)',
  'fe.repairFindingShort': 'Опишите, что обнаружили — какая неисправность и что нужно починить',
  'fe.repairNotServiceCall': 'Запрос на ремонт на месте отправляется только из сервисной работы по вызову',
  'fe.repairCallClosed': 'Вызов уже закрыт в офисе — позвоните руководителю сервиса',
  'fe.repairNotPaid': 'Вызов не платный — по гарантии или по договору ремонтируют без запроса разрешения',
  'fe.repairAlreadyDecided': 'Руководитель сервиса уже принял решение по вызову — оно показано в карточке работы',
  'fe.repairPendingExists': 'Запрос на ремонт по этому вызову уже отправлен — ждёт ответа руководителя сервиса',
  'fe.side.PRODUCTION': 'производство',
  'fe.side.ENGINEERING': 'инженерный отдел',
  'fe.side.PURCHASING': 'отдел закупок',
  'fe.side.PM': 'руководитель проекта',
  'fe.forbidden': 'Нет прав на это действие. Обратитесь к менеджеру сервиса.',
  'fe.sessionExpired': 'Сеанс истёк — войдите заново',
  'fe.visitItemInvalid': 'Данные строки некорректны — обновите страницу и попробуйте снова',
  'fe.visitHoursInvalid': 'Неверные моточасы — введите число со счётчика машины (от 0 до 1 000 000)',
  'fe.visitCancelled': 'Визит отменён — запишите моточасы в карточке системы',
  'fe.visitPhotoInvalid': 'Фото повреждено — сфотографируйте заново',
  'fe.visitItemNotFound': 'Строка не найдена — обновите страницу',
  'fe.visitClosed': 'Визит закрыт — строки подписанного отчёта изменять нельзя',
  'fe.visitAwaitingSignature': 'Отчёт отправлен клиенту на подпись — строки изменять нельзя. Для исправления обратитесь к руководителю сервиса',
  'fe.visitAlreadyClosed': 'Визит уже закрыт — обновите страницу, чтобы увидеть подписанный отчёт',
  'fe.visitNotFound': 'Визит не найден — возможно, он отменён. Вернитесь к списку своих визитов',
  'fe.visitPhotoNotFound': 'У этой строки нет фото',
  'fe.visitSignatureNotFound': 'У этого визита нет подписи',
  'fe.visitCallInvalid': 'Опишите, что обнаружено, и выберите срочность',
  'fe.visitCallExists': 'По этой строке заявка уже открыта',
  'fe.visitSignatureRequired': 'Не хватает имени подписавшего или самой подписи',
  'fe.visitPartNeedsPhoto': 'Замена детали требует фото — сфотографируйте заменённое перед сохранением',
  'fe.visitPartNeedsCause': 'Укажите причину замены — производство, конструкция, монтаж, деталь поставщика, износ или неправильная эксплуатация',
  'fe.visitPartBadCause': 'Причина замены не из списка — выберите один из вариантов',
  'fe.visitQtyWithoutPart': 'Указано количество без детали — выберите, какая деталь заменена',
  'fe.visitDocNotFound': 'Документ не найден — попросите офис проверить его в библиотеке модели',
  'fe.visitPartNotInModel': 'Выбранная деталь не относится к этой модели — обновите страницу и выберите из списка',
  'fe.visitNeedsPartInvalid': 'Выберите деталь из списка и количество не меньше 1',
  'fe.visitNoModel': 'У этой системы нет модели в библиотеке, поэтому нет плана обслуживания. Попросите менеджера сервиса привязать модель или откройте сервисный вызов.',
  'fe.visitItemsPending': 'Строк без ответа: {n}. Перед подписью отметьте в каждой строке «выполнено» или «не выполнено».',
  'fe.qrUnknown': 'Наклейка не распознана — отсканируйте её ещё раз или обратитесь к менеджеру сервиса',
  'fe.badgeNotThisPhone': 'Этот телефон не знает этот пропуск — войдите один раз с кодом работы и пропуском',
  'fe.badgeIncomplete': 'Отсканируйте пропуск или введите табельный номер',
  'fe.jobNotOpen': 'Работа закрыта или вы не входите в её бригаду — обновите список',
  'fe.jobHasNoVisit': 'У этой работы нет визита обслуживания',
  'fe.imageNotFound': 'Изображение не найдено',
  'fe.deviceForgotten': 'Телефон больше не распознан — войдите снова с кодом работы и пропуском.',
  'field.regNoTranslation': 'Регламент ещё не переведён на русский — показан на иврите. Попросите руководителя объяснить его перед подтверждением.',
  'field.whereTitle': 'Что и где',
  'field.call.title': 'Сервисная заявка',
  'field.call.reported': 'Что сообщил клиент',
  'field.call.system': 'Система',
  'field.call.serial': 'Серийный №',
  'field.call.photo': 'Фото от клиента',
  'field.call.sev.PRODUCTION_STOPPED': 'Производство остановлено',
  'field.call.sev.MACHINE_DOWN': 'Машина не работает',
  'field.call.sev.NO_URGENCY': 'Не срочно',
  'field.overnight': 'Ночуем в гостинице — на завод не возвращаемся',
  'field.arrivedHotel': 'Время прибытия в гостиницу',
  'field.leftHotel': 'Время выезда из гостиницы',
  'field.scanBadge': 'Отсканируйте QR на карточке сотрудника',
  'field.navigate': 'Навигация к объекту (Waze)',
  'field.regVersion': 'Версия {v}',
  'field.regDeclare': 'Я прочитал регламент, понял его и обязуюсь его соблюдать.',
  'field.regAccept': 'Подтверждаю и начинаю работу',
  'field.noPhotoSite': 'Объект без фотосъёмки',
  'field.noPhotoWithoutPermit': 'На этом объекте нельзя фотографировать без разрешения заказчика. Разрешения нет — обратитесь к руководителю проекта.',
  'field.photoPermitOn': 'Получено разрешение заказчика на съёмку: {note}. Снимайте только то, что оно разрешает.',
  'field.staffEntry': 'Вы вошли в систему и назначены в бригаду — выберите работу, без кода:',
  // ── Знакомый телефон: только пропуск (U-1009-08) ──
  'field.badgeHello': 'Здравствуйте, {name} — отсканируйте пропуск',
  'field.badgeHint': 'Сканер пропуска вводит номер сюда. Можно также ввести табельный номер и нажать Enter.',
  'field.useCode': 'Вход с кодом работы',
  'field.notMe': 'Это не я',
  'field.backToBadge': 'Назад к сканированию пропуска',
  // ── «Мои задачи» (Эли 16/10/2026, U-1016-02 этап Б) ──
  'field.hub.hello': 'Здравствуйте, {name}',
  'field.hub.title': 'Мои задачи',
  'field.hub.loadFailed': 'Не удалось загрузить задачи — проверьте связь и попробуйте снова.',
  'field.hub.retry': 'Попробовать снова',
  'field.hub.empty': 'Сейчас ничего не ждёт вас',
  'field.hub.emptyBody': 'Здесь появятся задачи, проверки качества и FAT, которые ждут вас.',
  'field.hub.floorTasks': 'Мои задачи',
  'field.hub.officeTasks': 'Задачи офиса',
  'field.hub.qa': 'Проверки качества, которые ждут меня',
  'field.hub.fatTitle': 'FAT',
  'field.hub.shipTitle': 'Отгрузка',
  'field.hub.fatEngSub': 'Производство отметило — ждёт подтверждения инженерии',
  'field.hub.fatMgrSub': 'Ждёт отметки начальника производства',
  'field.hub.fromPhoneNote': 'Каждое подтверждение отсюда записывается на ваше имя с пометкой «подтверждено с телефона».',
  'field.hub.due': 'до {d}',
  'field.hub.overdue': 'просрочено · {d}',
  'field.hub.done': 'Выполнено',
  'field.hub.pass': 'Исправно',
  'field.hub.fail': 'Неисправно',
  'field.hub.na': 'Не относится',
  'field.hub.confirm': 'Подтверждаю',
  'field.hub.reject': 'Не подтверждаю',
  'field.hub.cancel': 'Отмена',
  'field.hub.failWhat': 'Что обнаружено и что нужно исправить?',
  'field.hub.rejectWhat': 'Что не в порядке и что производство должно проверить снова?',
  'field.hub.value': 'Измеренное значение',
  'field.hub.prodPass': 'Исправно · {name}',
  'field.hub.prodNa': 'Не относится · {name}',
  'field.pickJob': 'Ваши открытые работы — выберите работу:',
  'field.noOpenJobs': 'У вас нет открытых работ',
  'field.noOpenJobsBody': 'Если вы приехали работать — обратитесь к менеджеру сервиса, чтобы вас назначили.',
  'field.kind.INSTALLATION': 'Монтаж',
  'field.kind.SERVICE': 'Сервис',
  'field.kind.MAINTENANCE': 'Обслуживание',
  'field.visit.title': 'Чек-лист обслуживания',
  'field.visit.open': 'Открыть чек-лист',
  'field.visit.view': 'Просмотреть чек-лист',
  'field.onSiteToday': 'На объекте сегодня',
  'field.dayCrew': 'Бригада на сегодня',
  'field.dayRemoved': 'День снят ({by}): {reason}. Работали сегодня? Попросите вернуть его.',
  'field.taskForDay': 'На {date}',
  'field.fixInCard': 'Открыть карточку установки',
  'field.dayLocked': 'День закрыт',
  'field.closeDay': 'Закрытие рабочего дня',
  'field.dayStillMissing': 'Чтобы закрыть день, не хватает:',
  'field.closeDayMissingTitle': 'Не хватает, чтобы закрыть день',
  'field.goToField': 'Перейти к полю',
  'field.closeDayLockedWhy': 'Кнопка откроется, когда список опустеет · осталось: {n}',
  'field.closeDayWarning': 'Закрытие дня окончательно — после него изменить нельзя, исправить может только руководитель.',
  'field.closeDayAction': 'Закрыть день',
  'field.closeDayConfirm': 'Закрыть день {date}? {hours} · {km}. День закроется и для остальных в бригаде, кто не отчитался сам. После закрытия исправить может только руководитель.',
  'field.closeDayYes': 'Да, закрыть день',
  'field.closeDayNo': 'Ещё нет',
  'field.naAsk': 'Не актуально…',
  'field.naReason': 'Почему? Например: клиент уже заменил сам',
  'field.naMark': 'Отметить: не актуально',
  'field.naShown': 'Не актуально · {by}: {reason}',
  'field.naUndo': 'Вернуть',
  'field.dayClosedAt': 'День закрыт в {time}',
  'field.dayChangeViaManager': 'Для изменений обратитесь к руководителю.',
  'field.dayOpen': 'не закрыт',
  'field.hoursShort': 'ч',
  'field.kmShort': 'км',
  'field.dates': 'Даты работы',
  'field.site': 'Объект',
  'field.address': 'Адрес',
  'field.contact': 'Контактное лицо',
  'field.permits': 'Требуемые допуски',
  // „שרטוטים וקבצים" — `Чертежи и файлы`, כי הרשימה מכילה גם תמונות ו-PDF ולא
  // רק שרטוטים. וההודעה הריקה מנוסחת כפעולה („попросите офис") ולא כתרגום
  // מילולי של „גלוי לצוות בשטח", שברוסית היה יוצא מסורבל ולא מובן.
  'field.drawings': 'Документы и чертежи',
  'field.noDrawings': 'Чертежи к этой работе не приложены. Попросите офис открыть их бригаде в деле проекта.',
  'field.equipment': 'Взять с собой',
  'field.eqStock': 'На складе {n}',
  'field.eqNoStock': 'Нет на складе',
  'field.eqNotInCatalog': 'Нет в каталоге',
  'field.eqInMachine': 'В машине {n}',
  'field.eqQty': '× {n}',
  // U-1010-06 — инструменты со склада у работника (полевая карточка и станция)
  'field.myTools': 'Инструменты со склада у вас',
  'tools.temp': 'временно',
  'tools.perm': 'постоянно',
  'tools.late': 'не возвращён вовремя',
  'tools.returnAtCounter': 'Возврат — на складе.',
  'field.departure': 'Выезд с завода',
  'field.leftPlant': 'Время выезда с завода',
  'field.crewWithTitle': 'Кто со мной сегодня',
  'field.crewWithHint': 'Отмечены все: то, что вы вводите, записывается и им, с теми же часами. Кого не было с вами — снимите отметку.',
  'field.crewSelf': 'Отчитался сам',
  'field.crewByOther': 'Записал {name}',
  'field.crewOverlap': 'В эти часы на проекте {n} — здесь не записан',
  'field.crewLeftEarly': 'Ушёл раньше · сейчас',
  'field.crewEndAt': 'Закончил в {time}',
  'field.crewEndUndo': 'Отменить',
  'field.crewEndClamped': '{name}: записано окончание в {time} — ваше окончание. «Сейчас» позже, а ранний уход не может быть позже вашего. Ушёл ещё раньше? Руководитель монтажа исправит в «исправить часы».',
  'field.crewNotWith': 'Не был со мной',
  'field.crewNeedsDeparture': 'После отметки выезда можно указать, кто ушёл раньше.',
  'field.reportedBy': '{name} записал ваш день — те же часы{hours}. Работали в другое время? Введите свои часы здесь — с этого момента они ваши.',
  'field.otherJobsToday': 'Сегодня у вас ещё проект',
  'field.otherOpenJobs': 'Ещё ваши открытые установки',
  'field.dayPicker': 'Дни работы',
  'field.dayToday': 'Сегодня',
  'field.dayNotToday': 'не сегодня',
  'field.dayStateOpen': 'открыт',
  'field.dayStateClosed': 'закрыт',
  'field.dayStateReadOnly': 'просмотр',
  'field.dayFuture': 'ещё не наступил',
  'field.dayClosedByShort': 'Закрыл(а) {by}',
  'field.dayClosedBy': 'День {date} закрыл(а) {by} — только для просмотра. Исправить может только руководитель монтажа.',
  'field.pastDayOpen': 'Вы отчитываетесь за {date} — прошлый день, который ещё не закрыт. Выберите время из списков — «Сейчас» только в сегодняшнем дне.',
  'field.jobClosedReadOnly': 'Работа закрыта — всё только для просмотра. Для исправления обратитесь к руководителю монтажа.',
  'field.switchJob': 'Перейти к отчёту {n}',
  'field.multiDayHint': 'Каждый проект — отдельный отчёт со своими часами. На втором проекте «выезд» — с предыдущего объекта.',
  'field.sharedDay': '{name} отчитался за день — ваши часы записаны по его отчёту ({hours} ч). Вводить не нужно. Работали в другое время или в другом месте? Введите свои часы ниже.',
  'field.odoOut': 'Пробег при выезде',
  'field.arrival': 'Прибытие на объект',
  'field.arrivedSite': 'Время прибытия на объект',
  'field.tasks': 'Задачи',
  'field.noTasks': 'Задачи для этой работы не заданы.',
  'field.untranslated': 'Без перевода — показано на языке оригинала',
  'field.translationStale': 'Задание изменено — перевод старый. Уточните у менеджера сервиса',
  'field.checks': 'Итоговые проверки',
  'field.noChecks': 'Проверки для этой работы не заданы.',
  'field.photos': 'Фотографии',
  'field.addPhoto': 'Снять или загрузить фото',
  'field.return': 'Возвращение',
  'field.leftSite': 'Время выезда с объекта',
  'field.returned': 'Время возвращения на завод',
  'field.odoBack': 'Пробег при возвращении',
  'field.km': 'Пройдено',
  'field.refuelIls': 'Заправка (₪)',
  'field.refuelLitres': 'Заправка (л)',
  'field.hoursGross': 'Часы брутто (выезд — возвращение)',
  'field.dayNote': 'Примечания за день',
  'field.close': 'Закрытие монтажа',
  // הליכה 12/10/2026 (D/E) — שטח + עמדה.
  'field.closeTitle.INSTALLATION': 'Закрытие монтажа',
  'field.closeTitle.VISIT': 'Закрытие визита',
  'field.closeTitle.CALL': 'Закрытие работы',
  'field.complete.INSTALLATION': 'Монтаж выполнен',
  'field.complete.VISIT': 'Визит выполнен',
  'field.complete.CALL': 'Работа выполнена',
  'field.closedKind.INSTALLATION': 'Монтаж закрыт. Спасибо.',
  'field.closedKind.VISIT': 'Визит закрыт. Спасибо.',
  'field.closedKind.CALL': 'Работа закрыта. Спасибо.',
  'field.kind.VISIT': 'Плановое обслуживание',
  'field.kind.CALL': 'Сервисная работа',
  'field.reqType.FAULT': 'Неисправность',
  'field.reqType.MAINTENANCE': 'Обслуживание',
  'field.reqType.DAY_WORK': 'Работа по дням / часам',
  'field.reqType.BUILD': 'Изготовление / ремонт',
  'field.officeNote': 'Заметка офиса',
  'field.notStarted': 'Начинается {date}',
  'field.notStartedBody': 'Уже сейчас можно отметить, что взять. Выезд, километры, задачи и закрытие — только в день работы.',
  'field.markAll': 'Отметить всё',
  'field.showList': 'Показать список',
  'field.hideList': 'Скрыть список',
  'field.closeEquipmentConfirm': 'Не отмечено позиций: {n}. Всё равно закрыть?',
  'field.eqGroup.PARTS': 'Запчасти (артикул)',
  'field.eqGroup.KIT': 'Инструменты и материалы',
  'field.eqGroup.MANUAL': 'Добавлено вручную',
  'field.visitSignInChecklist': 'Подпись клиента или «Нет представителя на объекте» с причиной — в конце чек-листа.',
  'field.stillMissing': 'Чтобы закрыть монтаж, ещё не хватает:',
  'fb.CLOSE_OPEN_GAPS': 'Открытых недоделок: {n} — закрыть или получить подпись клиента «Принято с недоделками»',
  'field.gaps.title': 'Недоделки',
  'field.gaps.hint': 'Не хватает детали, деталь повреждена, не соответствует чертежу, не хватает закупки или документа, или просьба клиента — откройте недоделку. Она сразу уйдёт ответственному, ответ появится здесь.',
  'field.gaps.open': 'Открыть недоделку',
  'field.gaps.type': 'Тип',
  'field.gaps.type.MISSING_PART': 'Не хватает детали',
  'field.gaps.type.DAMAGED_PART': 'Деталь повреждена',
  'field.gaps.type.DOESNT_FIT_DRAWING': 'Не соответствует чертежу',
  'field.gaps.type.MISSING_PURCHASE': 'Не хватает закупленного',
  'field.gaps.type.MISSING_DOC': 'Не хватает документа',
  'field.gaps.type.CUSTOMER_REQUEST': 'Просьба клиента',
  'field.gaps.description': 'Что случилось',
  'field.gaps.descriptionExample': 'Например: не хватает 4 анкерных болтов M12',
  'field.gaps.part': 'Деталь (необязательно)',
  'field.gaps.noPart': 'Без детали',
  'field.gaps.photo': 'Фото (необязательно)',
  'field.gaps.send': 'Отправить',
  'field.gaps.sentTo': 'Отправлено',
  'field.gaps.none': 'Открытых недоделок нет.',
  'field.gaps.answer': 'Ответ',
  'field.gaps.status.OPEN': 'Открыто',
  'field.gaps.status.IN_PROGRESS': 'В работе',
  'field.gaps.status.RESOLVED': 'Решено',
  'field.gaps.status.ACCEPTED_BY_CUSTOMER': 'Принято с недоделками',
  'field.gaps.failed': 'Недоделка не отправлена. Проверьте связь и попробуйте снова.',
  'field.gaps.acceptedWithGaps': 'Клиент принимает монтаж с открытыми недоделками',
  'field.gaps.acceptedWithGapsHint': 'Недоделки останутся открытыми для исправления и не помешают закрыть монтаж.',
  'field.gaps.cause': 'Причина',
  'field.gaps.causeOptional': 'Причина (необязательно)',
  'field.gaps.titleService': 'Находки',
  'field.repair.title': 'Запрос на ремонт на месте',
  'field.repair.hint': 'Платный вызов: прежде чем ремонтировать — опишите, что нашли и что предлагаете. Руководитель сервиса разрешит или попросит коммерческое предложение, ответ появится здесь.',
  'field.repair.open': 'Запросить ремонт на месте',
  'field.repair.finding': 'Что обнаружено',
  'field.repair.findingExample': 'Например: сгорел приводной мотор, нужна замена',
  'field.repair.hours': 'Оценка часов',
  'field.repair.parts': 'Нужные детали (по одной в строке)',
  'field.repair.photo': 'Фото (необязательно)',
  'field.repair.send': 'Отправить руководителю сервиса',
  'field.repair.failed': 'Запрос не отправлен. Проверьте связь и попробуйте снова.',
  'field.repair.status.PENDING': 'Ждёт ответа руководителя сервиса',
  'field.repair.status.APPROVED': 'Разрешено — ремонтировать на месте',
  'field.repair.status.REJECTED': 'Отклонено — нужно коммерческое предложение клиенту. Сейчас не ремонтировать.',
  'field.repair.decision.REPAIR_NOW': 'Решение: ремонт на месте',
  'field.repair.decision.QUOTE': 'Решение: перед ремонтом нужно коммерческое предложение',
  'field.repair.answerNote': 'Комментарий руководителя',
  'field.repair.hoursShort': 'ч',
  'field.markComplete': 'Монтаж выполнен',
  'field.closed': 'Монтаж закрыт. Спасибо.',
  'field.now': 'Сейчас',
  'field.pass': 'Годно',
  'field.fail': 'Не годно',
  'field.whatWasFound': 'Что обнаружено?',
  'field.saveFail': 'Сохранить замечание',
  'fv.myVisits': 'Мои визиты',
  'fv.myVisitsSub': 'Плановое обслуживание у клиентов',
  'fv.pendingToSend': 'Ожидают отправки: {n}',
  'fv.pending': 'Ожидают: {n}',
  'fv.noOpenVisits': 'Открытых визитов нет',
  'fv.noOpenVisitsBody': 'Визиты назначает офис. Как только вам назначат — он появится здесь.',
  'fv.loadVisitsFailed': 'Не удалось загрузить визиты',
  'fv.loadVisitFailed': 'Не удалось загрузить визит',
  'fv.visitNotFound': 'Визит не найден',
  'fv.saveRejected': 'Сохранение отклонено',
  'fv.closeFailed': 'Не удалось закрыть',
  'fv.qrFailed': 'Наклейка не распознана',
  'fv.qrFailedBody': 'Возможно, наклейку заменили или машину удалили. Обратитесь в сервисный офис.',
  'fv.serialNo': 'Серийный №',
  'fv.noOpenVisitForMachine': 'На эту машину нет открытого визита',
  'fv.noOpenVisitForMachineBody': 'Визиты назначает офис. Если вы приехали обслуживать — попросите открыть визит.',
  'fv.machineJobs': 'Ваши открытые работы на этой системе:',
  'fv.noJobOnMachine': 'У вас нет открытой работы на этой системе — обратитесь к менеджеру сервиса',
  'fv.toFieldApp': 'К полевым работам',
  'fv.backToCard': 'Назад к карточке работы',
  'fv.result.DONE': 'Выполнено',
  'fv.result.NOT_DONE': 'Не выполнено',  'fv.history': 'История',
  'fv.priorVisits': 'Предыдущих обслуживаний: {n}',
  'fv.neverServiced': 'Эта машина ещё ни разу не обслуживалась в системе.',
  'fv.service': 'Обслуживание {period}',
  'fv.officeNote': 'От офиса',
  'fv.period.DAILY': 'ежедневное',
  'fv.period.BIWEEKLY': 'раз в две недели',
  'fv.period.SEMIANNUAL': 'полугодовое',
  'fv.period.ANNUAL': 'годовое',
  'fv.period.BIENNIAL': 'раз в два года',
  'fv.period.HOURS': 'по моточасам',
  'fv.period.CUSTOM': 'по объёму заказа',
  'fv.answered': 'Отвечено',
  'fv.offline': 'Нет связи — всё сохраняется на устройстве',
  'fv.backOnline': 'Связь восстановлена, отправка…',
  'fv.beforeAnyWork': 'Перед началом работы',
  'fv.visitClosed': 'Визит закрыт',
  'fv.signedBy': 'Подписал {name}',
  'fv.oneRowLeft': 'Осталась одна строка без ответа',
  'fv.rowsLeft': 'Строк без ответа: {n}',
  'fv.notDoneIsValid': '«Не выполнено» — допустимый ответ. Отчёт, подписанный с пустыми строками, не поможет при споре о гарантии.',
  'fv.zoomImageOf': 'Увеличить иллюстрацию к «{action}»',
  'fv.zoom': 'Увеличить',
  'fv.techsRequired': 'Требуется техников: {n}',
  'fv.partReplaced': 'Заменяли деталь?',
  'fv.notReplaced': 'Не заменяли',
  'fv.howMany': 'Сколько',
  'fv.howManyUnits': 'Сколько единиц заменено',
  'fv.whyReplaced': 'Почему заменили?',
  'fv.chooseCause': 'Выберите причину',
  'fv.causeRequired': 'Укажите причину замены — без неё строка не сохранится',
  'fv.cause.MANUFACTURING': 'Производство',
  'fv.cause.DESIGN': 'Конструкция',
  'fv.cause.INSTALLATION': 'Монтаж',
  'fv.cause.SUPPLIER': 'Деталь поставщика',
  'fv.cause.WEAR': 'Износ',
  'fv.cause.MISUSE': 'Неправильная эксплуатация',
  'fv.photoRequired': 'Замена детали — перед сохранением обязательно фото',
  'fv.note': 'Примечание',
  'fv.notePlaceholder': 'Что увидели, что осталось сделать',
  'fv.photoAgain': 'Переснять',
  'fv.docs': 'Документы',
  'fv.docsHint': 'Инструкции и чертежи модели — открываются в телефоне',
  'fv.manualPage': 'Инструкция · стр. {n}',
  'fv.photoMust': 'Фото обязательно',
  'fv.photoMustMissing': 'Для этого действия нужно фото — сфотографируйте сейчас. Можно продолжить, в отчёте для офиса будет «нет фото».',
  'fv.photoMissingBeforeSign': 'Нет обязательного фото в действиях: {n}. Подписать можно — в карточке заявки в офисе будет «нет фото». Перейти к подписи?',
  'fv.photoMissingBeforeSign_one': 'Нет обязательного фото в {n} действии. Подписать можно — в карточке заявки в офисе будет «нет фото». Перейти к подписи?',
  'fv.photoMissingBeforeSign_few': 'Нет обязательного фото в {n} действиях. Подписать можно — в карточке заявки в офисе будет «нет фото». Перейти к подписи?',
  'fv.photoMissingBeforeSign_many': 'Нет обязательного фото в {n} действиях. Подписать можно — в карточке заявки в офисе будет «нет фото». Перейти к подписи?',
  'fv.startsOnLocked': 'Начинается {date} — закрыть можно только в день работы',
  'fv.needsPart': 'Нужна деталь',
  'fv.needsPartPick': 'Какая деталь нужна?',
  'fv.needsPartChoose': 'Выберите деталь',
  'fv.needsPartSave': 'Отправить в офис',
  'fv.needsPartSent': 'Передано в офис: {part} × {n}',
  'fv.needsPartHint': 'Офис получит черновик предложения на детали для утверждения менеджером сервиса. Клиенту ничего не отправляется.',
  'fv.needsPartRemove': 'Снять отметку',
  'fv.needsPartFailed': 'Отметка «Нужна деталь» не сохранена — попробуйте ещё раз',
  'fv.photo': 'Фото',
  'fv.photoForRow': 'Сфотографировать для этой строки',
  'fv.photoTaken': 'Сделанный снимок',
  'fv.photoRemove': 'Удалить фото',
  'fv.photoRemoveConfirm': 'Удалить фото?',
  'fv.photoRemoveFailed': 'Не удалось удалить фото',
  'fv.callOpened': 'Заявка открыта',
  'fv.openCall': 'Открыть заявку',
  'fv.answeredAt': 'Отвечено в {time}',
  'fv.closePhoto': 'Закрыть снимок',
  'fv.anyTouchCloses': 'Закрывается любым касанием',
  'fv.urgProductionStopped': 'Останавливает производство',
  'fv.urgMachineDown': 'Машина простаивает',
  'fv.urgNone': 'Без срочности',
  'fv.openServiceCall': 'Открытие сервисной заявки',
  'fv.onAction': 'По операции: {action}',
  'fv.urgency': 'Срочность',
  'fv.whatFound': 'Что обнаружено *',
  'fv.whatFoundPlaceholder': 'Сильный износ зубьев приводного колеса, требуется замена',
  'fv.openCallFailed': 'Не удалось открыть заявку',
  'fv.callCarriesContext': 'Заявка открывается с машиной, серийным номером, операцией и вашим примечанием — чтобы в офисе не пришлось переспрашивать, что именно обнаружено.',
  'fv.finishAndSign': 'Завершить и подписать у клиента',
  'pv.back': '→ Назад к заказам',
  'pv.title': 'Отчёт о визите обслуживания — на подпись',
  'pv.loading': 'Загрузка отчёта…',
  'pv.system': 'Система',
  'pv.serial': 'Серийный номер',
  'pv.site': 'Объект',
  'pv.date': 'Дата визита',
  'pv.service': 'Обслуживание',
  'pv.technician': 'Техник',
  'pv.action': 'Операция',
  'pv.result': 'Результат',
  'pv.replaced': 'Заменено',
  'pv.note': 'Примечание техника',
  'pv.done': 'Выполнено',
  'pv.notDone': 'Не выполнено',
  'pv.pending': 'Без ответа',
  'pv.signedBy': 'Отчёт подписал(а) {name}',
  'pv.signIntro': 'На объекте не было представителя для подписи. Если отчёт верен — подпишите здесь. Если что-то неверно, ответьте на полученное письмо, и мы проверим до подписи.',
  'pv.signButton': 'Подписать отчёт',
  'pv.signSubmit': 'Подписать и подтвердить отчёт',
  'pv.signHint': 'Подпись подтверждает, что операции в отчёте выполнены, как описано.',
  'pv.notAwaiting': 'Этот отчёт не ожидает подписи. По вопросам — свяжитесь с нами.',
  'fv.noRep': 'Нет представителя на объекте',
  'fv.noRepConfirm': 'На объекте нет представителя для подписи? Отчёт будет заблокирован, руководитель сервиса отправит его клиенту на подпись в портале. Работу можно закрыть.',
  'fv.noRepWhy': 'Почему некому подписать? (например: представитель ушёл до окончания)',
  'fv.noRepSend': 'Отправить на удалённую подпись',
  'fv.noRepHint': 'Отчёт будет заблокирован, руководитель сервиса отправит его клиенту на подпись в портале.',
  'fv.noRepFailed': 'Не удалось отметить «нет представителя на объекте»',
  'fv.meterHours': 'Моточасы на счётчике машины',
  'fv.meterHoursHint': 'Число на счётчике сейчас — по нему определяется следующее обслуживание по моточасам',
  'fv.meterSaveFailed': 'Не удалось сохранить моточасы — проверьте число и попробуйте снова',
  'fv.awaitingSignature': 'Ожидает подписи клиента',
  'fv.awaitingSignatureBody': 'Представителя на объекте не было. Руководитель сервиса отправит отчёт клиенту на подпись в портале — строки заблокированы. Работу можно закрыть.',
  'fv.hours': '{n} ч',
  'fv.minutes': '{n} мин',

  // ── laser board ──
  'laser.tabQueue': 'Ожидает резки',
  'laser.tabHistory': 'Что нарезано',
  'laser.tabMissing': 'Нет чертежа',
  'laser.queueEmpty': 'Сейчас нет деталей, ожидающих лазерной резки.',
  'laser.historyEmpty': 'Резка на лазере ещё не отмечалась.',
  'laser.missingEmpty': 'Всё нарезанное сопоставлено с чертежом.',
  'laser.missingNote': 'Нарезаны на лазере, чертежа в системе нет. Конструкторы уведомлены; строка закроется сама, когда чертёж загрузят.',
  'laser.unknownMaterial': 'Для {n} артикулов материал и толщина пока неизвестны — они определяются по имени файла при первой резке.',
  'laser.unknownMaterial_one': "Для {n} артикула материал и толщина пока неизвестны — они определяются по имени файла при первой резке.",
  'laser.unknownMaterial_few': "Для {n} артикулов материал и толщина пока неизвестны — они определяются по имени файла при первой резке.",
  'laser.unknownMaterial_many': "Для {n} артикулов материал и толщина пока неизвестны — они определяются по имени файла при первой резке.",
  'laser.material': 'Материал',
  'laser.materialThickness': 'Материал / толщина',
  'laser.thickness': 'Толщина',
  'laser.sheetWeight': 'Вес листа',
  'laser.partsWeight': 'Вес деталей',
  'laser.yield': 'Использование',
  'laser.operator': 'Оператор',
  'laser.delivery': 'Поставка',
  'laser.unknownYet': 'Пока неизвестно',
  'laser.revisedCheckNests': 'Новая ревизия {rev} — проверьте запрограммированные раскладки',
  'laser.toCut': 'К резке',
  'laser.waiting': 'Ожидает',
  'laser.parts': 'Детали',
  'laser.projects': 'Проекты',
  'laser.skus': 'Артикулы',
  'laser.noDrawing': 'без чертежа',
  'laser.outOf': 'из {n}',
  'laser.mm': 'мм',
  'laser.kg': 'кг',
  'laser.groupLine': "{parts} · {units} · {projects}",
  'laser.partsN': "{n} артикулов",
  'laser.partsN_one': "{n} артикул",
  'laser.partsN_few': "{n} артикула",
  'laser.partsN_many': "{n} артикулов",
  'laser.unitsN': "{n} шт.",
  'laser.unitsN_one': "{n} шт.",
  'laser.unitsN_few': "{n} шт.",
  'laser.unitsN_many': "{n} шт.",
  'laser.projectsN': "{n} проектов",
  'laser.projectsN_one': "{n} проект",
  'laser.projectsN_few': "{n} проекта",
  'laser.projectsN_many': "{n} проектов",
  // ── печать этикеток ──
  'laser.tabLabels': 'Печать этикеток',
  'laser.labelsPickUpload': 'Выберите загруженный файл',
  'laser.labelsSearchSku': 'или найдите артикул',
  'laser.labelsSearchPlaceholder': 'Артикул — введите или отсканируйте',
  'laser.labelsEmpty': 'Нет строк для печати. Выберите файл из списка или найдите артикул.',
  'laser.labelsSize': 'Размер этикетки',
  'laser.labelsCount': 'Сколько этикеток',
  'laser.labelsPrint': 'Печать',
  'laser.labelsHeavyHint': 'Более {kg} кг за единицу — предлагается этикетка на каждую деталь',
  'laser.labelsBulkHint': 'Более {kg} кг за единицу — этикетка на каждую деталь, всегда',
  'laser.labelsOneWithQty': 'Одна этикетка с количеством',
  'laser.labelsPerPart': 'Этикетка на каждую деталь, с нумерацией',
  'laser.labelsQtyOnLabel': 'Количество: {n}',
  'laser.labelsIndexOnLabel': 'Этикетка {i}/{n}',
  'laser.labelsNestOnLabel': 'В раскрое {pos}',

  // ── laser board — table headers ──
  'laser.sku': 'Артикул',
  'laser.date': 'Дата',

  // ── search in every window ──
  'laser.searchPlaceholder': 'Поиск — артикул, название или материал',
  'laser.searchClear': 'Очистить поиск',
  'laser.searchCount': 'Показано {shown} из {total}',
  'laser.searchNoMatch': 'Нет строк, подходящих под поиск.',
  'laser.searchQueuePlaceholder': 'Поиск в очереди — артикул, заказ, заказчик или материал',
  'laser.searchHistoryPlaceholder': 'Поиск по резам — файл, материал, оператор или заказ',
  'laser.searchMissingPlaceholder': 'Поиск по недостающим чертежам — артикул, заказ или причина',
  'laser.searchLinesPlaceholder': 'Поиск по строкам файла — артикул или название',
  'laser.searchNestPlaceholder': 'Поиск по деталям раскроя — артикул или название',

  // ── open a nest ──
  'laser.nestOpen': 'Открыть раскрой и посмотреть детали',
  'laser.nestClose': 'Закрыть раскрой',
  'laser.nestEmpty': 'По этому резу строки деталей не сохранены.',
  'laser.nestPartsCount': 'Строк на листе: {n}',
  'laser.nestMissingCount': 'Без чертежа: {n}',
  'laser.unitWeight': 'Вес единицы',
  'laser.size': 'Размер (мм)',

  // ── "загрузили ли чертежи?" ──
  // «Чертёж есть» = у детали есть активная версия в системе, то есть
  // загруженный конструкторским отделом PDF. «Запрошен» = версии нет, но
  // открыт запрос. «Нет» = версии нет и никто не запрашивал.
  'laser.drawing': 'Чертёж',
  'laser.drawingOk': 'Чертёж есть',
  'laser.drawingOkVersion': 'Чертёж есть · версия {n}',
  'laser.drawingArrivedAfter': 'Загружен {d}, уже после реза',
  'laser.drawingRequested': 'Нужен чертёж — запрос открыт {d}',
  'laser.drawingMissing': 'Чертежа нет, запрос не открыт',

  // ── laser board — rows ──
  'laser.lateBy': '(опоздание {n})',
  'laser.waitingDays': '{n} дн.',

  // ── saw station ──
  'saw.backToBar': 'Назад к прутку',
  'saw.nextBar': 'Следующий пруток',
  'saw.idleLogoutHint': 'Время остановлено. Без нажатия 5 минут — выход со станции, следующий продолжит отсюда.',
  'saw.profile': 'Профиль',
  'saw.length': 'Длина',
  'saw.total': 'Итого',
  'saw.queueEmpty': 'Очередь пуста.',
  'saw.table': 'Таблица',
  'saw.list': 'Список',
  'saw.noResults': 'По этому запросу ничего не найдено.',
  'saw.onSaw': 'На пиле',
  'saw.remnantSku': 'Остаток {sku}',
  'cut.straight': 'Прямой',
  'cut.piece': 'Заготовка',
  'cut.barRest': 'Остаток прутка',
  'cut.longFace': 'Длинная грань — по ней меряют',
  'cut.short': '(короткая)',
  'cut.whatComesOut': 'Что должно получиться',
  'cut.bothInward': 'Оба угла показаны внутрь — сверь с чертежом',
  'cut.notStated': 'не указано',
  'cut.inFile': 'в файле',
  'cut.without': 'без',
  'cut.toRotate': 'повернуть',
  'cut.rotateThe': 'Поверни',
  'cut.theProfile': 'профиль',
  'cut.bothEndsFlat': 'Оба конца прямые',
  'cut.headZero': 'Головка пилы на 0°. Без поворота и без наклона.',
  'cut.noRotationCol': 'В этом списке резки не было столбца поворота, поэтому',
  'cut.unknown': 'неизвестно',
  'cut.aroundAxis': 'вокруг своей оси.',
  'cut.dimsOnBar': 'Размеры на этом прутке',
  'cut.mm': 'мм',
  'cut.first': 'первый',
  'cut.second': 'второй',
  'cut.remnantShelf': 'Остаток со стеллажа',
  'cut.wholeBar': 'Целый пруток',
  'cutrep.uncut': 'Не нарезано',
  'cutrep.uncutTitle': 'Не нарезано — вернуть в очередь',
  'cutrep.uncutHint': 'Детали возвращаются в очередь пилы, профиль пересчитывается. Записывается кто, почему и когда.',
  'cutrep.uncutHowMany': 'Сколько не нарезано? (до {max})',
  'cutrep.uncutWhy': 'Причина (обязательно)',
  'cutrep.uncutWhyPh': 'Например: отмечено, но не нарезано',
  'cutrep.uncutGo': 'Вернуть {n} в очередь',
  'cutrep.uncutStage': 'Детали возвращены. Этап рамы детали не откатился автоматически — исправьте в отчёте детали.',
  'cutrep.title': 'Отчёт по резке',
  'cutrep.search': 'Поиск: проект, профиль, артикул, чертёж, рабочий или длина',
  'cutrep.exportCsv': 'Экспорт CSV',
  'cutrep.pieces': 'Заготовки',
  'cutrep.metres': 'м',
  'cutrep.unpriced': 'Без цены',
  'cutrep.cost': 'Стоимость',
  'cutrep.lastCut': 'Последний рез',
  'cutrep.projName': 'Название проекта',
  'cutrep.sku': 'Артикул',
  'cutrep.lengthMm': 'Длина, мм',
  'cutrep.totalMm': 'Итого, мм',
  'cutrep.costIls': 'Стоимость, ₪',
  'cutrep.noPricing': 'без цены',
  'cutrep.fileName': 'otchet-rezki.csv',
  'cutrep.emptyAll': 'На пиле ещё ничего не резали.',
  'cutrep.emptyProject': 'Для этого проекта на пиле ещё ничего не резали.',
  'cutrep.projectTitle': 'Материал, нарезанный на пиле',
  'cutrep.projectSearch': 'Поиск: профиль, артикул, чертёж или длина',
  'cutrep.drawing': 'Чертёж',

  // ── vehicle kiosk ──
  'veh.title': 'Путевой лист',
  'veh.exit': 'Выход',
  'veh.preCheck': 'Осмотр перед выездом',
  'veh.postCheck': 'Осмотр после поездки',
  'veh.whatFound': 'Что обнаружено?',
  'veh.siteOrCity': 'Объект или город',
  'veh.notes': 'Примечания',
  'veh.unknownCode': 'Отсканированный код не распознан, либо машина не в парке. Обратитесь к руководителю за наклейкой.',
  'veh.enterNumber': 'Введите свой табельный номер, чтобы отчитаться.',
  'veh.enter': 'Вход',
  'veh.lastKm': 'Последний записанный пробег:',
  'veh.noChecklist': 'Список осмотра не задан.',
  'veh.ok': 'Годно',
  'veh.notOk': 'Не годно',
  'veh.odometer': 'Пробег',
  'veh.failNeedsNote': 'Пункт, отмеченный «не годно», требует пояснения.',
  'veh.trip': 'Поездка',
  'veh.openTrips': 'Ваши открытые поездки на этой машине:',
  'veh.newTrip': 'Новая поездка вместо этой',
  'veh.purpose': 'Цель поездки',
  'veh.pickPurpose': 'Выберите цель…',
  'veh.destination': 'Пункт назначения',
  'veh.kmOut': 'Пробег при выезде',
  'veh.kmBack': 'Пробег при возврате',
  'veh.fuelIls': 'Заправка, ₪',
  'veh.fuelLitres': 'Заправка, л',
  'veh.distance': 'Пройдено:',
  'veh.km': 'км',
  'veh.closeTrip': 'Закрыть поездку',
  'veh.closeNeeds': 'Для закрытия нужны цель и пробег с обоих концов.',
  'veh.cannotEnter': 'Вход невозможен',
  'veh.insuranceExpired': 'Страховка просрочена',
  'veh.testExpired': 'Техосмотр просрочен',
  'veh.checkSaved': 'Осмотр сохранён.',
  'veh.saveFailed': 'Не удалось сохранить',
  'veh.doneToday': '· выполнен сегодня',
  'veh.updateCheck': 'Обновить осмотр',
  'veh.saveCheck': 'Сохранить осмотр',
  'veh.tripClosed': 'Поездка закрыта. Спасибо.',
  'veh.tripSaved': 'Поездка сохранена и осталась открытой. При возврате снова отсканируйте наклейку, введите пробег и закройте поездку.',
  'veh.othersOpen': 'Открытые поездки других водителей на этой машине:',
  'veh.reportForThem': 'Отчитаться о возврате вместо: {name}',
  'veh.onBehalfHint': 'Поездка остаётся за {name}, ваше имя запишется как отчитавшегося. Введите пробег при возврате и топливо, если заправлялись.',
  'veh.confirmLow': 'Так на одометре',
  'veh.outAtTime': '· выезд {time}',
  'veh.noPurpose': 'Без цели',
  'veh.requiredForPurpose': '(обязательно для этой цели)',
  'veh.optional': '(не обязательно)',
  'veh.save': 'Сохранить',
  'veh.scanTitle': 'Отсканируйте свой бейдж',
  'veh.scanHint': 'Наведите камеру на QR-код на бейдже работника. В следующий раз телефон вас запомнит.',
  'veh.scanButton': 'Сканировать бейдж камерой',
  'veh.identifying': 'Опознаём…',
  'veh.notMe': 'Это не я',
  'veh.refuelled': 'Машина заправлена',
  'veh.refuelNeeds': 'Вы отметили заправку — укажите литры и сумму.',
  'veh.draftSaved': 'Сохранено как черновик в {time}. Телефон можно закрыть — черновик ждёт, пока вы не закроете поездку.',
  'veh.draftLoaded': 'Загружено последнее заполнение (черновик от {time}).',
  'veh.draftFailed': 'Черновик не сохранён — проверьте связь. Введённое всё ещё на экране.',
  'veh.installTitle': 'Едете на монтаж?',
  'veh.installHint': 'Поездка на монтаж отмечается в дне монтажа: машина, пробег при выезде и возврате и заправка — в одном месте, без двойной записи.',
  'veh.installGo': 'Перейти к дню монтажа',
  'veh.hoursOut': 'Моточасы при выезде',
  'veh.hoursBack': 'Моточасы при возврате',
  'veh.lastHours': 'Последние записанные моточасы:',
  'veh.hours': 'ч',
  'veh.kmOutWas': 'При выезде: {km}',
  'veh.monthTitle': 'Ежемесячный пробег',
  'veh.monthHint': 'Эта машина отчитывается раз в месяц. Запишите пробег, который сейчас на одометре.',
  'veh.monthSave': 'Сохранить пробег',
  'veh.monthDone': 'Пробег за этот месяц записан: {km}{by}. Исправление делается в офисе.',
  'veh.monthSaved': 'Пробег сохранён. Спасибо.',
  'veh.byName': ' · {name}',

  // ── remnant station ──
  'rem.title': 'Пост остатков — материал для резки',
  'rem.searchProfile': 'Поиск профиля',
  'rem.shelfExample': 'например: стеллаж 3',
  'rem.searchAny': 'Поиск по артикулу остатка или профилю',
  'rem.switchWorker': 'Сменить рабочего',
  'rem.addRemnant': 'Приём остатка',
  'rem.managerOnlyNote': 'Только просмотр. Остаток от вашего реза записывается при закрытии прутка на пиле. Добавить со стеллажа, перемерить, списать или отметить «нет на месте» — у начальника производства.',
  'rem.whatOnShelf': 'Что на стеллаже',
  'rem.scanBadge': 'Отсканируйте бейдж',
  'rem.tooShort': 'Слишком короткий — в лом',
  'rem.writtenOnShelf': 'Записано на стеллаже',
  'rem.markOnMaterial': 'Отметьте на материале:',
  'rem.anotherRemnant': 'Ещё один остаток',
  'rem.fromThisCut': 'Осталось от текущей резки',
  'rem.foundUnlisted': 'Найден на стеллаже, не записан',
  'rem.whichProfile': 'Какой профиль',
  'rem.change': 'Сменить',
  'rem.noProfiles': 'Профили не заданы — начальник производства задаёт их в настройках',
  'rem.measuredLength': 'Измеренная длина (мм)',
  'rem.measureReal': 'Меряйте по факту — не по тому, что должно было получиться.',
  'rem.endAngle': 'Угол на конце (0 = прямой)',
  'rem.angleNote': 'Скошенный конец используется полностью только если следующий рез под тем же углом.',
  'rem.shelfPos': 'Место на стеллаже (не обязательно)',
  'rem.none': 'Записанных остатков нет.',
  'rem.notOnShelf': 'Нет на стеллаже',
  'rem.fixLength': 'Исправить длину',
  'rem.notFoundNote': '«Нет на стеллаже» вас не останавливает — возьмите целый пруток и продолжайте. Начальник производства получит уведомление.',
  'rem.workerNotFound': 'Рабочий не найден',
  'rem.error': 'Ошибка',
  'rem.minNotSet': '· мин. остаток пока не задан',
  'rem.manual': 'записан вручную',
  'rem.fromCut': 'от резки',

  // ── warehouse issue ──
  'wh.scanItem': 'Отсканируйте позицию',
  'wh.unknownBarcode': 'Этот штрихкод не распознан',
  'wh.searchSku': 'Поиск по артикулу или описанию',
  'wh.cancel': 'Отмена',
  'wh.unbindBtn': 'Это не та деталь — отвязать штрихкод',
  'wh.unbindConfirm': 'Отвязать штрихкод от {sku}? Следующее сканирование его не распознает.',
  'wh.unbindDone': 'Штрихкод отвязан. Отсканируйте снова, чтобы привязать правильно.',
  'wh.location': 'Место',
  'wh.whereFrom': 'Откуда взяли? (необязательно — поможет следующему)',
  'wh.whereFromHint': 'Полка C-8 · синий ящик · верхняя полка у двери',
  'wh.saveLocation': 'Сохранить',
  'wh.locationNotSaved': 'Место не сохранено',
  'wh.inStock': 'На складе',
  'wh.minimum': 'Минимум',
  'wh.supplier': 'Поставщик',
  'wh.belowMin': 'Ниже минимума — заказать',
  'wh.needBadgeFirst': 'Сначала отсканируйте бейдж — привязка записывается на того, кто её сделал.',
  'wh.noMatch': 'Подходящий артикул не найден. Можно закрыть и продолжить свободным текстом — привязать можно позже.',
  'wh.notNow': 'Не сейчас',
  'wh.scanFailed': 'Сканирование не удалось',
  'wh.badgeBeforePhoto': 'Перед съёмкой приложите бейдж',
  'wh.uploadError': 'Ошибка загрузки снимка',
  'wh.uploading': 'Загрузка…',
  'wh.retakePhoto': 'Переснять',
  // «Снято по ошибке» — Эли, 20.09.2026
  'wh.deletePhoto': 'Удалить фото',
  'wh.deletePhotoConfirm': 'Удалить фото этого артикула?',
  'wh.takePhoto': 'Сфотографировать позицию',
  'wh.notLinked': 'Не привязано',

  // ── vehicle kiosk — trip line ──
  'veh.leftAt': '· выехал на {km}',

  // ── remnant station — spec line ──
  'rem.bar': 'пруток {len}',
  'rem.minRem': 'мин. остаток {len}',
  'rem.onShelfN': 'на стеллаже {n}',

  // ── units ──
  'common.metres': 'м',
  'common.millimetres': 'мм',

  // ── дочерние компоненты ──────────────────────────────────────────────
  // Всё ниже добавлено 21.09.2026 — компоненты внутри уже переведённых
  // экранов, которые сами оставались на иврите.

  // ── баннер устаревшей сборки ──
  'build.newVersion': 'Вышла новая версия системы.',
  'build.stillOld': 'Этот экран всё ещё работает на предыдущей версии',
  'build.reloadNow': 'Обновить сейчас',
  'build.notNow': 'Не сейчас',

  // ── ui ──
  'ui.filterNoValues': '{label} — значений для фильтра пока нет',
  'ui.help': 'Что здесь показано?',
  'ui.helpAria': 'Что здесь показано? {title}',
  'ui.helpHow': 'Чтобы появились данные:',
  'ui.helpSource': 'Откуда берутся данные:',

  // ── бренд ──
  'brand.backToApp': 'Вернуться в систему (Esc)',
  'brand.exit': 'Выход',
  'brand.tagline': 'Система управления производством',
  'brand.company': 'ЭДАЛ — проекты робототехники и автоматизации',

  // ── этикетки процесса ──
  'lbl.printOne': 'Печать этикетки',
  'lbl.printMany': 'Печать этикеток: {n}',
  // ── действия руководителя в «Живом производстве» ──
  'common.unexpectedError': 'Непредвиденная ошибка',
  'common.saving': 'Сохранение…',
  'opsa.pickWorker': 'Выберите работника',
  'opsa.pickStage': 'Выберите этап',
  'opsa.pickRoute': 'Выберите маршрут',
  'opsa.badUnits': 'Введите корректное количество единиц (0 и больше)',
  'opsa.badMinutes': 'Введите корректное количество минут',
  'opsa.badQuantity': 'Введите корректное количество',
  'opsa.needsReason': 'Укажите причину',
  'opsa.stopNeedsReason': 'Укажите причину закрытия',
  'opsa.editNeedsReason': 'Укажите причину исправления',
  'opsa.stage': 'Этап',
  'opsa.stageColon': 'Этап:',
  'opsa.stageN': 'Этап {n}',
  'opsa.stageOptional': 'Этап (необязательно)',
  'opsa.chooseStage': '— выберите этап —',
  'opsa.chooseWorker': '— выберите работника —',
  'opsa.autoByDept': '— автоматически по участку —',
  'opsa.units': 'Количество единиц',
  'opsa.unitsShort': 'шт.',
  'opsa.reason': 'Причина',
  'opsa.reasonRequired': 'Причина *',
  'opsa.startTitle': 'Открыть смену',
  'opsa.starting': 'Открытие…',
  'opsa.start': 'Начать',
  'opsa.stopTitle': 'Закрыть смену',
  'opsa.stopping': 'Закрытие…',
  'opsa.zeroUnitsNote': '0 — закрыто без готовых единиц; время всё равно записывается.',
  'opsa.stopReasonPlaceholder': 'Например: работник ушёл со смены, а часы остались открытыми',
  'opsa.completeTitle': 'Отметить этап выполненным',
  'opsa.completeNote': 'Это отметит все единицы этапа как выполненные.',
  'opsa.overrideCode': 'Код руководителя, чтобы отметить несмотря на недостающие детали',
  'opsa.overrideHint': 'Обход записывается с именем владельца кода и списком недостающих деталей, владелец кода получает уведомление.',
  'opsa.workMinutes': 'Рабочее время (минуты)',
  'opsa.minutesShort': 'мин',
  'opsa.minutesPlaceholder': 'минуты',
  'opsa.clockRunning': 'На этапе идут часы —',
  'opsa.zeroClockWarn': '⚠ Таймер шёл меньше минуты — будет записано 0 минут. Уверены?',
  'opsa.confirmOrEdit': 'Подтвердите это время или измените, если оно неверно.',
  'opsa.confirmAndComplete': 'Подтвердить время и отметить выполненным',
  'opsa.confirmTimeFirst': 'Подтвердите или измените измеренное время, затем отметьте выполненным.',
  'opsa.noClockBlocked': 'На этапе нет идущих часов — введите, сколько минут заняла работа. Без времени нет норматива.',
  'opsa.minutesRequired': 'Не указано рабочее время. Введите минуты; если это оценка — отметьте «Оценочное время».',
  'opsa.estimatedTime': 'Оценочное время',
  'opsa.estimatedHint': 'Не измерено часами. Будет помечено как оценка во всех отчётах, чтобы не искажать нормативы времени.',
  'opsa.completing': 'Отмечаем…',
  'opsa.complete': 'Отметить выполненным',
  'opsa.editTimeTitle': 'Изменить время этапа',
  'opsa.currentTime': 'Текущее время: {n} мин.',
  'opsa.newTotalMinutes': 'Новое общее время, мин.',
  'opsa.editScanTitle': 'Изменить запись сканирования',
  'opsa.zeroScanNote': '0 — отчёт фактически отменяется; время остаётся тем, что введено в минутах.',
  'opsa.editReason': 'Причина исправления',
  'opsa.editReasonPlaceholder': 'Например: отчёт подан не по той детали',
  'opsa.reworkTitle': 'Возврат в работу (Rework)',
  'opsa.reworkUnits': 'Количество к возврату',
  'opsa.reworkReasonPlaceholder': 'Опишите причину возврата…',
  'opsa.reworking': 'Выполняем…',
  'opsa.reworkSubmit': 'Вернуть в работу',
  'opsa.qtyTitle': 'Изменение количества',
  'opsa.qtyCurrent': 'Текущее количество:',
  'opsa.qtyNew': 'Новое количество *',
  'opsa.qtyReasonPlaceholder': 'Например: заказчик увеличил заказ',
  'opsa.qtyMoreWarning': 'Больше единиц — больше материала, снабжение получит уведомление. Этапы, уже закрытые по старому количеству, откроются заново.',
  'opsa.qtySubmit': 'Изменить количество',
  'opsa.routeTitle': 'Сменить маршрут',
  'opsa.routeChanging': 'Меняем…',
  'opsa.routeChanged': 'Маршрут изменён',
  'opsa.newRoute': 'Новый маршрут',
  'opsa.chooseRoute': '— выберите маршрут —',
  'opsa.routeConfirm': 'Одинаковые этапы в новом маршруте (тот же участок и то же название) сохранят прогресс; остальные обнулятся. Продолжить?',
  'opsa.routeConfirmEvict': 'Подтвердите, что открытая смена будет закрыта автоматически',
  'opsa.routeCarried': 'Перенесено в новый маршрут:',
  'opsa.routeNothingCarried': 'Переносить было нечего — прогресса на одинаковых этапах нет.',
  'opsa.routeReset': 'Обнулено (в новом маршруте нет такого этапа):',
  'opsa.routeOpenSession': 'Сейчас открыта смена на этапе «{stage}»',
  'opsa.routeEvictNote': 'Смена маршрута закроет смену автоматически с 0 единиц. Отработанное время останется в истории с пометкой «(смена маршрута)».',
  'opsa.iUnderstand': 'Понимаю и подтверждаю',
  'opsa.routeHint': 'Одинаковые этапы в новом маршруте (тот же участок и то же название) сохраняют прогресс. Этап без пары обнуляется — и вы увидите точно, что перенеслось, а что обнулилось.',
  // ── «Живое производство»: доска, таблица и панель работника ──
  'ops.kpiWorkersActive': 'Работают прямо сейчас',
  'ops.kpiWorkersOne': 'один работник',
  'ops.kpiWorkersMany': 'работников: {n}',
  'ops.kpiQuietOne': 'один тихий пост',
  'ops.kpiQuietMany': 'тихих постов: {n}',
  'ops.kpiPartsOnFloor': 'Деталей в цеху',
  'ops.kpiInDepts': 'на участках: {n}',
  'ops.kpiStuckParts': 'Детали без движения',
  'ops.kpiNoneStuck': 'Порог не превышен ни одной деталью',
  'ops.kpiOldestOne': 'Не двигались — самая давняя уже сутки',
  'ops.kpiOldestMany': 'Не двигались — самая давняя уже {n} дн.',
  'ops.kpiCompletedToday': 'Завершено сегодня',
  'ops.managerActions': 'Действия руководителя',
  'ops.reworkMenu': 'Rework / возврат',
  'ops.printQrLabel': 'Печать QR-этикетки',
  'ops.dMinutes': '{n} мин',
  'ops.dHours': '{n} ч',
  'ops.dHoursMinutes': '{h}:{m} ч',
  'ops.dayTotals': 'Итоги дня',
  'ops.sinceMorning': 'С утра и до сейчас',
  'ops.activeWorkers': 'Работают',
  'ops.sessionsClosed': 'Смен закрыто',
  'ops.workTime': 'Рабочее время',
  'ops.stageOfTotal': 'Этап {n} из {total}',
  'ops.coworkerOne': 'ещё один работник',
  'ops.coworkerMany': 'ещё работников: {n}',
  'ops.floorQuiet': 'В цеху тихо',
  'ops.noActiveSession': 'Сейчас нет ни одной открытой смены',
  'ops.floorQuietHint': 'Сейчас никто не работает по открытой смене. Как только кто-то отсканирует бейдж и начнёт этап — доска обновится сама.',
  'ops.groupStuck': 'Без движения — нужно вмешаться',
  'ops.groupRunning': 'В работе сейчас',
  'ops.groupWaiting': 'Ждут участок',
  'ops.groupDone': 'Завершены',
  'ops.doneBadge': 'Завершено',
  'ops.waitingDays': '{n} дн.',
  'ops.noResults': 'Ничего не найдено',
  'ops.bbActive': 'В работе',
  'ops.bbSessions': 'смен',
  'ops.bbTitle': 'Живое производство',
  'ops.bbFooter': 'ESC — выйти из полноэкранного режима · доска обновляется каждые 15 секунд',
  'ops.refresh': 'Обновить',
  'ops.exitFullscreen': 'Выйти из полноэкранного режима',
  'ops.exitFullscreenEsc': 'Выйти из полноэкранного режима (ESC)',
  'ops.workerActivity': 'Активность работника',
  'ops.noWorkerData': 'Данных не найдено',
  'ops.noWorkerDataHint': 'Возможно, работник сегодня не выходил',
  'ops.unitsToday': 'Единиц сегодня',
  'ops.minutesToday': 'Минут сегодня',
  'ops.sessionsActiveNow': 'Открытые смены сейчас',
  'ops.sessionsClosedToday': 'Смены, закрытые сегодня',
  'ops.noClosedSessions': 'Сегодня закрытых смен нет',
  'ops.helperRole': 'помощь',
  'ops.clickToEditScan': 'Нажмите, чтобы изменить запись',
  // ── оболочка приложения: боковое меню, поиск экрана, уведомления, безопасность ──
  'nav.mainNav': 'Основная навигация',
  'nav.searchScreen': 'Поиск экрана',
  'nav.searchScreenEllipsis': 'Искать всё…',
  'nav.searchPlaceholder': 'Что найти? Экран, номер проекта, артикул, вызов, поставщика или сотрудника',
  'nav.closeSearch': 'Закрыть поиск',
  'nav.screens': 'Экраны',
  'nav.noScreenFound': 'Подходящий экран или запись не найдены. Попробуйте одно слово, номер проекта (260250), артикул, вызов или имя поставщика.',
  'find.type.screen': 'Экраны',
  'find.type.project': 'Проекты',
  'find.type.part': 'Детали',
  'find.type.call': 'Сервисные вызовы',
  'find.type.quote': 'Коммерческие предложения',
  'find.type.supplier': 'Поставщики',
  'find.type.stock': 'Складские позиции',
  'find.type.employee': 'Сотрудники',
  'find.kind.screen': 'Экран',
  'find.kind.project': 'Проект',
  'find.kind.part': 'Деталь',
  'find.kind.call': 'Сервисный вызов',
  'find.kind.quote': 'Коммерческое предложение',
  'find.kind.supplier': 'Поставщик',
  'find.kind.stock': 'Складская позиция',
  'find.kind.employee': 'Сотрудник',
  'find.fact.area': 'Раздел',
  'find.fact.customer': 'Клиент',
  'find.fact.site': 'Объект',
  'find.fact.delivery': 'Обещанная поставка',
  'find.fact.project': 'Проект',
  'find.fact.qty': 'Количество',
  'find.fact.opened': 'Открыт',
  'find.fact.created': 'Создано',
  'find.fact.validUntil': 'Действует до',
  'find.fact.contact': 'Контакт',
  'find.fact.phone': 'Телефон',
  'find.fact.email': 'Почта',
  'find.fact.family': 'Семейство',
  'find.fact.onHand': 'На складе',
  'find.fact.department': 'Отдел',
  'find.more': 'Ещё {n} — {label}',
  'find.open': 'Открыть',
  'find.preview': 'Предпросмотр',
  'find.previewEmpty': 'Выберите строку, чтобы увидеть здесь её состояние.',
  'find.inactive': 'Закрыто или неактивно',
  'find.loadError': 'Поиск по записям не удался — экраны по-прежнему здесь. Попробуйте ещё раз через минуту.',
  'find.typesHint': 'экраны, проекты, детали, вызовы, предложения, поставщики, склад, сотрудники',
  'nav.favorites': 'Избранное',
  'nav.pin': 'Добавить в «Моя работа»',
  'nav.unpin': 'Убрать из «Моя работа»',
  'nav.pinAria': 'Добавить «{label}» в «Моя работа»',
  'nav.unpinAria': 'Убрать «{label}» из «Моя работа»',
  'nav.favoritesFull': 'Список заполнен',
  'nav.myWork': 'Моя работа',
  'nav.myWorkHint': 'звёзды и частые',
  'nav.editStars': 'Изменить звёзды',
  'nav.editStarsDone': 'Готово',
  'nav.yourWork': 'Ваша работа',
  'nav.mine': 'Моё',
  'nav.search': 'Поиск',
  'nav.allScreens': 'Все экраны',
  'nav.recent': 'Недавно открытые',
  'nav.collapse': 'Свернуть меню',
  'nav.collapseTitle': 'Свернуть меню — больше места для экрана',
  'nav.expand': 'Развернуть меню',
  'nav.expandAll': 'Развернуть всё',
  'nav.collapseAll': 'Свернуть всё',
  'nav.expandAllAria': 'Развернуть все разделы меню',
  'nav.collapseAllAria': 'Свернуть все разделы меню',
  'nav.hintOpen': 'открыть',
  'nav.hintMove': 'переход',
  'nav.hintClose': 'закрыть',
  'nav.hintPin': 'звезда добавляет в «Моя работа»',
  'nav.openMenu': 'Открыть меню',
  'nav.closeMenu': 'Закрыть меню',
  'nav.logout': 'Выйти',
  'nav.user': 'Пользователь',
  'nav.buildTitle': 'Версия системы, загруженная на этом экране',

  // ── названия ролей в меню ──
  'role.ceo': 'Генеральный директор',
  'role.manager': 'Начальник производства',
  'role.engManager': 'Начальник КБ',
  'role.serviceMgr': 'Начальник сервиса и монтажа',
  'role.purchaser': 'Менеджер по закупкам',
  'role.vp': 'Заместитель директора',
  'role.pm': 'Руководитель проекта',
  'role.accountManager': 'Офис-менеджер',
  'role.warehouse': 'Кладовщик',
  'role.viewer': 'Наблюдатель',
  'role.engineer': 'Инженер',
  'role.productionWorker': 'Рабочий цеха',

  // ── выбор поста (общий вход цеха) ──
  'choose.subtitle': 'Выбор поста',
  'choose.title': 'На каком посту работаем?',
  'choose.hint': 'Выбирается один раз. Вернуться сюда можно в любой момент кнопкой «Сменить пост» вверху экрана.',
  'choose.station': 'Рабочий пост',
  'choose.saw': 'Пила',
  'choose.laser': 'Лазер',
  'choose.finishing': 'Отделка',
  'choose.remnants': 'Остатки',
  'choose.warehouse': 'Склад',
  'choose.nc': 'Несоответствие',
  'kiosk.switchStation': 'Сменить пост',
  'kiosk.endDay': 'Конец рабочего дня',
  'kiosk.endDayTitle': 'Конец рабочего дня',
  'kiosk.endDayBody': 'Все ваши открытые таймеры будут закрыты. Сколько годных и сколько брака по каждому? Введите 0, если нет. Затем выход со станции.',
  'kiosk.endDayConfirm': 'Закрыть всё и завершить день',
  'kiosk.endDayBoardBody': 'Все таймеры на доске будут закрыты. Сколько годных и сколько брака у каждого? Введите 0, если нет.',
  'kiosk.endDayConfirmBoard': 'Закрыть всю доску',
  'kiosk.endDayDone': 'Все таймеры закрыты. Хорошего дня!',
  'kiosk.endDayNoClocks': 'Открытых таймеров не было — вы вышли. Хорошего дня!',
  'kiosk.endDayAsk': 'Остановить таймер и выйти?',
  'kiosk.endDayYes': 'Да, завершить день',
  'kiosk.cancel': 'Отмена',
  'saw.endDayDone': 'Таймер прутка остановлен ({minutes} мин). Пруток остаётся следующему. Хорошего дня!',
  'choose.warehouseLocked': 'Нужен вход кладовщика',
  'wh.needsWarehouseLogin': 'Этот пост требует входа кладовщика — выйдите и войдите под пользователем склада',
  'wh.needsWarehouseLoginAnon': 'Этот пост требует входа кладовщика — войдите под пользователем склада',
  'wh.logoutToLogin': 'Выйти и перейти ко входу',
  'wh.goToLogin': 'Войти как кладовщик',
  'wh.boardError': 'Список комплектации не загрузился. Попробуйте ещё раз; если повторяется — сообщите начальнику производства.',
  'wh.retry': 'Повторить',
  'fin.v2SwitchDept': 'Сменить участок',

  // ── уведомления ──
  'ntf.title': 'Уведомления',
  'ntf.justNow': 'только что',
  'ntf.agoMinutes': '{n} мин назад',
  'ntf.agoHours': '{n} ч назад',
  'ntf.markAllRead': 'Отметить все прочитанными',
  'ntf.desktopBlocked': 'Уведомления рабочего стола заблокированы браузером — включите их в настройках сайта',
  'ntf.desktopEnable': 'Включить уведомления рабочего стола (всплывают и когда система в фоне)',
  // ── התקנה + התראות לפלאפון (אלי 29/09/2026) ──
  'pwa.install': 'Установить приложение',
  'pwa.installShort': 'Установить',
  'pwa.bannerText': 'Установите EDAL MES на главный экран — быстрый запуск и уведомления на телефон',
  'pwa.dismiss': 'Закрыть',
  'pwa.guideTitle': 'Установка приложения',
  'pwa.iosStep1': 'Нажмите кнопку «Поделиться» внизу экрана',
  'pwa.iosStep2': 'Выберите «На экран «Домой»»',
  'pwa.iosStep3': 'Нажмите «Добавить»',
  'pwa.openInSafari': 'На iPhone установить можно только из Safari: откройте этот адрес в Safari и выберите «На экран «Домой»».',
  'pwa.browserMenu': 'Откройте меню браузера (⋮) и выберите «Установить приложение» или «Добавить на главный экран».',
  'pwa.push': 'Уведомления на телефон',
  'pwa.on': 'вкл.',
  'pwa.off': 'выкл.',
  'pwa.pushDenied': 'Уведомления заблокированы браузером — разрешите их в настройках сайта',
  'pwa.pushUnavailable': 'Сейчас на этом устройстве нельзя включить уведомления',
  'pwa.pushIosInstall': 'На iPhone уведомления работают только после установки приложения на экран «Домой» и запуска оттуда.',
  'ntf.empty': 'Уведомлений нет',
  'ntf.emailed': 'отправлено на почту',
  'ntf.emailedAt': 'Письмо отправлено {when}',
  'ntf.measured': 'измерено',
  'ntf.andMore': 'и ещё {n} — нажмите, чтобы увидеть весь список',
  'ntf.dismiss': 'Закрыть уведомление',
  'ntf.details': 'подробнее ({n})',
  'ntf.hideDetails': 'скрыть',

  // ── сообщить о проблеме с экрана ──
  'ri.button': 'Сообщить о проблеме',
  'ri.buttonTitle': 'Сообщить о проблеме на этом экране',
  'ri.what': 'Что произошло?',
  'ri.describeAria': 'Описание проблемы',
  'ri.placeholder': 'Нажал «Сохранить» — и ничего не произошло…',
  'ri.context': 'Сообщение уйдёт вместе с экраном, на котором вы стоите ({path}), и версией системы — чтобы можно было воспроизвести, не спрашивая вас.',
  'ri.send': 'Отправить',
  'ri.sent': 'Отправлено. Спасибо.',
  'ri.failed': 'Сообщение не отправлено',
  'ri.mine': 'Мои сообщения',
  'ri.linkedWhat': 'Ваше сообщение',
  'ri.statusOpen': 'открыто',
  'ri.statusDone': 'решено',

  // ── безопасность учётной записи ──
  'sec.title': 'Безопасность учётной записи',
  'sec.accountSecurity': 'Безопасность учётной записи — пароль и двухфакторная проверка',
  'sec.password': 'Пароль',
  'sec.approvalCode': 'Код подтверждения',
  'sec.twoFactor': 'Двухфакторная проверка',
  'sec.changePassword': 'Смена пароля',
  'sec.passwordChanged': 'Пароль изменён',
  'sec.passwordChangedNote': 'Новый пароль действует с этой минуты. При следующем входе — и с поста, и с телефона — используйте его.',
  'sec.currentPassword': 'Текущий пароль',
  'sec.newPassword': 'Новый пароль',
  'sec.confirmPassword': 'Подтверждение нового пароля',
  'sec.ruleLength': 'не менее {n} символов',
  'sec.ruleLetterDigit': 'хотя бы одна буква и хотя бы одна цифра',
  'sec.passwordsDiffer': 'Пароли не совпадают',
  'sec.setupFailed': 'Настройка не удалась',
  'sec.codeMismatch': 'Код не подходит',
  'sec.disableFailed': 'Отключить не удалось',
  'sec.statusUnavailable': 'Не удалось загрузить состояние проверки.',
  'sec.onWithCodes': 'Включена. Для входа нужен код с телефона; резервных кодов осталось: {n}.',
  'sec.offRecommended': 'Отключена. У вашей роли есть доступ ко всем деньгам, всем заказчикам и удалению проектов — утёкшего пароля хватит, чтобы войти.',
  'sec.offOptional': 'Отключена. Включить можно, но для вашей роли это не требование.',
  'sec.scanInApp': 'Отсканируйте в приложении-аутентификаторе',
  'sec.anyAuthApp': 'Google Authenticator, Microsoft Authenticator или Authy — подойдёт любое.',
  'sec.qrAlt': 'QR-код для настройки проверки',
  'sec.typeSecret': 'Или введите ключ вручную, если камера недоступна',
  'sec.codeNow': 'Код, который сейчас показывает приложение *',
  'sec.enable': 'Включить',
  'sec.notActiveYet': 'Пока вы не подтвердите кодом — проверка не включена, и вход у вас не изменился.',
  'sec.backupCodesTitle': 'Десять резервных кодов — сохраните их сейчас',
  'sec.backupCodesNote': 'Каждый код работает один раз, и у нас они хранятся только в зашифрованном виде — показать их ещё раз невозможно. Без них потерянный телефон закроет вам вход в систему.',
  'sec.copyAll': 'Копировать все',
  'sec.copy': 'Копировать',
  'sec.copied': 'Скопировано',
  'sec.savedDone': 'Сохранил — готово',
  'sec.yourPassword': 'Ваш пароль — не код с телефона',
  'sec.yourPasswordWhy': 'Телефон и так у вас в кармане. Пароль — это то, чего не знает тот, кто сел за открытый экран.',
  'sec.disableTwoFactor': 'Отключить двухфакторную проверку',
  'sec.enableTwoFactor': 'Включить двухфакторную проверку',
  'sec.turnOff': 'Отключить',
  // ── ценник, NFC-киоск, список склада ──
  'nk.readFailed': 'Считать не удалось',
  'nk.holdCard': 'Приложите карту к устройству…',
  'pt.noPrice': 'цена не указана',
  'pt.history': 'история',
  'pt.loadError': 'ошибка загрузки',
  'pt.noHistory': 'прежних цен в записях нет',
  'pt.onePriceOnly': 'в записях только одна цена',
  'pt.from': 'с ',
  'pt.to': 'до ',
  'pt.range': 'диапазон',
  'wl.lessThanHour': 'меньше часа',
  'wl.hoursN': '{n} ч',
  'wl.oneDay': 'сутки',
  'wl.twoDays': 'двое суток',
  'wl.daysN': '{n} дн.',
  'wl.checkStock': 'Проверить склад',
  'wl.awaitingPurchase': 'Ждёт закупки',
  'wl.toPick': 'На комплектацию',
  'wl.signInFirst': 'Список комплектации загрузится после входа поста в систему.',
  'wl.loadFailed': 'Не удалось загрузить список заявок. Это сбой загрузки, а не «комплектовать нечего».',
  'wl.emptyProject': 'По этому проекту открытых заявок нет.',
  'wl.emptyWarehouse': 'Открытых заявок на складе нет.',
  'wl.oldest': 'Самая давняя:',
  'wl.inStock': 'на складе',

  // ── решение по несоответствующему материалу (ISO 9001 8.7) ──
  'dsp.choose': 'Что делаем с материалом? (обязательно)…',
  'dsp.qty': 'Количество единиц',
  'dsp.qtyLabel': 'Сколько единиц (обязательно)',
  'dsp.reasonRequired': 'Обоснование (обязательно)',
  'dsp.noteOptional': 'Примечание (необязательно)',
  'dsp.useAsIsNote': 'Использование как есть — это допуск отклонения: ваше имя запишется как утвердившего.',
  'dsp.scrapNote': 'Брак записывается с количеством и с тем, кто его утвердил.',
  'dsp.needDisposition': 'Укажите, что делаем с материалом',
  'dsp.needReason': 'Нужно обоснование',
  'dsp.needQty': 'Укажите количество',
  'dsp.needRootCause': 'Выберите корневую причину',
  'dsp.needAction': 'Опишите корректирующее действие',
  'dsp.needOwner': 'Назначьте ответственного',
  'dsp.needDueDate': 'Назначьте срок',

  // ── закрытие несоответствия — один экран (NcCloseDialog) ──
  'ncc.title': 'Закрытие несоответствия',
  'ncc.whatToDo': 'Что делаем с деталью',
  'ncc.kind.REWORK': 'Исправить — вернуть на участок',
  'ncc.kind.REPAIR': 'Ремонт (не по чертежу)',
  'ncc.kind.USE_AS_IS': 'Использовать как есть',
  'ncc.kind.SCRAP': 'Брак (списать)',
  'ncc.kind.RETURN': 'Вернуть поставщику',
  'ncc.kind.NOT_APPLICABLE': 'Не применимо',
  'ncc.returnTo': 'Возвращается на участок',
  'ncc.foundHere': 'найдено здесь',
  'ncc.foundAt': 'найдено на участке {stage}',
  'ncc.units': '{qty} шт.',
  'ncc.qty': 'Сколько штук',
  'ncc.reason': 'Обоснование (обязательно)',
  'ncc.rootCause': 'Корневая причина (обязательно)',
  'ncc.whatDone': 'Что сделано (необязательно)',
  'ncc.action': 'Корректирующее действие, чтобы не повторилось',
  'ncc.actionNo': 'Не нужно',
  'ncc.actionYes': 'Нужно',
  'ncc.actionWhat': 'Какое действие',
  'ncc.owner': 'Ответственный…',
  'ncc.due': 'Срок',
  'ncc.submit': 'Закрыть несоответствие',
  'ncc.cancel': 'Отмена',
  'ncc.loading': 'Загрузка несоответствия…',
  'ncc.notOpen': 'Несоответствие уже закрыто или не найдено. Обновите экран.',
  'ncc.failed': 'Не удалось закрыть — попробуйте ещё раз',
  'ncc.done': 'Сохранено ✓ несоответствие по {part} закрыто',
  'ncc.selfClose': 'Вы открыли это несоответствие — закрывает другой человек (инженер или начальник производства).',
  'ncc.outRework': 'Деталь вернётся на участок {stage} и пройдёт маршрут оттуда заново ({qty} шт.).',
  'ncc.outReworkNoStage': 'Деталь будет исправлена и вернётся на проверку ({qty} шт.).',
  'ncc.outRepair': 'Деталь ремонтируется и идёт дальше — записано как ремонт не по чертежу.',
  'ncc.outUseAsIs': 'Деталь идёт дальше как есть — отклонение записывается на ваше имя.',
  'ncc.outScrap': '{qty} шт. списываются — начальнику производства открывается задача изготовить заново.',
  'ncc.outReturn': '{qty} шт. возвращаются поставщику — записано в отчёт качества.',
  'ncc.outNa': 'Несоответствие закрывается — несоответствующего материала не было.',
  'ncc.outUnblock': 'Блокировка следующего участка снимается.',
  // ── открытие несоответствия одним нажатием (RaiseNcModal) ──
  'ncr.pickType': 'В чём проблема? Нажатие сразу открывает',
  'ncr.complaintWarranty': 'Жалоба клиента: на гарантии?',
  'ncr.underWarranty': 'На гарантии',
  'ncr.notUnderWarranty': 'Не на гарантии',
  'ncr.openComplaint': 'Открыть жалобу клиента',
  'ncr.opened': 'Сохранено ✓ открыто несоответствие по {part}',
  // ── фото несоответствия (30/10/2026) ──
  'ncr.photoAdd': 'Сфотографировать / приложить',
  'ncr.photoMore': 'Ещё фото',
  'ncr.photoRemove': 'Убрать фото',
  'ncr.photoCount': 'Будет приложено фото: {n}',
  'ncr.photoOne': 'Будет приложено одно фото',
  'ncr.photoSaving': 'Сохраняю фото…',
  'ncr.photoSaved': 'Сохранено ✓ фото приложено',
  'ncr.photoOpen': 'Фото {n} — открыть в полном размере',
  // ── несоответствие из строки комплекта на складе ──
  'ncr.lineButton': 'Несоответствие',
  'ncr.lineTitle': 'Открыть несоответствие по {item}',
  'ncr.lineOnSub': 'Позиции нет в дереве — записано на подсборку {part}, артикул в описании.',
  'ncr.lineOnProject': 'Позиции нет в дереве — записано на проект (сборка {part}), артикул в описании.',

  // ── настройка пунктов проверки перед работой ──
  'chka.intro': 'Пункты, которые нужно подтвердить до начала работы, по этапам. Этап без пунктов ничего не блокирует — требует подтверждения только тот, которому пункты заданы.',
  'chka.pickStage': 'Выберите этап…',
  'chka.item': 'Пункт',
  'chka.itemPlaceholder': 'Очистить стол перед сваркой',
  'chka.deptOptional': 'Участок (необязательно)',
  'chka.allDepts': 'Все участки',
  'chka.needsValueHint': 'Требует значения (например, размер с предыдущего поста) — подтвердить без ввода нельзя',
  'chka.needsEngineerHint': 'Требует подтверждения инженера — бейдж рабочего этот пункт не закроет',
  'chka.add': 'Добавить',
  'chka.empty': 'Пункты не заданы. Сейчас ни один этап работу не блокирует.',
  'chka.itemCount': 'пунктов: {n}',
  'chka.needsValue': 'требует значения',
  'chka.engineerApproval': 'подтверждение инженера',

  // ── открытые корректирующие действия ──
  'nco.title': 'Открытые корректирующие действия ({n})',
  'nco.overdue': 'просрочено: {n}',
  'nco.closesOnlyAfterCheck': 'Действие закрывается только после проверки, что оно сработало',
  'nco.supplier': 'поставщик',
  'nco.production': 'производство',
  'nco.checkedFailed': 'проверено — не сработало',
  'nco.cause': 'причина: {name}',
  'nco.owner': 'ответственный: {name}',
  'nco.workedYes': 'Проверено — помогло',
  'nco.workedNo': 'Проверено — не помогло',
  'nco.noNote': 'обоснование не записано',
  'nco.review': 'Проверка результативности',
  'nco.notePlaceholder': 'Что проверяли и на основании чего (необязательно)',
  'nco.workedClose': 'Сработало — закрыть',
  'nco.didNotWork': 'Не сработало',
  'nco.saveFailed': 'Ошибка при сохранении проверки',

  // ── несоответствия поставщиков ──
  'snc.title': 'Несоответствия поставщиков ({n})',
  'snc.subtitle': 'Забраковано при приёмке — ждут корневой причины',
  'snc.rejectedOf': 'забраковано {n} из {total}',
  'snc.deliveryNote': 'накладная {no}',
  'snc.handle': 'Обработать',
  'snc.rootCause': 'Корневая причина (обязательно)…',
  'snc.actionNeeded': 'Нужно корректирующее действие по поставщику',
  'snc.actionPlaceholder': 'В чём будет действие',
  'snc.owner': 'Ответственный…',
  'snc.close': 'Закрыть несоответствие',
  'snc.closeFailed': 'Ошибка при закрытии несоответствия',
  'snc.closeDoesNotRelease': 'Закрытие несоответствия не освобождает материал. Освобождение делается при входном контроле на экране закупок.',
  // ── чертежи реза и отчёт по резке ──
  'cut.sectionAria': 'Сечение профиля, повёрнуто на {deg}°',
  'cut.topViewAria': 'Вид сверху: полотно под углом {deg} градусов',
  'cut.pieceAria': 'Готовая заготовка: {len} мм по длинной грани',
  'cut.thePiece': 'заготовка',
  'cut.restOfBar': 'остаток прутка',
  'cut.longFaceHint': 'Длинная грань — по ней и меряем',
  'cut.noRotationColumnA': 'В этом списке реза не было столбца поворота, поэтому',
  'cut.noRotationColumnB': 'режется ли второй угол по той же грани. Сверьтесь с чертежом до реза.',
  'cut.redMarkNote': 'Красная метка на сечении — только базовая грань: она показывает величину поворота, а не то, какая грань сверху. Важно другое: между первым и вторым резом профиль поворачивается на',
  'cutrep.qty': 'Кол-во',
  'cutrep.projects': 'проектов',
  'cutrep.nUnpriced': '{n} без расценки',
  'cutrep.helpWhat': 'Каждая заготовка, отрезанная на посту пилы, сгруппированная по проекту, профилю и длине: сколько штук, сколько метров и во что обошлось.',
  'cutrep.helpHow': 'Строка появляется в момент, когда заготовка отмечена отрезанной на посту пилы — при отметке заготовки или при закрытии прутка.',
  'cutrep.helpSource': 'Пост пилы. Стоимость — доля заготовки в прутке, из которого её отрезали, по израсходованной длине, включая работу (тариф участка) и материал (цена за метр профиля), по ценам дня реза. Пруток без расценки считается отдельно и не суммируется как ноль. Столбец стоимости виден только тем, у кого есть право VIEW_COSTS.',
  'cut.cutN': 'Рез {which}',
  'cut.fromSquare': 'от перпендикуляра',
  'cut.fromProfile': 'от профиля',
  'cut.layoutForCut': 'Укладка профиля для реза {n}',
  'cut.nCut': 'отрезано: {n}',
  'cut.nWaiting': 'ждут: {n}',
  'cut.nSkipped': 'пропущено: {n}',
  'cut.barMapAria': 'Карта прутка: {len} мм',

  // ── קיוסק המחסן ────────────────────────
  'wh.title': 'Склад',
  'wh.openRows': 'открытых строк',
  'wh.readyToPick': 'готово к выдаче',
  'wh.atSupplier': 'Заказано',
  'wh.notOrdered': 'не заказано',
  'wh.awaitingStock': 'ждёт проверки склада',
  'wh.switch': 'Сменить',
  'wh.scanBadge': 'Отсканируйте свой бейдж, чтобы начать',
  'wh.cantScan': 'Не сканируется? Выберите из списка',
  'wh.noSku': 'Без артикула — не сканируется',
  'wh.noLocation': 'Нет места хранения',
  'wh.nothingToPick': 'Сейчас нечего собирать.',
  'wh.allProjects': '← Все проекты',
  'wh.kitOpen': 'Тележка открыта',
  'wh.openKitPrint': 'Открыть тележку и печать',
  'wh.badgeFirst': 'Сначала отсканируйте бейдж',
  'wh.needBadgeForKit': 'Отсканируйте бейдж перед открытием тележки',
  'wh.nothingReady': 'В этом проекте сейчас нет строк, готовых к выдаче',
  'wh.pickSheetTitle': 'Лист комплектации',
  'wh.pickHeading': 'Комплектация',
  'wh.colSku': 'Артикул',
  'wh.colLocation': 'Место',
  'wh.routeOrder': 'По маршруту обхода — готовое к сборке, полка за полкой',
  'wh.colDesc': 'Описание',
  'wh.colQty': 'Кол-во',
  'wh.colState': 'Статус',
  'wh.colWaiting': 'Ожидает',
  'wh.colPicked': 'Собрано',
  'wh.inKit': 'В тележке',
  'wh.whoAreYou': 'Кто вы?',
  'wh.close': 'Закрыть',
  'wh.identifyFailed': 'Не удалось распознать — попробуйте ещё раз',
  'wh.unknownCode': '{code} — не найден в системе',
  'wh.codeNotRecognised': 'Код не распознан',
  'wh.networkError': 'Ошибка сети',
  'wh.rowsCount': 'строк',
  'wh.rowsCount_one': 'строка',
  'wh.rowsCount_few': 'строки',
  'wh.rowsCount_many': 'строк',
  'wh.oldest': 'самая старая {age}',
  'wh.days': '{n} дней',
  'wh.days_one': '{n} день',
  'wh.days_few': '{n} дня',
  'wh.days_many': '{n} дней',
  'wh.hours': '{n} часов',
  'wh.hours_one': '{n} час',
  'wh.hours_few': '{n} часа',
  'wh.hours_many': '{n} часов',

  // ── Буква ревизии назначается системой ──
  'upload.revision': 'Ревизия {rev}',
  'upload.revisionLabel': 'Ревизия',
  'upload.revisionCorrected': 'В файле ревизия {file} — сохранено как {assigned} (следующая ревизия)',
  'upload.revisionBySystem': 'назначается системой',
  'upload.revisionBySystemHint': 'Букву ревизии назначает система: новая деталь получает A, существующая — следующую букву после последней выпущенной.',
  'upload.fileRevision': 'в имени файла: {rev}',

  // ── Разрешение работы без чертежа (задача 4) ──
  'engHold.wait': 'Ждать чертёж',
  'engHold.waive': 'Разрешить работу без чертежа',
  'engHold.waitState': 'Ждёт чертёж',
  'engHold.waivedState': 'Работа без чертежа разрешена',
  'engHold.defaultTag': 'по умолчанию',
  'engHold.by': 'решил(а): {name}',
  'engHold.error': 'Решение не сохранено: {msg}',
  'engHold.networkError': 'Нет связи с сервером — попробуйте через минуту.',

  // ── Станция: деталь, разрешённая без чертежа ──
  'st.waivedTitle': 'Нет чертежа',
  'st.waivedBy': 'Работа без чертежа разрешена: {name}',
  'st.waivedUnknown': 'руководитель',
  'st.waivedHint': 'Когда чертёж будет загружен, станция сообщит и предложит перейти на него.',

  // ── Отмена строки «вырезано на лазере без чертежа» (задача 4b) ──
  'laserCancel.button': 'Отменить',
  'laserCancel.title': 'Отмена строки — {part}',
  'laserCancel.titleShort': 'Отмена строки',
  'laserCancel.reasonLabel': 'Причина (обязательно)',
  'laserCancel.reason.RENAMED': 'Чертёж загружен под другим именем',
  'laserCancel.reason.PROJECT_UNTRACKED': 'Проект не требует отслеживания',
  'laserCancel.reason.CUT_ERROR': 'Ошибка в вырезанной детали',
  'laserCancel.reason.OTHER': 'Другое',
  'laserCancel.hint.RENAMED': 'Выберите деталь, чей чертёж загружен под другим именем. Вырезанное количество и минуты будут записаны на неё, этап лазера продвинется.',
  'laserCancel.hint.PROJECT_UNTRACKED': 'Все {n} строк проекта {project} будут закрыты, будущие резки проекта пропускаются — без запроса чертежа и без уведомлений. Можно восстановить.',
  'laserCancel.hint.PROJECT_UNTRACKED_one': '{n} строка проекта {project} будет закрыта, будущие резки проекта пропускаются — без запроса чертежа и без уведомлений. Можно восстановить.',
  'laserCancel.hint.PROJECT_UNTRACKED_few': 'Все {n} строки проекта {project} будут закрыты, будущие резки проекта пропускаются — без запроса чертежа и без уведомлений. Можно восстановить.',
  'laserCancel.hint.PROJECT_UNTRACKED_many': 'Все {n} строк проекта {project} будут закрыты, будущие резки проекта пропускаются — без запроса чертежа и без уведомлений. Можно восстановить.',
  'laserCancel.hint.CUT_ERROR': 'Будет записан брак лазера: {qty} шт. · {material} · и машинное время — для отчётов по браку и стоимости, без несоответствия.',
  'laserCancel.hint.OTHER': 'Коротко опишите, что произошло — пояснение сохраняется и попадает в отчёты.',
  'laserCancel.noProject': 'Проекта {project} нет в системе, отмечать нечего. Выберите «Другое», чтобы закрыть строку.',
  'laserCancel.noteRequired': 'Пояснение (обязательно)',
  'laserCancel.noteOptional': 'Примечание (необязательно)',
  'laserCancel.pickPart': 'Выберите деталь…',
  'laserCancel.similar': 'похожа на артикул из отчёта',
  'laserCancel.loadingParts': 'Загрузка деталей проекта…',
  'laserCancel.noParts': 'В этом проекте нет деталей для выбора — загрузите чертёж вместо отмены.',
  'laserCancel.mm': 'мм',
  'laserCancel.restorable': 'Можно восстановить в «Недавно отменённые».',
  'laserCancel.confirm': 'Отменить строку',
  'laserCancel.close': 'Закрыть',
  'laserCancel.needReason': 'Выберите причину',
  'laserCancel.networkError': 'Нет связи с сервером — попробуйте через минуту.',
  'laserCancel.recentTitle': 'Недавно отменённые',
  'laserCancel.project': 'Проект {project}',
  'laserCancel.linkedTo': 'записано на',
  'laserCancel.by': '{name} · {date}',
  'laserCancel.restore': 'Восстановить',
  'laserCancel.restoreGroup': 'Восстановить все {n} строк и отслеживание',
  'laserCancel.restoreGroup_one': 'Восстановить {n} строку и отслеживание',
  'laserCancel.restoreGroup_few': 'Восстановить все {n} строки и отслеживание',
  'laserCancel.restoreGroup_many': 'Восстановить все {n} строк и отслеживание',
  'laserCancel.restored': 'Восстановлено: {name}',
  'laserCancel.renamedNoRestore': 'Записано на деталь — для исправления обратитесь к начальнику производства',

  // ── Конструктор отчётов: отмены и брак лазера ──
  'rb.dataset.laserCancels': 'Отмены и брак лазера',
  'rb.lc.rows': 'Количество отмен',
  'rb.lc.qty': 'Вырезано, шт.',
  'rb.lc.minutes': 'Машинное время (мин)',
  'rb.lc.kg': 'Вес (кг)',
  'rb.lc.cost': 'Стоимость материала',
  'rb.lc.records': 'Отмены',
  'rb.lc.unknown': 'неизвестно',
  'rb.lc.g.reason': 'Причина',
  'rb.lc.g.project': 'Проект',
  'rb.lc.g.part': 'Артикул',
  'rb.lc.g.by': 'Кто отменил',
  'rb.lc.g.material': 'Материал и толщина',
  'rb.lc.g.month': 'Месяц',

  // ── מניעת ייצור כפול במיפוי (אלי, 25/09/2026) ──
  'dup.noDrawing': "нет чертежа",
  'dup.compare': "Сравнить",
  'dup.checking': "Сравниваю с чертежами проекта…",
  'dup.compareFailed': "Сравнение с чертежами проекта не загрузилось — сервер проверит при отправке.",
  'dup.looksLike': "Похоже на {pn} ({pct}%)",
  'dup.titleBlock': "В штампе указан {pn} — он уже есть в проекте",
  'dup.looksLikeHint': "Если это та же деталь под другим номером — её изготовят дважды. Сравните и выберите.",
  'dup.thisPage': "Страница файла",
  'dup.thisIsIt': "Это она — ревизия",
  'dup.notThis': "Это новая деталь, не {pn}",
  'dup.existingParts': "Детали проекта — похожие первыми. Нажатие = это она (ревизия того же номера).",
  'dup.showAll': "Все детали ({n})",
  'dup.showLess': "Только похожие",
  'dup.newCount': "{n} новых деталей",
  'dup.newCount_one': "{n} новая деталь",
  'dup.newCount_few': "{n} новые детали",
  'dup.newCount_many': "{n} новых деталей",
  'dup.revCount': "{n} ревизий",
  'dup.revCount_one': "{n} ревизия",
  'dup.revCount_few': "{n} ревизии",
  'dup.revCount_many': "{n} ревизий",
  'dup.summaryHint': "В проекте уже есть детали. Убедитесь, что ни одна из новых не является существующей под другим номером — иначе её изготовят дважды.",
  'dup.confirmAll': "Проверил — это новые детали",
  'dup.confirmOne': "Проверил — это новая деталь",
  'dup.retry': "Загрузить снова",
  'dup.compareTitle': "Страница {page} и {pn}",
  'dup.existing': "Существующий чертёж — {pn}",
  'dup.blockLooksLike': "Страница {page} похожа на {pn} — выберите её («Это она») или отметьте «Это новая деталь»",
  'dup.blockConfirmAll': "Нужно отметить «Проверил — это новые детали» (в жёлтом блоке)",
  'sheet.repeatedMark': "Одинаковый на всех листах — проверьте",
  'sheet.fromSimilarMark': "({pct}%) — проверьте",
  'sheet.checkAck': "Проверил — страница {page} это {pn}",
  'sheet.checkAckWhole': "Проверил — этот файл это {pn}",
  // ── Отмена детали = отмена чертежа (U-0924-07) ──
  'pc.title': 'Отмена детали {part} (= отмена чертежа)',
  'pc.restoreTitle': 'Восстановление детали {part}',
  'pc.titleLoading': 'Отмена детали',
  'pc.retry': 'Повторить',
  'pc.cancelledBy': 'Отменил(а) {who}, {when}. Причина: {reason}',
  'pc.restoreWith': 'Вернутся вместе с ней (отменены вместе): {list}',
  'pc.restoreExplain': 'Деталь вернётся во все очереди и посты. Остановленные таймеры не перезапускаются, заявки на закупку не меняются.',
  'pc.consequences': 'Что произойдёт',
  'pc.clocks': 'Будет остановлено таймеров: {n} — рабочих попросят указать, сколько сделано: {list}',
  'pc.clocks_one': 'Будет остановлен {n} таймер — рабочего попросят указать, сколько сделано: {list}',
  'pc.clocks_few': 'Будут остановлены {n} таймера — рабочих попросят указать, сколько сделано: {list}',
  'pc.clocks_many': 'Будет остановлено {n} таймеров — рабочих попросят указать, сколько сделано: {list}',
  'pc.clocksNone': 'Открытых таймеров по детали нет.',
  'pc.produced': 'Уже изготовлено: {list} — остаётся в истории и в затратах.',
  'pc.producedNone': 'По детали ещё ничего не изготовлено.',
  'pc.laser': 'Лазер ещё не завершён — деталь уходит из очереди лазера, лазер получит уведомление убрать её из раскроев.',
  'pc.saw': 'Строк в карте резки: {n} — уходят из очереди пилы, пила получит уведомление.',
  'pc.saw_one': '{n} строка в карте резки — уходит из очереди пилы, пила получит уведомление.',
  'pc.saw_few': '{n} строки в карте резки — уходят из очереди пилы, пила получит уведомление.',
  'pc.saw_many': '{n} строк в карте резки — уходят из очереди пилы, пила получит уведомление.',
  'pc.nc': 'Открытых несоответствий: {n} — остаются у качества с меткой «деталь отменена» и не блокируют отгрузку.',
  'pc.nc_one': '{n} открытое несоответствие — остаётся у качества с меткой «деталь отменена» и не блокирует отгрузку.',
  'pc.nc_few': '{n} открытых несоответствия — остаются у качества с меткой «деталь отменена» и не блокируют отгрузку.',
  'pc.nc_many': '{n} открытых несоответствий — остаются у качества с меткой «деталь отменена» и не блокируют отгрузку.',
  'pc.history': 'Уже записанные время и затраты остаются в отчётах. Деталь остаётся в дереве с меткой «отменена» и может быть восстановлена — загрузка чертежа тоже её восстанавливает.',
  'pc.children': 'Отменить также дочерние детали: {n}',
  'pc.children_one': 'Отменить также {n} дочернюю деталь',
  'pc.children_few': 'Отменить также {n} дочерние детали',
  'pc.children_many': 'Отменить также {n} дочерних деталей',
  'pc.reason': 'Причина отмены (обязательно)',
  'pc.reasonPlaceholder': 'Например: заказчик отказался от второго конвейера',
  'pc.reasonHint': 'Это увидит рабочий, когда отсканирует деталь.',
  'pc.reasonMissing': 'Укажите причину, чтобы отменить',
  'pc.note': 'Примечание об уже изготовленном (необязательно)',
  'pc.notePlaceholder': 'Например: 2 сваренные единицы на полке у покраски',
  'pc.submit': 'Отменить деталь',
  'pc.restore': 'Восстановить деталь',
  'pc.cancelShort': 'Отменить деталь',
  'pc.restoreShort': 'Восстановить деталь',
  'pc.cancelledBadge': 'Отменена',
  'pc.cancelledTip': 'Отменил(а) {who}: {reason}',
  'upload.partRestored': 'Деталь была отменена ({reason}) — чертёж вернул её в работу. Восстановил(а): {who}.',
  'upload.partRestoredWith': 'Вернулись вместе с ней: {list}',
  'st.stoppedAUTO_CLOSED': 'Таймер закрылся автоматически — экран остался без ответа. Записано 0 единиц.',
  'st.autoClosedSaved': 'Отправлено начальнику производства на подтверждение. Спасибо!',
  'st.autoClosedHint': 'Ответ уйдёт начальнику производства, он подтвердит количество и часы. 0 = единиц не закончил.',
  'st.stoppedPART_CANCELLED': 'Деталь отменена — работать по ней нельзя',
  'st.stoppedOnPart': 'Деталь {part}',
  'err.partCancelled': 'Деталь {partNumber} отменена — {cancelReason}. Работать по ней нельзя. Если это ошибка — обратитесь к: {cancelledBy}.',
  'pc.projectReqs': 'Открытых заявок на закупку в проекте {project}: {n}. Снабжение получит уведомление проверить их; заявки не отменяются.',
  'pc.projectReqs_one': 'В проекте {project} {n} открытая заявка на закупку. Снабжение получит уведомление проверить её; она не отменяется.',
  'pc.projectReqs_few': 'В проекте {project} {n} открытые заявки на закупку. Снабжение получит уведомление проверить их; заявки не отменяются.',
  'pc.projectReqs_many': 'В проекте {project} {n} открытых заявок на закупку. Снабжение получит уведомление проверить их; заявки не отменяются.',
  'pc.possiblyRelated': 'Возможно, связано (название совпадает с номером): {list}',
  'pc.menuCancelledNote': 'Деталь отменена',
  'pc.ncPartCancelled': 'Деталь отменена',
  'pb.toolbar': 'Действия с выбранными деталями',
  'pb.selected': 'Выбрано: {n}',
  'pb.cancel': 'Отменить детали',
  'pb.restore': 'Восстановить ({n})',
  'pb.revision': 'Загрузить ревизию',
  'pb.print': 'Печать этикеток',
  'pb.clear': 'Снять выбор',
  'pb.noneLive': 'Все выбранные детали уже отменены — можно восстановить',
  'pb.noneCancelled': 'Среди выбранных нет отменённых деталей',
  'pb.selectAll': 'Выбрать все показанные',
  'pb.selectBranch': 'Выбрать весь узел',
  'pb.selectRow': 'Выбрать {part}',
  'pb.cancelTitle': 'Отмена деталей (= отмена чертежей) — выбрано {n}',
  'pb.restoreTitle': 'Восстановление деталей — {n}',
  'pb.revisionTitle': 'Загрузка ревизии для выбранных деталей — {n}',
  'pb.blocked': 'Не будет отменена: {why}',
  'pb.withParent': 'Будет отменена вместе с {parent}',
  'pb.willCancel': 'Будет отменена',
  'pb.clocksWillStop': 'Будут остановлены таймеры: {n} — рабочего спросят, сколько сделано',
  'pb.clocksStopped': 'Остановлено таймеров: {n}',
  'pb.includeChildren': 'Отменить также дочерние детали каждой детали',
  'pb.reasonHint': 'Одна причина для всех деталей — её увидит рабочий при сканировании.',
  'pb.noneReady': 'Среди выбранных нет деталей, которые можно отменить',
  'pb.cancelSubmit': 'Отменить ({n})',
  'pb.restoreExplain': 'Детали вернутся во все очереди и посты — вместе со всеми, кто был отменён вместе с ними.',
  'pb.restoreSubmit': 'Восстановить ({n})',
  'pb.resultSummary': 'Выполнено: {done} · Отказано: {refused}',
  'pb.outcome.CANCELLED': 'Отменена',
  'pb.outcome.CANCELLED_WITH_PARENT': 'Отменена вместе с родительской',
  'pb.outcome.RESTORED': 'Восстановлена',
  'pb.outcome.RESTORED_WITH_PARENT': 'Восстановлена вместе с родительской',
  'pb.outcome.REFUSED': 'Отказано',
  'pb.revisionExplain': 'Перетащите PDF-файлы. Каждый файл привязывается к выбранной детали по номеру в имени; букву ревизии назначает система.',
  'pb.pickFiles': 'Выбрать файлы',
  'pb.dropHint': 'или перетащите сюда',
  'pb.matched': 'Будут загружены: {n}',
  'pb.unmatchedFiles': 'Файлы без пары — не будут загружены: {n}',
  'pb.unmatchedParts': 'Выбранные детали без файла: {n}',
  'pb.unmatched.NO_PART_NUMBER': 'В имени файла нет номера детали',
  'pb.unmatched.NOT_SELECTED': 'Номер {part} не входит в выбор',
  'pb.unmatched.DUPLICATE': 'Для детали {part} уже есть файл',
  'pb.noMatched': 'Ни один файл не подошёл к выбранной детали',
  'pb.uploadSubmit': 'Загрузить ({n})',
  'pb.uploadNetwork': 'Связь с сервером прервалась — часть чертежей могла загрузиться. Обновите список перед повтором.',
  'pb.releaseRefused': 'Проект ещё не передан в производство — чертежи сохраняются только вместе с передачей, в окне экрана загрузки. Отсюда ничего не сохранено.',
  'pb.outcome.DONE': 'Выполнено',
  'pb.outcome.SKIPPED': 'Пропущено',
  'pbm.route': 'Сменить маршрут',
  'pbm.pin': 'Первым в очереди',
  'pbm.complete': 'Отметить этап выполненным',
  'pbm.assign': 'Назначить рабочему',
  'pbm.routeTitle': 'Сменить маршрут — выбрано: {n}',
  'pbm.pinTitle': 'Первым в очереди — выбрано: {n}',
  'pbm.completeTitle': 'Отметить этап выполненным — выбрано: {n}',
  'pbm.assignTitle': 'Назначить рабочему на сегодня — выбрано: {n}',
  'pbm.routeExplain': 'Один маршрут для всех деталей. Сделанное на таком же этапе (тот же отдел и название) переносится; остальное обнуляется — как при смене маршрута одной детали.',
  'pbm.hold': 'Остановить',
  'pbm.holdTitle': 'Остановить детали — выбрано: {n}',
  'pbm.holdExplain': 'Детали остановлены, пока их не освободят. Причина видна цеху — как «Остановить деталь» в меню одной детали.',
  'pbm.release': 'Вернуть в работу',
  'pbm.releaseTitle': 'Вернуть в работу — выбрано: {n}',
  'pbm.releaseExplain': 'Снимает остановку с каждой выбранной остановленной детали. Неостановленные пропускаются. Укажите причину — её получит тот, кто остановил.',
  'pbm.skip': 'Пропустить отдел',
  'pbm.skipTitle': 'Пропустить отдел — выбрано: {n}',
  'pbm.skipExplain': 'Этап отдела отмечается «не выполнен» с причиной, деталь идёт дальше без него. Сам маршрут не меняется.',
  'pbm.pull': 'Перевести в отдел / вернуть на доработку',
  'pbm.unitsAll': 'Все',
  'pbm.pullTitle': 'Перевести в отдел — выбрано: {n}',
  'pbm.pullExplain': 'Вперёд: каждый этап по пути отмечается — оценкой минут или «не выполнен» с причиной. Назад: сколько единиц возвращается + причина, как «На доработку». Отдела нет в маршруте — отметьте «Добавить в маршрут».',
  'pbm.reasonLabel': 'Причина',
  'pbm.reasonPlaceholder': 'Почему? (обязательно)',
  'pbm.deptLabel': 'Отдел',
  'pbm.pickDept': 'Выберите отдел',
  'pbm.pullInRoute': 'В отдел из маршрута',
  'pbm.pullAdd': 'Добавить отдел в маршрут и перейти туда',
  'pbm.pullMinutes': 'Оценка минут на каждый этап по пути (вперёд)',
  'pbm.pullMinutesHint': 'Пусто = этапы по пути записываются «не выполнен» с причиной.',
  'pbm.unitsBack': 'Возвращается единиц',
  'pbm.pinExplain': 'Каждая деталь встаёт в начало очереди своего текущего отдела. Порядок сохраняется: первая выбранная — первая.',
  'pbm.completeExplain': 'Текущий этап каждой детали будет отмечен выполненным. Если этап изменится до нажатия — деталь будет пропущена с объяснением.',
  'pbm.assignExplain': 'Выберите одного рабочего; все детали войдут в его план на сегодня.',
  'pbm.routeLabel': 'Новый маршрут',
  'pbm.employeeLabel': 'Рабочий',
  'pbm.pickRoute': 'Выберите маршрут',
  'pbm.pickEmployee': 'Выберите рабочего',
  'pbm.noneReady': 'В выборе нет детали, с которой можно выполнить действие — причина указана у каждой',
  'pbm.willSkip': 'Будет пропущено: {why}',
  'pbm.minutesEachHint': 'Время для каждого чертежа отдельно — в его строке списка (всего на чертёж, не на единицу). Число здесь заполняет каждую пустую строку. Записывается как «Оценочное время». Деталь с идущими часами будет пропущена — отметьте её отдельно, чтобы подтвердить измеренное время.',
  'pbm.acceptEvict': 'Понимаю: открытые таймеры закроются с 0 шт., рабочим нужно будет отсканировать заново',
  'pbm.submit': 'Выполнить ({n})',
  'pbm.resultSummary': 'Выполнено: {done} · Пропущено: {refused}',
  // ── עבודה מחוץ למחלקה (U-0924-08) ──
  'ood.badge': 'Вне отдела',
  'ood.badgeTitle': 'Сотрудник другого отдела, без подтверждения руководителя — работа разрешена и отмечена для начальника производства',
  'ood.tab': 'Вне отдела',
  'ood.tabHint': 'Работа сотрудников другого отдела без подтверждения руководителя',
  'ood.intro': 'Сотрудник работал на этапе чужого отдела без подтверждения руководителя. Работа не заблокирована — она здесь, чтобы вы знали.',
  'ood.from': 'С даты',
  'ood.to': 'По дату',
  'ood.stageDept': 'Отдел этапа',
  'ood.worker': 'Сотрудник',
  'ood.all': 'Все',
  'ood.count': 'Строк: {n} · идут сейчас: {open}',
  'ood.colWorker': 'Сотрудник',
  'ood.colWorkerDepts': 'Его отделы',
  'ood.colPart': 'Деталь',
  'ood.colStage': 'Этап',
  'ood.colStageDept': 'Отдел этапа',
  'ood.colWhen': 'Когда',
  'ood.colMinutes': 'Время',
  'ood.running': 'Идёт сейчас',
  'ood.closed': 'Закрыто',
  'ood.partner': 'Напарник',
  'ood.owner': 'Владелец таймера',
  'ood.noDept': 'Без отдела',
  'ood.minutes': '{n} мин',
  'ood.empty': 'В этом диапазоне и с этим фильтром нет работы вне отдела. Расширьте диапазон дат или выберите «Все».',
  'ood.loading': 'Загрузка…',
  'ood.errorLabel': 'Работа вне отдела',
  'ood.badRange': 'Дата «с» позже даты «по» — измените одну из них.',

  // ── „לא שוחרר לייצור = לא קיים" (אלי, 25/09/2026) ──
  'rel.title': "Запуск в производство — проект {project}",
  'rel.checkTitle': "Проект {project} уже в производстве",
  'rel.nothingSaved': "Проект ещё не запущен, поэтому ничего не сохранено — чертежи сохраняются только вместе с «Запустить в производство». Выход с экрана или «Отмена» = ничего не сохраняется.",
  'rel.checkNote': "Пока ничего не сохранено. Проверьте, что ни один чертёж здесь не дублирует уже выполненное в производстве.",
  'rel.checkLabel': "Проверил чертежи — они не дублируют уже выполненное",
  'rel.release': "Запустить в производство",
  'rel.upload': "Загрузить",
  'rel.cancel': "Отмена — ничего не сохраняется",
  'rel.new': "Новая деталь",
  'rel.revision': "Ревизия",
  'rel.unchanged': "Без изменений",
  'rel.error': "Не будет записано",
  'rel.colPart': "Номер",
  'rel.colAction': "Что будет записано",
  'rel.colRevision': "Ревизия",
  'rel.colQty': "Кол-во",
  'rel.panelRefused': "Проект ещё не запущен в производство — чертёж сохраняется только вместе с запуском, на экране загрузки. Отсюда ничего не сохраняется, запрос остаётся открытым; когда чертёж загрузят с запуском, он закроется, а резка будет записана на деталь.",
  'rel.toUpload': "К экрану загрузки и запуска",
  // ── Поиск/сканирование детали в киосках ──────────────────────────────
  'psearch.toggle': 'Поиск или сканирование детали',
  'psearch.placeholder': 'Артикул или номер проекта',
  'psearch.hint': 'Сканирование штрихкода чертежа сразу открывает деталь',
  'psearch.noParts': 'Деталь не найдена. Проверьте номер на чертеже или ищите по номеру проекта.',
  'psearch.notFound': 'Деталь не найдена. Проверьте номер на чертеже или ищите по номеру проекта.',
  'psearch.clear': 'Очистить и выбрать другую деталь',
  'psearch.project': 'Проект',
  'psearch.whereNow': 'Сейчас находится в',
  'psearch.stage': 'Этап',
  'psearch.unknownStage': 'Неизвестно',
  'psearch.routeDone': 'Маршрут завершён',
  'psearch.inMyDept': 'Деталь в вашем отделе',
  'psearch.notInMyDept': 'Деталь не в вашем отделе — для работы нужен перевод с разрешения руководителя',
  'psearch.missingChildren': 'Отсутствующие детали: {n}',
  'psearch.openDrawing': 'Открыть чертёж',
  'psearch.drawingNeedsBadge': 'Перед открытием чертежа отсканируйте пропуск',
  'psearch.drawingRefused': 'Не удалось открыть чертёж. Обратитесь к начальнику смены.',
  'psearch.noThumb': 'Нет изображения чертежа',
  'psearch.thumbAlt': 'Миниатюра чертежа',
  'psearch.enlarge': 'Увеличить чертёж {part}',
  'psearch.tapToEnlarge': 'Нажмите на чертёж, чтобы увеличить',
  'psearch.pickThis': 'Выбрать деталь',
  'psearch.scanForFull': 'Это предпросмотр. Для полного листа отсканируйте пропуск.',
  'psearch.sheetRefusedPreview': 'Показан только предпросмотр.',
  'psearch.sheetPage': 'Страница {i} из {n}',
  'psearch.prevPage': 'Предыдущая страница',
  'psearch.nextPage': 'Следующая страница',
  'dbar.placeholder': 'Номер чертежа — сканируйте или введите. Полный номер открывает чертёж',
  'dbar.placeholderStart': 'Номер чертежа — сканируйте или введите. Полный номер открывает и запускает работу',
  'dbar.placeholderFinishing': 'Номер чертежа — сканируйте или введите (после пропуска)',
  'ot.title': 'Сборочные комплекты — проект {n}',
  'ot.kits': 'Сборочные комплекты',
  'ot.singles': 'Отдельные детали',
  'ot.kitMine': '{mine} у вас · готово {done}/{n}',
  'ot.asm': 'Сборка',
  'ot.asmParts': 'Её детали ({n})',
  'ot.groupMine': 'У вас',
  'ot.groupDone': 'Готовые',
  'ot.noDept': 'Без отдела',
  'ot.waiting': 'Ждёт вас',
  'ot.doneView': 'Готово — откроется для просмотра, без часов',
  'ot.doneShort': 'Готово',
  'ot.elsewhere': '{d} — выполняется на другом киоске',
  'ot.viewNeedsOpen': 'Готовая сборка открывается только вместе с открытой деталью — сначала отметьте деталь',
  'ot.start': 'Запустить часы: {n}',
  'ot.plusView': ' + чертёж сборки для просмотра',
  'ot.pickParts': 'Отметьте детали',
  'ot.pickOpen': 'Отметьте и открытую деталь',
  'ot.drawingOf': 'Чертёж сборки {n}',
  'ot.noDrawing': 'Нет чертежа',
  'ot.viewOnly': 'Только просмотр — без часов. Время идёт на {n}',
  'ot.scanned': 'Отсканировано',
  'ot.done': 'Готово — все этапы маршрута выполнены',
  'ot.alone': 'Открыть отдельно',
  'ot.aloneWhy': 'Сейчас в отделе «{d}» — открывается отдельно, перенос с разрешения начальника',
  'ot.aloneWhyNoDept': 'Не в вашем отделе — открывается отдельно',
  'ot.alreadyOpen': 'Уже открыт у вас',
  'ot.cancel': 'Отмена',
  'ot.limit': 'До {max} одновременно · открыто у вас {open} · можно отметить ещё {left}',
  'ot.failed': 'Не открылись — откройте каждый отдельно:',
  'ot.continue': 'Перейти к открытым чертежам ({n})',
  'ot.onboarding': 'Режим внедрения — маршрут не блокирует',
  'ot.empty': 'В этом проекте нет активных деталей.',
  // Q&A — questions and answers (Eli 05/10)
  'qa.newQuestion': 'Новый вопрос',
  'qa.newAnswer': 'Новый ответ',
  'qa.newMessage': 'Новое сообщение',
  'qa.unanswered': 'Без ответа со вчера',
  'qa.stOpen': 'Открыт',
  'qa.stAnswered': 'Есть ответ',
  'qa.stDone': 'Решён',
  'qa.stVerbal': 'Закрыт — ответ устно',
  'qa.myQuestions': 'Мои вопросы',
  'qa.openCount': 'открыто: {n}',
  'qa.tabTo': 'Мне',
  'qa.tabAsked': 'Я спросил',
  'qa.tabClosed': 'Закрытые',
  'qa.loading': 'Загрузка…',
  'qa.loadError': 'Не удалось загрузить вопросы. Обновите страницу.',
  'qa.emptyTo': 'Нет открытых вопросов к вам.',
  'qa.emptyAsked': 'Нет открытых вопросов от вас.',
  'qa.emptyClosed': 'За последние 60 дней закрытых вопросов нет.',
  'qa.to': 'Кому:',
  'qa.linkedTo': 'Связано с:',
  'qa.askedFrom': 'Задан из:',
  'qa.reopen': 'Открыть снова',
  'qa.reopened': 'Открыт снова',
  'qa.dueBy': 'Ответ до {d}',
  'qa.dueLabel': 'Ответ до',
  'qa.now': 'сейчас',
  'qa.minAgo': '{n} мин назад',
  'qa.hAgo': '{n} ч назад',
  'qa.yesterday': 'вчера',
  'qa.question': 'Вопрос',
  'qa.threadError': 'Вопрос не загрузился. Откройте его снова из «Мои вопросы».',
  'qa.reply': 'Ответ',
  'qa.addToThread': 'Добавить в переписку…',
  'qa.yourAnswer': 'Ваш ответ…',
  'qa.send': 'Отправить',
  'qa.answer': 'Ответить',
  'qa.closeVerbal': 'Закрыть без ответа — ответил устно',
  'qa.markDone': 'Решено — закрыть вопрос',
  'qa.verbalHint': 'Закрытие без ответа → спросивший получит: «Закрыт — ответ устно»',
  'qa.linkProject': 'Проект',
  'qa.linkPart': 'Деталь / артикул',
  'qa.linkPurchasing': 'Закупки',
  'qa.linkEngineering': 'Инженерия',
  'qa.needProject': 'Номер проекта',
  'qa.needPart': 'Артикул',
  'qa.removeLink': 'Убрать связь',
  'qa.link': 'Связать',
  'qa.cancel': 'Отмена',
  'qa.noLinkHint': 'или без связи — выберите получателя в «Кому:»',
  'qa.remove': 'Убрать: {name}',
  'qa.pickRecipient': 'Выберите получателя',
  'qa.pick': 'Выберите…',
  'qa.addRecipient': '+ ещё получатель',
  'qa.purchaseHint': 'Из строки закупки: автор заявки + его руководитель по роли. Гендиректор не в копии.',
  'qa.text': 'Вопрос',
  'qa.needRecipient': 'Добавьте получателя в «Кому:», чтобы отправить.',
  'qa.ask': 'Вопрос по строке',
  'qa.askOpen': 'Вопрос по строке (открыто: {n})',
  'qa.failed': 'Не получилось. Попробуйте ещё раз.',
  // סקר העמדה 11/10/2026
  'st.excelOnlyStage': 'Деталь на участке {dept} — он отчитывается файлом Excel на посту лазера, не здесь. Код мастера не нужен: обратитесь на пост лазера или к мастеру.',
  'ot.goTo': 'Перейти к чертежу',
  'err.offline': 'Нет связи с сервером — проверьте сеть и попробуйте снова. Если повторяется — позовите мастера.',
  'net.reconnecting': 'Система обновляется или пропала связь — переподключение произойдёт само, нажимать ничего не нужно',
  'err.serverDown': 'Сервер не ответил — попробуйте ещё раз через минуту. Если повторяется — позовите мастера.',
  'st.retry': 'Повторить',
  'st.badgeNextStep': 'Отсканируйте ещё раз или введите номер с пропуска. Если повторяется — обратитесь к мастеру или в офис.',
  // ── «Доброе утро, <имя>» — мои выезды на станции (Эли 12/10/2026, решения 159–160) ──
  'fm.helloMorning': 'Доброе утро, {name}',
  'fm.helloNoon': 'Добрый день, {name}',
  'fm.helloEvening': 'Добрый вечер, {name}',
  'fm.todayField': 'Сегодня вы на выезде',
  'fm.tomorrowField': 'Завтра вы на выезде',
  'fm.kind.INSTALLATION': 'Монтаж',
  'fm.kind.VISIT': 'Сервисный визит',
  'fm.kind.CALL': 'Сервисный вызов',
  'fm.departure': 'Выезд',
  'fm.vehicle': 'Машина',
  'fm.days': 'Дни',
  'fm.dayOf': '{n} из {m}',
  'fm.crewLead': 'Бригадир: {name}',
  'fm.you': 'вы',
  'fm.load': 'Что грузим',
  'fm.loadLegend': 'Тот же список, что «Оборудование» в полевом приложении — отмеченное здесь отмечено и там.',
  'fm.noEquipment': 'Для этой работы оборудование не задано — спросите у начальника сервиса.',
  'fm.todaySite': 'Сегодня на объекте',
  'fm.tomorrowSite': 'Завтра на объекте',
  'fm.contact': 'Контакт: {name}',
  'fm.safety': 'Безопасность',
  'fm.noTasks': 'Задачи для работы не записаны.',
  'fm.visitActions': 'Действий в чек-листе обслуживания: {n}',
  'fm.markAll': 'Отметить всё',
  'fm.showList': 'Показать список',
  'fm.hideList': 'Скрыть список',
  'fm.officeNote': 'Заметка офиса',
  'fm.group.PARTS': 'Запчасти (артикул)',
  'fm.group.KIT': 'Инструменты и материалы',
  'fm.group.MANUAL': 'Добавлено вручную',
  'fm.openField': 'Открыть в полевом приложении',
  'fm.toStation': 'К станции →',
  'fm.tickFailed': 'Отметка не сохранена — попробуйте ещё раз. Если повторяется — обратитесь к начальнику сервиса.',
  'fm.takenBy': 'Отметил: {name}',
  'fm.cond.AT_HEIGHT': 'Работа на высоте — страховка и инструктаж на объекте',
  'fm.cond.SITE_WELD': 'Сварка на объекте — допуск на огневые работы',
  'fm.cond.PHOTO_RESTRICTED': 'Режимный объект — фотографировать без разрешения нельзя',
  'err.fieldNotOnCrew': 'Этот предмет относится к работе, в бригаде которой вас нет — отсканируйте пропуск ещё раз или обратитесь к начальнику сервиса.',
  'err.fieldJobClosed': 'Работа уже закрыта — грузить нечего. Если это ошибка — обратитесь к начальнику сервиса.',
  // 15/10/2026 — коды цеха, которые ещё приходили на иврите (см. `lib/stationError.ts`).
  'err.drawingMissingWait': 'Деталь ждёт чертёж — мастер решил не начинать, пока его не загрузят',
  'err.drawingMissing': 'У этой детали нет чертежа — нужно разрешение мастера',
  'err.badgeRevoked': 'Этот бейдж аннулирован — вместо него выдан новый (версия {version}). Отсканируйте новый бейдж или обратитесь к начальнику производства.',
  'err.badgeScanOnly': 'Этот бейдж недействителен, а один табельный номер не открывает — отсканируйте новый бейдж. Бейдж потерян? Начальник производства распечатает замену.',
  'err.badgeOld': 'Это старый бейдж, он больше не действует — попросите новый у начальника производства.',
  'err.photoPickOff': 'Выбор из списка на этом месте отключён — отсканируйте бейдж. Нет бейджа? Начальник производства распечатает замену.',
  'err.exceedsPreviousStageAll': 'Все {arrived} шт., пришедшие с «{prevStageName}», здесь уже отчитаны. Чтобы отчитать ещё {units} шт., нужен код мастера.',
  'err.exceedsPreviousStage':'С этапа «{prevStageName}» пришло {arrived} шт., здесь уже отчитано {doneHere} шт. (брак {scrappedHere} шт.) — можно отчитать не больше {mayReport} шт. Чтобы отчитать {units} шт., нужен код мастера.',
  'err.hasDrawing': 'У этой детали уже есть действующий чертёж — разрешение не нужно. Обновите список.',
  'err.notAManager': 'Отсканированная карта — не карта мастера. Можно ввести код мастера вместо неё.',
  'err.invalidMinutes': 'Минуты предыдущего этапа: не меньше 1 и меньше времени текущей работы',
  'err.notSelfPrior': 'Этот таймер не на предыдущем этапе — завершите как обычно',
  'err.partDone': 'Деталь прошла все этапы маршрута. Исправление готовой детали — только через несоответствие.',
  'err.priorNotEarlier': 'Сами можно выполнить только этап, который идёт перед вашим',
  'err.priorNoOwnStage': 'У вашего участка нет этапа в этой детали — выполнить предыдущий этап нельзя',
  'err.priorStageMachine': 'Это станочный этап (лазер, гибка или мехобработка) — он остаётся на своём участке',
  'err.routeChanged': 'Маршрут детали только что изменился. Отсканируйте чертёж ещё раз.',
  'err.routeNotSequential': 'В нумерации маршрута детали есть пропуски, поэтому добавить этап со станции нельзя. Обратитесь к начальнику производства — он добавит участок через «Потянуть» на доске производства.',
  'err.sharedScannedRoute': 'Этот маршрут собран из сканирований и общий с другими деталями, поэтому добавить в него этап нельзя — это изменило бы процесс других деталей. Обратитесь к начальнику производства, чтобы перевести деталь на собственный маршрут.',
  'err.weldViaChildInvalid': 'Это не та сборка, которая ждёт сварки отсканированной детали. Отсканируйте чертёж ещё раз, а если повторится — обратитесь к начальнику производства.',
  'err.partNotInWork': 'Этот артикул есть, но сейчас не в работе (проект не активен или деталь удалена). Обратитесь к начальнику производства.',
  'err.projectClosed': 'Проект закрыт, поэтому деталь не в работе. Если её нужно делать — начальник производства откроет проект заново.',
  'err.sawBadgeRequired': 'Отсканируйте пропуск на станции пилы',
  'err.sawNotInDepartment': 'Выбранный станок — не рабочая пила участка пилы. Выберите пилу из списка на станции или попросите начальника производства обновить станки участка.',
  'err.sawRequired': 'Выберите, на какой пиле вы работаете',
  'err.remainingUnanswered': 'Остались неотмеченные куски. Укажите, отрезали вы их или нет — система не угадывает.',
  'err.laserAlreadyUploaded': 'Этот раскрой уже загружен. Станок записал точно такое же время резки — значит, это та же резка. Если вы действительно вырезали ту же программу ещё раз — подтвердите и загрузите всё равно.',
  'err.noFileChosen': 'Файл не выбран',
  'err.approvalCodeUnset': 'В системе не задан код мастера. Его нужно задать в настройках, прежде чем подтверждать действия на станции.',
  'err.approvalCodeDefault': 'Код мастера всё ещё заводской. Его нужно сменить в настройках — пока он не изменён, он ничего не защищает.',
  'err.cardRevoked': 'Карта аннулирована и никого не опознаёт. Обратитесь в офис за новой картой.',
  'fm.navigate': 'Waze',  'st.tree.running': 'В работе у вас',
  'st.tree.awayShow': 'Детали на других участках: {n}',
  'st.tree.awayHide': 'Скрыть детали других участков',
  'st.tree.doneGroup': 'Завершены ({n})',
  'st.ncQty': 'Сколько единиц? (из {max}, необязательно)',
  'st.reportedNow': '✓ Отчитано {n} шт. ({done}/{total}) · {stage}',
  'st.movedTo': ' · деталь передана: {dept}',
  'st.movedToParent': ' · деталь передана в сборку {n}',
  'st.nextPartOpened': 'Экран перешёл к следующему открытому чертежу: {n}',
  // ── «Обновлено · Что нового» внизу каждого экрана (UX X4) ──
  'upd.updated': 'Обновлено {when}',
  'upd.dev': 'Версия разработки',
  'upd.whatsNew': 'Что нового',
  'upd.loading': 'Загрузка…',
  'upd.error': 'Список не загрузился — откройте «Что нового и обновлено».',
  'upd.none': 'Пока нет изменений, опубликованных для команды.',
  'upd.all': 'Всё — в «Что нового и обновлено»',
  // ── склад по процессу кладовщика (31/10/2026) ──
  'whp.title': "Склад",
  'whp.close': "Закрыть",
  'whp.loading': "Загрузка…",
  'whp.loadFail': "Список не загрузился. Обновите страницу; если повторится — сообщите о неисправности.",
  'whp.nav.check': "Проверка запаса",
  'whp.nav.issue': "К выдаче",
  'whp.dock.check': "Проверка",
  'whp.dock.issue': "Выдача",
  'whp.noDateShort': "Нет даты",
  'whp.date.release': "Запуск в производство",
  'whp.date.productionEnd': "Конец производства",
  'whp.date.need': "Требуемая дата",
  'whp.date.delivery': "Поставка клиенту",
  'whp.date.fat': "Плановый FAT",
  'whp.date.install': "Монтаж",
  'whp.date.promised': "Обещание поставщика",
  'whp.date.requested': "Дата заявки",
  'whp.drain.left': "Осталось {n}",
  'whp.drain.checked': "Проверено {done} из {total}",
  'whp.drain.issued': "Выдано {done} из {total}",
  'whp.drain.search': "Поиск или скан: позиция, артикул, проект",
  'whp.drain.groupBy': "Группировка",
  'whp.drain.by.project': "Проект",
  'whp.drain.by.sku': "Артикул",
  'whp.drain.sortBy': "Сортировка по дате",
  'whp.drain.sortOption': "Сортировка: {name}",
  'whp.drain.project': "Фильтр по проекту",
  'whp.drain.allProjects': "Все проекты",
  'whp.drain.noSkuGroup': "Без артикула",
  'whp.drain.need': "Нужно",
  'whp.drain.catalogHint': "В системе {qty} (не проверено)",
  'whp.drain.answer': "Ответ",
  'whp.drain.now': "Выдать сейчас",
  'whp.drain.nowDoes': "Без подсчёта",
  'whp.drain.filters': "Фильтр",
  'whp.drain.onOrder': "В заказе:",
  'whp.drain.onOrderTo': "для {to}",
  'whp.drain.onOrderStock': "склада",
  'whp.drain.onOrderDue': "ожидается {date}",
  'whp.ready.title': "{name}: готово к выдаче",
  'whp.ready.sub': "Строка уже в «К выдаче». Можно выдать отсюда работнику, который стоит перед вами.",
  'whp.ready.issue': "Выдать {qty} сейчас",
  'whp.ready.later': "Оставить в «К выдаче» и продолжить",
  'whp.ready.to': "Выдача: {name}",
  'whp.ready.doneTo': "{name}: выдано, {worker}",
  'whp.ready.serial': "Строк с серийным номером: {n}. Продолжите в «К выдаче», там откроется поле.",
  'whp.overStock': "В системе записано только {n}. Проверили на полке?",
  'whp.overStockOk': "На полке есть, выдать",
  'whp.finderOnOrder': "{n} в заказе",
  'whp.drain.doneNow': "{name}: выдано на проект",
  'whp.drain.doneNowLeft': "{name}: есть на складе. Строк осталось к выдаче: {n}, проверьте там.",
  'whp.origin.item': "Позиция",
  'whp.origin.qty': "Кол-во",
  'whp.origin.from': "Источник",
  'whp.origin.who': "Кто",
  'whp.origin.when': "Когда",
  'whp.origin.shelf': "Полка",
  'whp.origin.counted': "Пересчитано на складе",
  'whp.origin.noCount': "На складе, без подсчёта",
  'whp.origin.supplier': "От поставщика",
  'whp.origin.box': "Коробка",
  'whp.origin.none': "Не записано",
  'whp.drain.have': "Есть всё",
  'whp.drain.haveDoes': "без подсчёта → к выдаче",
  'whp.drain.none': "Нет",
  'whp.drain.noneDoes': "→ в закупку",
  'whp.drain.part': "Часть",
  'whp.drain.partDoes': "пересчитано, остаток → в закупку",
  'whp.drain.typeHint': "Или введите количество + Enter, или сканируйте",
  'whp.drain.backToAnswers': "Назад к ответам",
  'whp.drain.notInList': "«{code}» нет в этом списке. Сканируйте ожидающую позицию или введите количество.",
  'whp.drain.doneHave': "Есть всё, без подсчёта: {name} → к выдаче",
  'whp.drain.doneNone': "Нет: {name} → в закупку",
  'whp.drain.donePart': "{name}: есть {got} из {of} → остаток в закупку",
  'whp.drain.noneInFilter': "В этом фильтре нет строк",
  'whp.drain.checkEmpty': "Всё проверено",
  'whp.drain.checkEmptySub': "Закупка может заказывать.",
  'whp.drain.toIssueN': "Дальше к выдаче ({n})",
  'whp.drain.atEngineer': "Ждут инженера — замена ({n})",
  'whp.drain.linesReady': "Готовых строк: {n}",
  'whp.drain.linesReady_few': "{n} готовые строки",
  'whp.drain.linesReady_many': "{n} готовых строк",
  'whp.drain.linesReady_one': "Одна готовая строка",
  'whp.drain.andMore': "и ещё {n}",
  'whp.drain.lastIssued': "Выдано {n}: строк {lines} · этикетки:",
  'whp.drain.issueEmpty': "Всё выдано",
  'whp.drain.issueEmptySub': "Сейчас нет готовых строк в запущенных проектах.",
  'whp.drain.backHome': "Назад на «Сегодня»",
  'whp.drain.noSkuN': "{n} без артикула",
  'whp.drain.inCellN': "{n} в ячейке",
  'whp.drain.unreleased': "Ещё не запущены ({n})",
  'whp.drain.continue': "Дальше",
  'whp.drain.checkWhy': "Закупка ждёт ответа",
  'whp.drain.issueWhy': "Производство ждёт материал",
  'whp.drain.statsToday': "Проверено сегодня {c} · выдано сегодня {i}",
  'whp.drain.allClear': "Всё проверено и всё выдано",
  'whp.drain.allClearNone': "Сейчас на складе нет открытой работы.",
  'whp.drain.nextRecv': "Дальше: приём от поставщиков ({n})",
  'whp.drain.nextCount': "Дальше: подсчёт полки ({n})",
  'whp.drain.nextUpcoming': "Дальше: скоро в производство ({n})",
  'whp.issueN': "Выдать строк: {n}",
  'whp.issueN_few': "Выдать {n} строки",
  'whp.issueN_many': "Выдать {n} строк",
  'whp.issueN_one': "Выдать одну строку",
  'whp.issuedSelf': "Выдано строк {project}: {n} — записано на вас",
  'whp.issuedSelf_few': "Выдано {n} строки {project} — записано на вас",
  'whp.issuedSelf_many': "Выдано {n} строк {project} — записано на вас",
  'whp.issuedSelf_one': "Выдана строка {project} — записано на вас",
  'whp.badgeOptional': "Карточка работника (необязательно) — без неё записывается на вас",
  'whp.sku.onShelf': "На полке",
  'whp.sku.howMany': "Сколько?",
  'whp.sku.howManyOf': "Сколько на полке — {name}",
  'whp.sku.total': "Всего нужно",
  'whp.sku.forProjects': "для проектов: {n}",
  'whp.sku.forProjects_few': "для {n} проектов",
  'whp.sku.forProjects_many': "для {n} проектов",
  'whp.sku.forProjects_one': "для одного проекта",
  'whp.sku.needBy': "Нужно к {date}",
  'whp.sku.need': "Нужно",
  'whp.sku.gets': "Получает",
  'whp.sku.getsOf': "Сколько получает {project}",
  'whp.sku.toBuy': "{qty} в закупку",
  'whp.sku.hint': "Распределение по первой потребности; можно изменить до подтверждения. Количество на полке записывается как подсчёт.",
  'whp.sku.over': "Распределено {given} — больше, чем на полке ({shelf}).",
  'whp.sku.needQty': "Напишите, сколько на полке, или нажмите «Нет».",
  'whp.sku.failed': "Сохранено не во всех проектах — {list}",
  'whp.sku.noneDone': "Нет на складе: {name} → закупка может заказать (проектов: {n})",
  'whp.sku.haveDone': "Есть на складе: {name} ×{qty} распределено на проектов: {n}",
  'whp.sku.haveDone_few': "Есть на складе: {name} ×{qty} распределено на {n} проекта",
  'whp.sku.haveDone_many': "Есть на складе: {name} ×{qty} распределено на {n} проектов",
  'whp.sku.haveDone_one': "Есть на складе: {name} ×{qty} для одного проекта",
  'whp.sku.shortDone': "остаток ({qty}) ушёл в закупку",
  'whp.sku.confirm': "Подтвердить — выдать потом",
  'whp.sku.confirmPart': "Подтвердить — остаток в закупку",
  'whp.sku.allBuy': "Нет — всё в закупку",
  'whp.dock.more': "Ещё",
  'whp.issued.open': "Открыть",
  'whp.issued.close': "Закрыть",
  'whp.nav.today': "Сегодня",
  'whp.nav.kits': "Комплекты",
  'whp.nav.projects': "Проекты",
  'whp.nav.recv': "Приёмка",
  'whp.nav.stock': "Запасы",
  'whp.nav.clean': "Навести порядок",
  'whp.nav.pick': "Сборка",
  'whp.nav.count': "Подсчёт",
  'whp.nav.desk': "Выдача",
  'whp.scanAll': "Сканируйте: товар, поставщика, ячейку, проект или карточку",
  'whp.scanMany': "Найдено {n} с «{v}» — показано первое. Уточните запрос.",
  'whp.searchResults': "Результаты поиска",
  'whp.searchStock': "Товары на складе",
  'whp.searchWork': "Открытая работа",
  'whp.searchNoStock': "Нет товара с «{v}».",
  'whp.notCounted': "Не считан",
  'whp.kind.ask': "Есть на складе?",
  'whp.kind.issue': "Комплект",
  'whp.kind.receive': "Приёмка",
  'whp.requests': "Запросы вам",
  'whp.answer': "Ответить",
  'whp.toIssue': "Выдать — ждёт в ячейке",
  'whp.issue': "Выдать",
  'whp.toPlace': "Положить в ячейку",
  'whp.place': "В ячейку",
  'whp.linesInCell': "Строк в ячейке: {n}",
  'whp.linesToPlace': "Пришло, ещё не в ячейке: {n}",
  'whp.linesN': "Строк: {n}",
  'whp.all': "Все",
  'whp.upcoming': "Скоро в производство",
  'whp.inDays': "через {n} раб. дн.",
  'whp.todayWord': "сегодня",
  'whp.overdueRelease': "просрочено на {n} раб. дн.",
  'whp.noList': "Нет списка закупки — {who} получил напоминание",
  'whp.listState': "На заводе: {n}, не заказано: {order}",
  'whp.engineer': "инженер",
  'whp.check': "Проверить",
  'whp.see': "Открыть",
  'whp.ask': "Есть на складе?",
  'whp.askSub': "Строка на товар. Один ответ — первым получает тот, кому нужно раньше.",
  'whp.askRest': "Без артикула или с заменой — по проектам",
  'whp.skuNeed': "Нужно {qty} для {projects}",
  'whp.nothing': "Сейчас на складе нет открытой работы.",
  'whp.field': "Выездные комплекты — до вечера накануне",
  'whp.leaves': "Выезд {date} {time}",
  'whp.fieldMissing': "Не хватает {n}: {names}",
  'whp.fieldReady': "Всё собрано",
  'whp.arriving': "Поступления — по поставщикам",
  'whp.toReceive': "В приёмку",
  'whp.noSupplier': "Поставщик не указан",
  'whp.supLines': "Строк: {n}",
  'whp.lateN': "С опозданием: {n}",
  'whp.receive': "Принять",
  'whp.waiting': "Ждёт кого-то другого",
  'whp.small': "Мелочи на сегодня",
  'whp.countToday': "Подсчёт на сегодня: {n}",
  'whp.countDone': "Сегодня подсчитано {n} из {of}",
  'whp.start': "Начать",
  'whp.belowMin': "Ниже минимума: {n}",
  'whp.cleanN': "Навести порядок: {n}",
  'whp.doneToday': "Сделано сегодня: {n}",
  'whp.doneTitle': "Сделано сегодня на складе",
  'whp.belowTitle': "Ниже минимума",
  'whp.belowSub': "На складе {qty}, минимум {min}",
  'whp.restock': "Заказать на склад",
  'whp.w.subOut': "Отправить субподрядчику: {name}",
  'whp.w.left': "Вышло",
  'whp.w.subAt': "У {name}",
  'whp.w.back': "Вернулось",
  'whp.w.sub': "Замена у инженера: {name}",
  'whp.w.qa': "Входной контроль: {name}",
  'whp.w.ret': "Вернуть поставщику: {name} ×{qty}",
  'whp.w.collected': "Забрали",
  'whp.w.retSub': "Забраковано, не на складе · ×{qty}",
  'whp.kitTitle': "Комплект {n}",
  'whp.noKits': "Все проекты закрыты. Никто не ждёт склад.",
  'whp.proj.search': "Поиск проекта по номеру или названию",
  'whp.proj.none': "Такого проекта нет. Проверьте номер или попробуйте другое слово.",
  'whp.proj.pick': "Выберите проект из списка, чтобы увидеть все его закупки.",
  'whp.proj.loadFail': "Не удалось загрузить проекты. Обновите страницу.",
  'whp.proj.open': "Открытых строк на складе: {n}",
  'whp.proj.clear': "Нет открытых строк на складе",
  'whp.proj.issue': "Выдать на проект",
  'whp.proj.ask': "Проверка наличия ({n})",
  'whp.proj.kit': "Открыть комплект",
  'whp.proj.back': "Назад к списку",
  'whp.proj.work': "Есть работа для склада",
  'whp.proj.found': "Найдено",
  'whp.proj.noWork': "Сейчас ни один проект не ждёт склад. Любой другой проект есть в поиске.",
  'whp.proj.rest': "Остальные проекты есть в поиске.",
  'whp.proj.allData': "Все данные закупки",
  'whp.proj.print': "Список закупки для печати",
  'whp.proj.lines': "Назад к таблице",
  'whp.hist.filter': "Фильтр по статусу",
  'whp.hist.f.wait': "Ждёт ответа",
  'whp.hist.f.open': "Открыто",
  'whp.hist.f.done': "Выдано",
  'whp.hist.f.all': "Все",
  'whp.hist.state': "Статус",
  'whp.hist.waits': "Ждёт ответа",
  'whp.hist.done': "Выдано",
  'whp.hist.what': "Что произошло на самом деле?",
  'whp.hist.full': "Полная история",
  'whp.hist.title': "История строк",
  'whp.hist.asked': "Запросил",
  'whp.hist.ordered': "Заказал",
  'whp.hist.issued': "Выдал",
  'whp.hist.notYet': "Ещё нет",
  'whp.hist.unknown': "Не записано кто",
  'whp.hist.empty': "В этом проекте нет строк закупки.",
  'whp.hist.loadFail': "Не удалось загрузить историю. Обновите страницу.",
  'whp.proj.waits': "Ждут ответа: {n}",
  'whp.proj.waitsAct': "Ответить по строкам: {n}",
  'whp.issued.title': "Закупки ждут ответа: строк с отметкой «выдано»: {n}",
  'whp.issued.sub': "Эти строки закрыты без проверки наличия. Проверьте полку и ответьте по каждой строке, что произошло на самом деле.",
  'whp.issued.when': "Отмечено {at} {by}",
  'whp.issued.yes': "Выдано на самом деле",
  'whp.issued.have': "Не выдано, есть на складе",
  'whp.issued.none': "Не выдано, нет",
  'whp.issued.doneISSUED': "{name}: записано как выданное",
  'whp.issued.doneHAVE': "{name}: возвращено в список на выдачу",
  'whp.issued.doneNONE': "{name}: передано в закупки",
  'whp.kitViewAria': "Вид комплектов",
  'whp.kitQueue': "Очередь",
  'whp.kitTable': "Таблица",
  'whp.printDay': "Печать списка дня",
  'whp.showUnreleased': "Показать ещё не выпущенные: {n}",
  'whp.hideUnreleased': "Скрыть ещё не выпущенные: {n}",
  'date.pick': "Выбрать дату в календаре",
  'date.bad': "Неверная дата — введите день/месяц/год, например 31/10/2026.",
  'date.badRange': "Дата вне допустимого диапазона или неверна — введите день/месяц/год.",
  'kitOrder.title': "Порядок комплектов",
  'kitOrder.hint': "Этот порядок видит склад. То, что не сдвинуто, идёт по дате; новый проект встаёт по своей дате.",
  'kitOrder.notInQueue': "У {n} сейчас нет открытого комплекта — упорядочивать нечего.",
  'kitOrder.loading': "Загрузка очереди комплектов…",
  'kitOrder.loadError': "Очередь комплектов не загрузилась.",
  'kitOrder.retry': "Повторить",
  'kitOrder.empty': "Открытых комплектов нет.",
  'kitOrder.saveFailed': "Порядок не сохранён — повторите.",
  'kitOrder.need': "Нужно к {d}",
  'kitOrder.noDate': "Без даты",
  'kitOrder.ready': "Готово {n}",
  'kitOrder.missing': "Не хватает {n}",
  'kitOrder.byDate': "По дате",
  'kitOrder.top': "{n} в начало очереди",
  'kitOrder.up': "{n} на одно место выше",
  'kitOrder.down': "{n} на одно место ниже",
  'whq.saveFailed': "Действие не сохранено. Повторите; если повторится — сообщите кнопкой «Сообщить о неисправности».",
  'whq.lastCount': "Последний пересчёт {d}",
  'whq.lastCountNever': "Последний пересчёт: никогда",
  'whq.replacement': "Замена",
  'whq.noSku': "Без артикула",
  'whq.oneLabel': "1 этикетка",
  'whq.nLabels': "Этикеток: {n} + этикетка комплекта",
  'whq.serialRequired': "Для этих позиций обязателен серийный номер на каждую единицу — сканируйте или введите, затем «Выдать».",
  'whq.serialOf': "Серийный {i}: {name}",
  'whq.writeHowMany': "Укажите, сколько на полке, или нажмите «Нет».",
  'whq.shortToBuy': "Недостача ({q}) передана в закупку",
  'whq.issuedLine': "Выдано {name} ×{q} → {p}",
  'whq.haveLine': "В наличии: {name} ×{q} → «К выдаче» {p}",
  'whq.noneLine': "Нет на складе: {name} → закупка может заказывать",
  'whq.zeroLine': "В «{name}» указан 0 — нажмите там «Нет» или исправьте количество.",
  'whq.lines_one': "{n} строка",
  'whq.lines_many': "{n} строк",
  'whq.lines_few': "{n} строки",
  'whq.noneAll': "Нет на складе: {lines} {p} → закупка может заказывать",
  'whq.haveAll': "В наличии: {lines} {p} → «К выдаче»",
  'whq.shortN': "В строках ({n}) недостача передана в закупку",
  'whq.savedOf': "Сохранено {done} из {total}.",
  'whq.answerAll': "Ответить всем ({n}):",
  'whq.yes': "Есть",
  'whq.no': "Нет",
  'whq.savingAnswers': "Сохраняем ответы…",
  'whq.answerHint': "Сначала ответьте на исключения в строке (другое количество, замена) — потом один ответ для остальных.",
  'whq.confirmHave': "Отметить «Есть» в строках ({n}) с количеством из каждой строки?",
  'whq.confirmNone': "Отметить «Нет» в строках ({n})? Закупка закажет всё.",
  'whq.yesHaveAll': "Да, есть на всё",
  'whq.yesNoneAll': "Да, нет ничего",
  'whq.required': "Требуется",
  'whq.subProposed': "Предложена замена",
  'whq.waitsEngineer': "ждёт инженера",
  'whq.onShelf': "На полке",
  'whq.issueNow': "Выдать сейчас",
  'whq.issueLater': "Есть — выдать позже",
  'whq.other': "Есть другое",
  'whq.countNote': "Указанное количество записывается и как пересчёт. Меньше нужного? Укажите сколько есть — недостача уйдёт в закупку.",
  'whq.notInList': "«{v}» нет в списке {p} — сюда не выдавать.",
  'whq.alreadyScanned': "{name} — уже отсканировано ({lines}).",
  'whq.fromShelf': "с {loc}",
  'whq.scanItem': "Сканируйте позицию",
  'whq.scanPick': "Сканируйте позицию для комплектации — {p}",
  'whq.scanned': "Отсканировано",
  'whq.clearAll': "Снять всё",
  'whq.pickAll': "Отметить всё (без сканирования)",
  'whq.toIssue': "Выдать {name}",
  'whq.noLocation': "Нет места",
  'whq.boxSwap': "Коробка за коробку",
  'whq.toStock': "Перевести на склад",
  'whq.labelsNote': "Этикетка на строку и этикетка комплекта, если строк больше одной.",
  'whq.issuedLines': "Выдано строк: {n} → {p}",
  'whq.issueWithLabels': "Выдать {n} + этикетки",
  'whq.whereTo': "Куда положить",
  'whq.whereToQ': "Куда положить?",
  'whq.scanShelf': "Сканируйте полку",
  'whq.wentToStock': "Принято на склад",
  'whq.issuedTo': "Выдано на {p}",
  'whq.waitsIssue': "Ждёт в «К выдаче» {p}",
  'whq.receivedFrom': "Принято от {s}: {lines}",
  'whq.supplier': "поставщик",
  'whq.partNotIssued': "Часть не выдана (нет серийного или приёмка частичная) — она в «К выдаче»",
  'whq.arrived': "Пришло",
  'whq.stateOf': "Состояние: {name}",
  'whq.ok': "Исправно",
  'whq.notOk': "Неисправно",
  'whq.deliveryNoteOpt': "Накладная (необязательно)",
  'whq.deliveryNote': "Накладная",
  'whq.receiveHint': "«Принять и выдать» сразу печатает этикетку. «Только принять» переводит в «К выдаче» {p}.",
  'whq.receiveHintStock': "Заказано на склад: приходует при приёмке, выдавать нечего.",
  'whq.receiveIssue': "Принять и выдать",
  'whq.receiveOnly': "Только принять",
  'whq.receiveStock': "Принять на склад",
  'whq.finish': "Готово",
  'whq.act.have': "Есть на складе",
  'whq.act.none': "Нет",
  'whq.act.issue': "Выдать",
  'whq.act.receive': "Пришло",
  'whq.act.undoIssue': "Отменить выдачу",
  'whq.act.return': "Возврат на склад",
  'whq.closeAct': "Закрыть: {a}",
  'whq.saving': "Сохраняется…",
  'whq.moreOn': "Ещё действия: {name}",
  'whq.undoneIssue': "Выдача отменена: {name} — вернулось в «К выдаче»",
  'whq.barcodeUnknown': "Штрихкод неизвестен. Введите название в поле «Позиция» или привяжите штрихкод к артикулу в карточке позиции.",
  'whq.scanOrName': "Сканируйте позицию или введите название.",
  'whq.qtyAboveZero': "Укажите количество больше нуля.",
  'whq.pickProject': "Выберите проект — стоимость записывается на проект.",
  'whq.deskIssued': "Выдача без запроса: {name} ×{q} → {p} (вне плана)",
  'whq.deskReturned': "Возврат на склад: {name} ×{q}",
  'whq.deskIssueHint': "Рабочий подошёл к стойке и просит. Записывается «вне плана».",
  'whq.deskReturnHint': "То, что вернулось с участка. Приходует на склад.",
  'whq.itemBarcode': "Штрихкод позиции",
  'whq.item': "Позиция",
  'whq.itemName': "Название позиции",
  'whq.qty': "Количество",
  'whq.inSystem': "В системе",
  'whq.unverified': "(не проверено)",
  'whq.locationIs': "Место: {loc}",
  'whq.project': "Проект",
  'whq.whichProject': "Какой проект?",
  'whq.clearWorker': "Убрать рабочего",
  'whq.scanBadgeOpt': "Сканируйте карточку рабочего (необязательно)",
  'whq.badge': "Карточка рабочего",
  'whq.returnToStock': "Вернуть на склад",
  'whq.serialEach': "Серийный номер на каждую единицу ({label}):",
  'whq.serialN': "Серийный {i}",
  'whq.photo': "Фото: {name}",
  'whq.subSkuReq': "Найдите или отсканируйте позицию, либо опишите её словами.",
  'whq.subFind': "Поиск по описанию или артикулу, или сканирование",
  'whq.subNotFound': "Нет в каталоге. Можно оставить описание и добавить фото.",
  'whq.subChange': "Выбрать другую позицию",
  'whq.subPhotoOn': "Фото есть, заменить",
  'whq.subNoteReq': "Напишите, почему подходит — инженер решает по этому.",
  'whq.subSent': "Предложена замена для {name} ({p}) — ждёт инженера проекта или начальника инженерии",
  'whq.subAsk': "Есть на складе что-то другое вместо {name} ×{q}?",
  'whq.subSkuPh': "Артикул на складе (сканируйте)",
  'whq.subSku': "Артикул замены",
  'whq.subWhyPh': "Почему подходит (обязательно)",
  'whq.subWhy': "Почему подходит",
  'whq.takePhotoOpt': "Сфотографировать (необязательно)",
  'whq.sendApproval': "Отправить на утверждение",
  'whq.subNote': "Письмо и уведомление инженеру проекта и начальнику инженерии — достаточно одного. До ответа строка не заказывается. Утверждено → уходит в «К выдаче».",
  'whq.repOrdered': "поставщик шлёт замену (в «К приёмке»)",
  'whq.repSends': "поставщик шлёт замену",
  'whq.pickupWaits': "ждёт вывоза («Вернуть поставщику»)",
  'whq.buyerDecides': "решает закупка",
  'whq.rejectedDone': "{name} забраковано — {what}",
  'whq.rejectSaved': "Записано по браку {name}. Закупка уведомлена.",
  'whq.dontKnow': "Не знаю — решает закупка",
  'whq.waitPickup': "Ждёт вывоза",
  'whq.rejectAsk': "{name} забраковано — не идёт ни на склад, ни в проект. Что сказал {s}?",
  'whq.theSupplier': "поставщик",
  'whq.supplierAnswer': "Ответ поставщика",
  'whq.whenArrive': "Когда придёт",
  'whq.saidPh': "Что сказал поставщик (необязательно)",
  'whq.said': "Что сказал поставщик",
  'whq.photoDefect': "Сфотографировать дефект",
  'whq.rejectNote': "Заявка на замену уже открыта автоматически. «Шлёт замену» заказывает её у того же поставщика. Кредит и отмена — в закупке.",
  'whq.pickedUp': "{name} забрал {s}",
  'whq.pickedBtn': "Забрали",
  'whq.tostock.SURPLUS': "Излишек",
  'whq.tostock.NOT_NEEDED': "Не нужно",
  'whq.tostock.REPLACED': "Заменено",
  'whq.tostock.PROJECT_CANCELLED': "Проект отменён",
  'whq.pickReason': "Выберите причину.",
  'whq.movedToStock': "{name} ×{q} переведено с {p} на склад",
  'whq.stillNeeds': "проекту ещё нужно — вернулось в «Есть на складе?»",
  'whq.toStockTitle': "На склад: {name} ×{q}",
  'whq.reason': "Причина",
  'whq.reasonPick': "Причина…",
  'whq.noteOpt': "Примечание (необязательно)",
  'whq.note': "Примечание",
  'whq.stillNeedsShort': "Проекту ещё нужно",
  'whq.move': "Перевести",
  'whq.toStockNote': "Инженер проекта и закупка получат уведомление. Количество уже на складе с приёмки.",
  'whq.undo.receipt': "Отменить приёмку",
  'whq.undo.issue': "Отменить выдачу",
  'whq.undo.answer': "Отменить ответ",
  'whq.whyCancel': "Напишите причину отмены.",
  'whq.whyCancelPh': "Причина отмены (обязательно, пишется в журнал)",
  'whq.cancelReason': "Причина отмены",
  'whq.undoneAnswer': "Ответ отменён — строка вернулась в «Есть на складе?»",
  'whq.undoneSome': "Отменено {done} из {total}. Остальные: {err}",
  'whq.doneToday': "Сделано сегодня на складе:",
  'whq.listFailed': "Список не загрузился — обновите страницу.",
  'whq.tool.all': "Все",
  'whq.tool.in': "На складе",
  'whq.tool.temp': "Временно",
  'whq.tool.perm': "Постоянно",
  'whq.tool.late': "Не возвращён",
  'whq.tool.damaged': "Повреждён",
  'whq.tool.lost': "Утерян",
  'whq.since': "с {d}",
  'whq.jobEnded': "Работа завершена {d}",
  'whq.daysLate_one': "опоздание {n} день",
  'whq.daysLate_many': "опоздание {n} дней",
  'whq.daysLate_few': "опоздание {n} дня",
  'whq.takenToday': "Взят сегодня {t}",
  'whq.taken': "Взят {d}",
  'whq.days_one': "{n} день",
  'whq.days_many': "{n} дней",
  'whq.days_few': "{n} дня",
  'whq.serialShort': "S/N {s}",
  'whq.noSerialOld': "Без серийного номера · старый инструмент",
  'whq.warrantyTo': "Гарантия до {d}",
  'whq.warrantyOver': "Гарантия истекла {d}",
  'whq.printLabel': "Печать этикетки",
  'whq.backInUse': "{name} — снова в работе",
  'whq.toolFilter': "Фильтр инструментов",
  'whq.searchName': "Поиск по названию",
  'whq.searchTool': "Поиск инструмента по названию",
  'whq.newTool': "+ Новый инструмент",
  'whq.yourTools': "Инструменты у вас. Выдача и возврат — на стойке склада.",
  'whq.loading': "Загрузка…",
  'whq.toolsFailed': "Список инструментов не загрузился. Обновите страницу; если повторится — сообщите о неисправности.",
  'whq.noToolName': "Инструмента с таким названием нет. Старый, ещё не записанный — «+ Новый инструмент» ← старый инструмент.",
  'whq.noTools': "Инструментов нет.",
  'whq.stickerN': "Этикетка {s}",
  'whq.invoice': "Счёт",
  'whq.toFix': "В ремонт или по гарантии поставщика",
  'whq.shelfN': "Полка {s}",
  'whq.return': "Возврат",
  'whq.issueBtn': "Выдача",
  'whq.fixedBack': "Починен, снова в работе",
  'whq.foundBack': "Найден, снова в работе",
  'whq.backOk': "{name} — возвращён на склад",
  'whq.backDamaged': "{name} — возвращён повреждённым, выведен из работы. Руководитель уведомлён.",
  'whq.backLost': "{name} — отмечен «Утерян». Руководитель уведомлён.",
  'whq.returnState': "Состояние при возврате",
  'whq.defectQ': "Какой дефект? (необязательно)",
  'whq.lostQ': "Где потерян? (необязательно)",
  'whq.returnNote': "Примечание к возврату",
  'whq.returnBtn': "Вернуть",
  'whq.badBadge': "Это не известная карточка рабочего. Сканируйте его бейдж или проверьте на экране рабочих, что он активен.",
  'whq.scanBadge': "Сканируйте карточку рабочего",
  'whq.continue': "Далее",
  'whq.toolsOf': "Инструменты — {name}",
  'whq.badgeN': "Карточка {n}",
  'whq.lateN': "С опозданием: {n}",
  'whq.hasNow': "Сейчас у него",
  'whq.hasNone': "У него нет инструментов со склада.",
  'whq.oldTool': "Старый инструмент",
  'whq.pickTool': "Выберите инструмент — сканируйте этикетку или найдите по названию.",
  'whq.tempJob': "Выберите выездную работу или перейдите на «Проект».",
  'whq.tempProject': "Выберите проект: найдите по номеру или названию.",
  'whq.tool': "Инструмент",
  'whq.clearTool': "Убрать инструмент",
  'whq.scanOrSearch': "Сканируйте этикетку или ищите по названию",
  'whq.dest.internal': "Внутреннее",
  'whq.pickDest': "Выберите, куда идёт инструмент: проект, внутреннее или выездная работа.",
  'whq.findProject': "Найти проект по номеру или названию",
  'whq.noProject': "Такого открытого проекта нет. Проверьте номер или выберите «Внутреннее».",
  'whq.clearProject': "Очистить проект",
  'whq.issuedBy': "Выдал: {name}",
  'whq.unknownTool': "Наклейка {s} не зарегистрирована. Проверьте номер или добавьте инструмент: «+ новый инструмент» на вкладке «Инструменты».",
  'whq.toolNotYours': "Инструмент {s} не числится за вами. Обратитесь на склад.",
  'whq.notTool': "Это неизвестная наклейка инструмента. Сканируйте наклейку или найдите инструмент по названию.",
  'whq.noMatch': "Не найдено. Проверьте написание или сканируйте.",
  'whq.scanBadgeOrName': "Сканируйте карточку рабочего или найдите по имени",
  'whq.scanBadgeToolOrName': "Сканируйте карточку или наклейку либо найдите по имени",
  'whq.blockedOut': "{name} у {who} с {d} · {dest}.",
  'whq.blockedDamaged': "{name} повреждён и выведен из работы. После ремонта нажмите «Отремонтирован, снова в работе».",
  'whq.blockedLost': "{name} отмечен как утерянный. Если нашёлся, нажмите «Найден, снова в работе».",
  'whq.alreadyHis': "{name} уже у {who}.",
  'whq.moveTo': "Передать: {name}",
  'whq.movedNote': "Передан: {name}",
  'whq.openHolder': "Открыть панель: {who}",
  'whq.camera': "Камера",
  'whq.projectTools': "Инструменты на проекте",
  'whq.kind': "Тип",
  'whq.issueKind': "Тип выдачи",
  'whq.whereReq': "Куда (обязательно)",
  'whq.destination': "Назначение",
  'whq.fieldJob': "Выездная работа",
  'whq.job': "Работа",
  'whq.noJobs': "У него нет открытых выездных работ. Выберите «Проект» или «Внутреннее».",
  'whq.noFreeTool': "Свободного инструмента с таким названием нет.",
  'whq.toolNameReq': "Введите название инструмента.",
  'whq.toolSaved': "{name} записан",
  'whq.ifKnown': "Если известно",
  'whq.newToolName': "Новый инструмент",
  'whq.newToolHint': "Новый инструмент — все поля обязательны",
  'whq.oldToolHint': "Старый инструмент — обязательно только название",
  'whq.toolName': "Название инструмента",
  'whq.makerModel': "Производитель и модель",
  'whq.serialNumber': "Серийный номер",
  'whq.supplierCap': "Поставщик",
  'whq.purchaseDate': "Дата покупки",
  'whq.warrantyMonths': "Гарантия (месяцев)",
  'whq.until': "до {d}",
  'whq.invoiceDoc': "Счёт / накладная",
  'whq.shelf': "Полка",
  'whq.savePrint': "Сохранить и напечатать этикетку",
  'whq.oldToolLabel': "Этикетка для старого инструмента — по желанию. Без неё его находят поиском по названию.",
  'whq.inStockNoReturn': "{name} на складе — возвращать нечего.",
  'whq.scanReceiver': "{name} — теперь сканируйте карточку рабочего, который его получает.",
  'whq.notRecognized': "Не распознана карточка рабочего или этикетка инструмента. Старый инструмент без этикетки — ищите по названию на вкладке «Инструменты».",
  'whq.scanBadgeDot': "Сканируйте карточку рабочего.",
  'whq.scanBadgeOrTool': "Сканируйте карточку рабочего или этикетку инструмента.",
  'whq.badgeOrTool': "Карточка рабочего или этикетка инструмента",
  'whq.scanBadgeOrToolPh': "Сканируйте карточку рабочего или этикетку инструмента",
  'whp.rankNote': "{n} поднят: {by}, {at}",
  'whp.rankFirst': "Поднять в начало",
  'whp.rankClear': "Вернуть порядок по дате",
  'whp.rankDone': "{n} в начале очереди",
  'whp.rankCleared': "{n} снова по дате",
  'whp.pickHint': "Выберите комплект или сканируйте проект или ячейку.",
  'whp.subProjects': "{n}: подпроекты, у каждого свой комплект",
  'whp.allHere': "Всё на заводе",
  'whp.kitMissing': "Не хватает {n} из {of}",
  'whp.needs': "Нужно",
  'whp.col.project': "Проект",
  'whp.col.name': "Название",
  'whp.col.need': "Нужно",
  'whp.col.plant': "На заводе",
  'whp.col.notOrdered': "Не заказано",
  'whp.col.late': "Опоздание",
  'whp.col.cell': "Ячейка",
  'whp.light.r': "Производство ждёт",
  'whp.light.y': "Не хватает",
  'whp.light.g': "Всё на заводе",
  'whp.none': "Нет.",
  'whp.stockWord': "склад",
  'whp.clean.late': "Срок прошёл, приёмки нет",
  'whp.clean.lateSub': "{sup}, обещано {date}, прошло {days} дн., {project}",
  'whp.clean.arrived': "Пришло",
  'whp.clean.stuck': "Получено, не выдано больше недели",
  'whp.clean.stuckSub': "{project}, {days} дн. на складе",
  'whp.sub.items': "Товары",
  'whp.sub.count': "Подсчёт",
  'whp.sub.loc': "Места",
  'whp.sub.labels': "Этикетки",
  'whp.sub.tools': "Инструменты",
  'whp.desk.issue': "Выдача без запроса",
  'whp.desk.return': "Возврат на склад",
  'whp.desk.unplanned': "Приёмка без заказа",
  'whp.desk.toolIssue': "Выдача инструмента",
  'whp.desk.toolReturn': "Возврат инструмента",
  'whp.cellN': "Ячейка {cell}",
  'whp.noCell': "Нет ячейки",
  'whp.cellOnFirst': "Ячейка откроется с первой строкой",
  'whp.needDate': "Нужно производству {date}",
  'whp.noDate': "Нет даты",
  'whp.notReleased': "ещё не в производстве",
  'whp.needShort': "нужно {date}",
  'whp.firstStation': "Первая станция ({station})",
  'whp.ready': "Готово",
  'whp.missingN': "Не хватает {n}",
  'whp.answerAsk': "Ответить «Есть на складе?» ({n})",
  'whp.placeN': "Положить в ячейку ({n})",
  'whp.issueToWorker': "Выдать ({n})",
  'whp.profiles': "Сводка профилей",
  'whp.moveCell': "В другую ячейку",
  'whp.cellMoved': "{n}: теперь ячейка {cell}",
  'whp.placed_few': "{n} строки проекта {project} в ячейке {cell}",
  'whp.placed_many': "{n} строк проекта {project} в ячейке {cell}",
  'whp.placed_one': "Строка проекта {project} в ячейке {cell}",
  'whp.issuedTo_few': "Выдано {n} строки {project}: {name}",
  'whp.issuedTo_many': "Выдано {n} строк {project}: {name}",
  'whp.issuedTo_one': "Выдана строка {project}: {name}",
  'whp.linesInCell_few': "{n} строки в ячейке",
  'whp.linesInCell_many': "{n} строк в ячейке",
  'whp.linesInCell_one': "Одна строка в ячейке",
  'whp.linesToPlace_few': "{n} строки пришли и ещё не в ячейке",
  'whp.linesToPlace_many': "{n} строк пришли и ещё не в ячейке",
  'whp.linesToPlace_one': "Одна строка пришла, ещё не в ячейке",
  'whp.supLines_few': "{n} строки",
  'whp.supLines_many': "{n} строк",
  'whp.supLines_one': "Одна строка",
  'whp.linesN_few': "{n} строки",
  'whp.linesN_many': "{n} строк",
  'whp.linesN_one': "Одна строка",
  'whp.material': "Что на заводе, а чего нет",
  'whp.counterForProject': "Выдать вне списка",
  'whp.lineMenu': "Действия: {name}",
  'whp.ownerOf': "у: {who}",
  'whp.owner.wh': "склад",
  'whp.owner.saw': "пила",
  'whp.owner.purchase': "закупки",
  'whp.owner.sub': "субподрядчик",
  'whp.owner.supplier': "поставщик, следят закупки",
  'whp.line.inCell': "В ячейке",
  'whp.line.qa': "Ждёт входного контроля",
  'whp.line.qaReject': "Забраковано на контроле",
  'whp.owner.qa': "качество",
  'whp.line.toPlace': "На складе, в ячейку",
  'whp.line.saw': "На складе, ждёт пилу",
  'whp.line.notOrdered': "Не заказано",
  'whp.line.atSub': "У {name}",
  'whp.line.toSub': "Отправить субподрядчику",
  'whp.line.promised': "Обещано {date}",
  'whp.line.noPromise': "Заказано, без даты",
  'whp.placeTitle': "В ячейку: {project}",
  'whp.placeInCell': "Положить {n} в ячейку {cell}, работник заберёт",
  'whp.placeInNew': "Положить {n} в ячейку, работник заберёт",
  'whp.addItem': "Добавить позицию не из списка",
  'whp.addSearch': "Поиск позиции по названию или артикулу",
  'whp.addIssue': "Выдать",
  'whp.addSerial': "Укажите {label} для каждой единицы",
  'whp.proj.find': "Поиск в строках проекта",
  'whp.notAsked': "Этой позиции нет в заявках проекта ({n}). Подтвердить выдачу?",
  'whp.notAskedOk': "Подтвердить и выдать",
  'whp.placeHint': "Сканируйте товар или нажмите на строку.",
  'whp.placed': "{n} строк проекта {project} в ячейке {cell}",
  'whp.scanItem': "Сканируйте товар",
  'whp.scanned': "Сканировано {n} из {of}",
  'whp.markAll': "Отметить всё (без скана)",
  'whp.clearAll': "Снять всё",
  'whp.scanNotInKit': "«{code}» нет в списке {project}, сюда не класть.",
  'whp.scanAgain': "{name}: уже сканировано.",
  'whp.noSku': "Без артикула",
  'whp.issueTitle': "Выдача: {project}",
  'whp.scanBadge': "Работник сканирует карточку",
  'whp.issueNeedsBadge': "Сканируйте карточку, чтобы выдать",
  'whp.issueToName': "Выдать {n}: {name}",
  'whp.changeWorker': "Другой работник",
  'whp.partialHint': "Частично? Снимите строку, она останется на складе.",
  'whp.issuedTo': "Выдано {n} строк {project}: {name}",
  'whp.fact.qty': "Количество",
  'whp.fact.shelf': "Полка",
  'whp.fact.requested': "Кто запросил",
  'whp.fact.supplier': "Поставщик",
  'whp.fact.note': "Примечание",
  'whp.lineNote': "Примечание к строке — прочитать перед выдачей:",
  'whp.fact.changed': "Последнее изменение",
  'whp.placeOne': "Положить в ячейку",
  'whp.placedOne': "{name} в ячейке",
  'whp.removeOne': "Убрать из ячейки",
  'whp.removedOne': "{name} убрано из ячейки",
  'whp.receiveNow': "Пришло, принять",
  'whp.subOut': "Вышло к субподрядчику",
  'whp.subOutDone': "Записано: вышло к {name}",
  'whp.subBack': "Вернулось, принять",
  'whp.subOutUndo': "Отменить выход",
  'whp.subOutUndone': "Выход отменён",
  'whp.toStock': "Перевести на склад",
  'whp.other': "Есть другое",
  'whp.subWaits': "Предложена замена: {name}, ждёт инженера",
  'whp.countAllDone': "На сегодня всё: подсчитано {n}.",
  'whp.countNeedQty': "Введите количество, от 0.",
  'whp.countMatch': "{name}: совпадает",
  'whp.countDiff': "{name}: {d} к системе, запас обновлён",
  'whp.countMoved': "Ожидавшие заявки: {issue} к выдаче, {buy} в закупку",
  'whp.countStep': "Товар {n} из {of}",
  'whp.neededSoon': "скоро понадобится проекту",
  'whp.countBoxes': "Сколько полных коробок?",
  'whp.countQty': "Сколько насчитали?",
  'whp.countShelf': "Где это? Сканируйте полку",
  'whp.countSave': "Сохранить, дальше",
  'whp.countSkip': "Не нашёл, пропустить",

  // צילום מהטלפון ב-QR (אלי 01/11/2026).
  'plink.button': "Снять с телефона",
  'plink.title': "Съёмка с телефона",
  'plink.scan': "Отсканируйте код камерой телефона и сделайте снимок. Фото появится здесь.",
  'plink.valid10': "Действует 10 минут",
  'plink.qrAlt': "QR-код для съёмки с телефона",
  'plink.creating': "Создаём код…",
  'plink.createFailed': "Код не создан. Нажмите «Новый код» и повторите.",
  'plink.expired': "Срок действия кода истёк. Создайте новый код, чтобы продолжить съёмку.",
  'plink.newCode': "Новый код",
  'plink.received': "Получено {n} фото",
  'plink.received_one': "Получено одно фото",
  'plink.received_few': "Получено {n} фото",
  'plink.received_many': "Получено {n} фото",
  'plink.loading': "Проверяем ссылку…",
  'plink.take': "Снять",
  'plink.takeMore': "Снять ещё",
  'plink.preparing': "Готовим фото…",
  'plink.uploading': "Загрузка… {pct}%",
  'plink.saved': "Фото сохранено",
  'plink.usedUp': "Лимит фото для этого кода исчерпан. Для новых фото отсканируйте новый код с компьютера.",
  'plink.gone': "Эта ссылка больше не действует. Отсканируйте новый код с компьютера.",
  'plink.badType': "Это не фото JPG, PNG или WebP. Снимите ещё раз камерой или выберите другое фото.",
  'plink.tooBig': "Фото больше 5 МБ. Снимите ещё раз или выберите фото меньше.",
  'plink.slow': "Слишком много попыток подряд. Подождите несколько минут и повторите.",
  'plink.failed': "Фото не сохранено. Повторите; если ошибка вернётся, отсканируйте новый код с компьютера.",
  'plink.network': "Нет связи с сервером. Проверьте сеть на телефоне и повторите.",
  'plink.kind.FAULT': "Фото к несоответствию",
  'plink.kind.GAP': "Фото к замечанию",
  'plink.kind.SERVICE_JOB': "Фото к выездной работе",
  'plink.kind.SITE_PHOTO': "Фото объекта",
  'plink.kind.STOCK_ITEM': "Фото позиции на складе",
  'plink.kind.SHELF_CARD': "Фото для карточки полки",
  'plink.kind.PART': "Фото артикула",
  'plink.kind.PROJECT_IMAGE': "Фото проекта",
  'plink.kind.ORDER_IMAGE': "Фото к заказу",
  'plink.kind.EMPLOYEE': "Фото работника",
  'plink.kind.USER': "Фото пользователя",
  'plink.kind.CATALOG_ITEM': "Фото позиции каталога",
  'plink.kind.CATALOG_FOLDER': "Фото папки каталога",
  'plink.kind.RETURN_PHOTO': "Фото к строке закупки",
  'plink.kind.EXPENSE_RECEIPT': "Фото чека",
  'plink.kind.DELIVERY_PROOF': "Фото накладной",
  'plink.kind.SUBSTITUTE_PHOTO': "Фото замены",
  'plink.kind.CUSTOMER_EVIDENCE': "Фото подтверждения клиента",
  'plink.kind.APPROVAL_EVIDENCE': "Фото подтверждения клиента",
  'plink.savedTitle': "Сохранено",
  'plink.savedHint': "Теперь можно добавить фото с телефона.",
  // ── Лист сборки + киоск сборки (02/11/2026) ──
  'asm.sheetButton': "Лист сборки для печати",
  'asm.sheetFail': "Не удалось подготовить лист сборки — попробуйте ещё раз; если повторится, обратитесь к администратору.",
  'asm.popupBlocked': "Браузер заблокировал окно печати — разрешите всплывающие окна для этого сайта и попробуйте снова.",
  'asm.sheet.customer': "Заказчик",
  'asm.sheet.devEng': "Инженер-конструктор",
  'asm.sheet.name': "Название проекта",
  'asm.sheet.delivery': "Сдача",
  'asm.sheet.plannedShort': "плановая",
  'asm.sheet.prodEng': "Инженер-технолог",
  'asm.sheet.delivered': "Сдано",
  'asm.sheet.planned': "Плановая сдача",
  'asm.sheet.scan': "Сканируйте для сборки",
  'asm.sheet.project': "Проект",
  'asm.sheet.title': "Лист сборки",
  'asm.sheet.noImage': "Нет фото проекта",
  'asm.title': "Сборка",
  'asm.scanBadge': "Отсканируйте пропуск, чтобы начать сборку",
  'asm.badgeHint': "или введите номер пропуска",
  'asm.badgePlaceholder': "Номер пропуска",
  'asm.loading': "Загрузка…",
  'asm.loadFail': "Страница не загрузилась — проверьте, что вы в сети завода, и попробуйте снова.",
  'asm.drawings': "Чертежи для сборки",
  'asm.noDrawings': "В проекте нет чертежей с этапом сборки — обратитесь к инженеру-технологу.",
  'asm.notInModel': "Нет в модели",
  'asm.assembled': "Собрано",
  'asm.mark': "Собрано",
  'asm.markN': "Собрано ({n})",
  'asm.pick': "Выберите чертёж в списке или в модели и нажмите «Собрано».",
  'asm.openDrawing': "Чертёж",
  'asm.openParent': "Сборочный чертёж",
  'asm.finish': "Закончить",
  'asm.finished': "Учёт сборки закрыт. Отсканируйте снова, чтобы продолжить.",
  'asm.since': "На сборке с {t}",
  'asm.marked': "Записано: {part} — {min} мин",
  'asm.missing': "Внимание — не хватает деталей: {list}",
  'asm.stillHere': "Вы ещё здесь?",
  'asm.stillHereYes': "Да, я здесь",
  'asm.stillHereClose': "Без ответа учёт сборки закроется через {m} мин.",
  'asm.stillHereShift': "Время продолжает идти. Нажмите, чтобы подтвердить, что вы ещё собираете.",
  'asm.no3d': "У проекта нет 3D-модели — работайте по списку.",
  'asm.needCode': "Нужен код мастера — попросите начальника производства ввести его здесь.",
  'asm.managerCode': "Код мастера",
  'asm.send': "Подтвердить",
  'asm.cancel': "Отмена",
  'asm.progress': "Собрано {done} из {all}",
  'asm.otherWorker': "Другой работник",
  'asm.todo': "К сборке",
  'asm.doneList': "Собрано",
  'asm.qty': "×{n}",
  'asm.myMarks': "Отмечено сегодня",
  'err.asmSheetUnknown': "Этот лист не распознан — распечатайте новый лист сборки из карточки проекта.",
  'err.asmStationClockOpen': "У вас открыт учёт на станции по {partNumber} — закончите его на станции и отсканируйте здесь снова.",
  'err.asmAlreadyAssembled': "{partNumber} уже отмечен как собранный — выберите другой чертёж.",
  'err.asmNotAssemblyDrawing': "У этого чертежа нет этапа сборки в проекте — выберите чертёж из списка.",
  'err.asmSessionEnded': "Учёт сборки уже закрыт — отсканируйте пропуск снова, чтобы открыть новый.",
  // ── „הבוקר" של מנהל הייצור (03/11/2026) ──
  'morning.title': 'Утро',
  'morning.weekUntil': 'Неделя — до {date}',
  'morning.whatsNew': 'Что нового',
  'morning.weekView': 'Вид недели →',
  'morning.updates': 'новых чертежей, которые вы не видели, в проектах в производстве',
  'morning.updates_one': 'новый чертёж, который вы не видели, в проектах в производстве',
  'morning.updates_few': 'новых чертежа, которые вы не видели, в проектах в производстве',
  'morning.updates_many': 'новых чертежей, которые вы не видели, в проектах в производстве',
  'morning.updatesNc': '{n} после несоответствия',
  'morning.updatesNc_one': '{n} после несоответствия',
  'morning.updatesNc_few': '{n} после несоответствия',
  'morning.updatesNc_many': '{n} после несоответствия',
  'morning.updatesOpen': 'Открыть обновления производства',
  'morning.ship': 'Поставки на неделе',
  'morning.shipEmpty': 'Нет просроченных поставок и поставок на ближайшую неделю.',
  'morning.prod': 'Окончание производства на неделе',
  'morning.prodEmpty': 'Нет проектов, которые должны закончить производство на ближайшей неделе.',
  'morning.dateOnly': 'и ещё {n} проектов, которые не начаты — только дата, без деталей. На доске →',
  'morning.dateOnly_one': 'и ещё {n} проект, который не начат — только дата, без деталей. На доске →',
  'morning.dateOnly_few': 'и ещё {n} проекта, которые не начаты — только дата, без деталей. На доске →',
  'morning.dateOnly_many': 'и ещё {n} проектов, которые не начаты — только дата, без деталей. На доске →',
  'morning.lateCount': '{n} с опозданием',
  'morning.stuck': 'Что тормозит неделю',
  'morning.stuckEmpty': 'Ничто не тормозит проекты этой недели.',
  'morning.projects': '{n} проектов',
  'morning.projects_one': '{n} проект',
  'morning.projects_few': '{n} проекта',
  'morning.projects_many': '{n} проектов',
  'morning.moreInProject': 'ещё {n} в проекте',
  'morning.open': 'Открыть',
  'morning.decisions': 'Ждёт моего решения',
  'morning.decisionsEmpty': 'Нет решений, ожидающих вас.',
  'morning.hours': 'Часы на подпись',
  'morning.hoursAuto': 'учётов закрыты без отчёта',
  'morning.hoursAuto_one': 'учёт закрыт без отчёта',
  'morning.hoursAuto_few': 'учёта закрыты без отчёта',
  'morning.hoursAuto_many': 'учётов закрыты без отчёта',
  'morning.hoursUnconfirmed': '{time} не подтверждено',
  'morning.hoursAssembly': '{n} со станции сборки',
  'morning.hoursAssembly_one': '{n} со станции сборки',
  'morning.hoursAssembly_few': '{n} со станции сборки',
  'morning.hoursAssembly_many': '{n} со станции сборки',
  'morning.hoursNoMinutes': 'этапов без минут',
  'morning.hoursNoMinutes_one': 'этап без минут',
  'morning.hoursNoMinutes_few': 'этапа без минут',
  'morning.hoursNoMinutes_many': 'этапов без минут',
  'morning.load': 'Загрузка недели против мощности',
  'morning.loadEmpty': 'Нет данных о мощности на эту неделю.',
  'morning.loadOf': '{load} из {cap} ч',
  'morning.loadUnscheduled': 'ещё {h} ч не распределены',
  'morning.loadPlan': 'К планированию мощности →',
  'morning.who': 'Кто сканировал, над чем и сколько',
  'morning.noParts': 'Нет деталей в системе',
  'morning.partsDone': '{done}/{total} деталей готово',
  'morning.afterFat': 'FAT пройден, ждёт отгрузки',
  'morning.openNc': '{n} открытых несоответствий',
  'morning.openNc_one': '{n} открытое несоответствие',
  'morning.openNc_few': '{n} открытых несоответствия',
  'morning.openNc_many': '{n} открытых несоответствий',
  'morning.urgent': 'Срочно',
  'morning.handle': 'Обработать',
  'morning.lateDays': 'опоздание {n} дней',
  'morning.lateDays_one': 'опоздание {n} день',
  'morning.lateDays_few': 'опоздание {n} дня',
  'morning.lateDays_many': 'опоздание {n} дней',
  'morning.today': 'Сегодня',
  'morning.tomorrow': 'Завтра',
  'morning.inDays': '{date} · {n} дн.',
  'workersDay.min': '{n} мин',
  'workersDay.hours': '{h} ч',
  'workersDay.assemblyStage': 'сборка',
  'workersDay.today': 'сегодня',
  'workersDay.onDay': '{date}',
  'workersDay.todayTab': 'Сегодня',
  'workersDay.prevTab': 'Предыдущий день · {date}',
  'workersDay.summary': 'сканировали {seen} из {total}',
  'workersDay.error': 'Не удалось загрузить сканирования. Обновите экран.',
  'workersDay.notScanned': 'Не сканировали {when} ({n}):',
  'workersDay.nobody': 'Никто не сканировал {when}.',
  'workersDay.badgeAt': 'пропуск в {time}',
  'workersDay.noBadge': 'без пропуска',
  'workersDay.badgeOnly': 'Отсканировал пропуск, нет отчёта о работе',
  'workersDay.working': 'работает сейчас',
  'workersDay.assembly': 'станция сборки',
  'workersDay.unconfirmed': 'не подтверждено — на подпись',
};
